import React, { useState, useEffect, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import API_CONFIG from '../utils/apiConfig';
import {
  normalizeOrgUtrInput,
  validateOrgComplianceForm,
  isValidPayeReference,
  isValidAccountsOfficeReference,
  isNonEmptyPayeReference,
  PAYE_REFERENCE_INVALID_MESSAGE,
  PAYE_REFERENCE_MAX_LENGTH,
  ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE,
  ACCOUNTS_OFFICE_REFERENCE_MAX_LENGTH,
} from '../utils/orgComplianceValidation';
import { formatPaymentNetPayable, getPaymentNetPayable } from '../utils/paymentDisplay';
import {
  getEmployerSubcontractorStatusMatchPresentation,
  relationshipCisVerificationAttempted,
  relationshipCisVerificationMatched,
} from '../utils/employerSubcontractorCisStatus';
import {
  employerDashboardOrgTitle,
  employerIsMainUser,
  employerHasPermission,
  parseEmployerDashboardMenuFromSearch,
} from '../utils/userDashboardUi';
import { useDashboardNav } from '../context/DashboardNavContext';
import './CompanyDashboard.css';
import '../styles/pagination-mobile.css';

const UK_SHORT_DATE_OPTS = { day: '2-digit', month: '2-digit', year: 'numeric' };

/** DD/MM/YYYY with leading zeros (en-GB), for Payments UI consistency */
function formatUkShortDate(value) {
  if (value == null || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', UK_SHORT_DATE_OPTS);
}

const CompanyDashboard = ({ user, onLogout }) => {
  const location = useLocation();
  // Local user state to handle updates after OAuth
  const [localUser, setLocalUser] = useState(user);
  
  // Sync localUser with prop user when it changes
  useEffect(() => {
    if (user) {
      setLocalUser(user);
    }
  }, [user]);
  
  // Also sync from localStorage when user-updated event fires or on mount
  useEffect(() => {
    const handleUserUpdated = () => {
      const userJson = localStorage.getItem('user');
      if (userJson) {
        try {
          const userData = JSON.parse(userJson);
          setLocalUser(userData);
        } catch (err) {
          console.error('Error parsing user data:', err);
        }
      }
    };
    
    // Check localStorage on mount
    handleUserUpdated();
    
    window.addEventListener('user-updated', handleUserUpdated);
    return () => window.removeEventListener('user-updated', handleUserUpdated);
  }, []);
  
  // Also listen for storage events (cross-tab updates)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'user' && e.newValue) {
        try {
          const userData = JSON.parse(e.newValue);
          setLocalUser(userData);
        } catch (err) {
          console.error('Error parsing user data from storage:', err);
        }
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);
  
  // Function to refresh user data
  const refreshUserData = async () => {
    try {
      const response = await API_CONFIG.fetchWithAuth(API_CONFIG.getApiUrl(API_CONFIG.endpoints.me));
      if (response.ok) {
        const userData = await response.json();
        localStorage.setItem('user', JSON.stringify(userData));
        // Update local user state immediately - this will trigger re-render
        setLocalUser(userData);
        // Trigger user update event for App.js to refresh user state
        window.dispatchEvent(new Event('user-updated'));
        return userData;
      }
    } catch (error) {
      console.error('Error refreshing user data:', error);
    }
    return null;
  };

  const currentUser = localUser || user;
  const canUseSubcontractorDashboard = Boolean(
    currentUser && Array.isArray(currentUser.employers) && currentUser.employers.length > 0
  );
  const [pendingEmployerInvitesActionCount, setPendingEmployerInvitesActionCount] = useState(0);

  useEffect(() => {
    if (!canUseSubcontractorDashboard) return;
    const fetchPendingEmployerInvites = async () => {
      try {
        const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
        url.searchParams.append('page', '1');
        url.searchParams.append('per_page', '100');
        url.searchParams.append('status_filter', 'pending');
        const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
        if (!response.ok) return;
        const data = await response.json();
        const items = data.items || [];
        const actionNeeded = items.filter((r) => r.pending_initiated_by === 'employer').length;
        setPendingEmployerInvitesActionCount(actionNeeded);
      } catch {
        /* ignore */
      }
    };
    fetchPendingEmployerInvites();
  }, [canUseSubcontractorDashboard]);

  const employerDashboardTitle = useMemo(
    () => employerDashboardOrgTitle(currentUser),
    [currentUser]
  );

  const { setDashboardNav, clearDashboardNav } = useDashboardNav();

  // Use currentUser (localUser fallback) for all checks
  const [hmrcData, setHmrcData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [pendingRequests, setPendingRequests] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [requestActionLoading, setRequestActionLoading] = useState({});
  const [connectedSubcontractors, setConnectedSubcontractors] = useState([]);
  const [approvedCount, setApprovedCount] = useState(0); // Keep separate count for approved
  const [pendingCount, setPendingCount] = useState(0); // Keep separate count for pending
  const [subcontractorsLoading, setSubcontractorsLoading] = useState(false);
  const [activeMenu, setActiveMenu] = useState('subcontractors');
  const [mobileBottomNav, setMobileBottomNav] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches
  );
  const [activeTab, setActiveTab] = useState('approved');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [payments, setPayments] = useState([]);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [paymentTab, setPaymentTab] = useState('pending'); // pending, approved, rejected, paid, monthly
  // Monthly payment statements (paid only)
  const [monthlyStatementsYear, setMonthlyStatementsYear] = useState(new Date().getFullYear());
  const [monthlyStatementsSubcontractorId, setMonthlyStatementsSubcontractorId] = useState(null); // null = All
  const [monthlyStatementsItems, setMonthlyStatementsItems] = useState([]);
  const [monthlyPaymentStatementsLoading, setMonthlyPaymentStatementsLoading] = useState(false);
  const [monthlyStatementsSubcontractors, setMonthlyStatementsSubcontractors] = useState([]);
  const [monthlyPaymentStatementDownloading, setMonthlyPaymentStatementDownloading] = useState(null); // 'year-month' while downloading
  // Bulk action state
  const [selectedPayments, setSelectedPayments] = useState([]);
  const [, setBulkCompleteLoading] = useState(false);
  const [bulkApproveLoading, setBulkApproveLoading] = useState(false);
  const [showBulkApproveModal, setShowBulkApproveModal] = useState(false);
  /** @type {Record<string, { reverseChargeApplies: boolean, overrideReason: string }>} */
  const [bulkApproveDecisions, setBulkApproveDecisions] = useState({});
  const [bulkRejectLoading, setBulkRejectLoading] = useState(false);
  const [bulkMarkPaidLoading, setBulkMarkPaidLoading] = useState(false);
  const [showPaymentListModal, setShowPaymentListModal] = useState(false);
  const [paymentListData, setPaymentListData] = useState([]);
  
  // Clear selected payments when switching tabs
  useEffect(() => {
    setSelectedPayments([]);
  }, [paymentTab]);

  // Rejected payments tab is hidden on mobile — avoid staying on that tab when narrow or after resize
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(max-width: 768px)');
    const bumpOffRejectedOnMobile = () => {
      if (mq.matches && activeMenu === 'payments' && paymentTab === 'rejected') {
        setPaymentTab('pending');
      }
    };
    bumpOffRejectedOnMobile();
    mq.addEventListener('change', bumpOffRejectedOnMobile);
    return () => mq.removeEventListener('change', bumpOffRejectedOnMobile);
  }, [activeMenu, paymentTab]);
  
  // Pagination state
  const [subcontractorsPage, setSubcontractorsPage] = useState(1);
  const [subcontractorsPerPage, setSubcontractorsPerPage] = useState(100);
  const [subcontractorsTotal, setSubcontractorsTotal] = useState(0);
  const [subcontractorsTotalPages, setSubcontractorsTotalPages] = useState(0);
  const [paymentsPage, setPaymentsPage] = useState(1);
  const [paymentsPerPage, setPaymentsPerPage] = useState(100);
  const [paymentsTotal, setPaymentsTotal] = useState(0);
  const [paymentsTotalPages, setPaymentsTotalPages] = useState(0);
  const [paymentTabCounts, setPaymentTabCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    paid: 0,
  });
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isPaymentDetailModalOpen, setIsPaymentDetailModalOpen] = useState(false);
  const [isEditPaymentModalOpen, setIsEditPaymentModalOpen] = useState(false);
  const [selectedSubcontractorForPayments, setSelectedSubcontractorForPayments] = useState(null);
  const [isSubcontractorPaymentsModalOpen, setIsSubcontractorPaymentsModalOpen] = useState(false);
  const [subcontractorPayments, setSubcontractorPayments] = useState([]);
  const [isContactSheetOpen, setIsContactSheetOpen] = useState(false);
  const [selectedSubcontractorContact, setSelectedSubcontractorContact] = useState(null);
  const [subcontractorPaymentsLoading, setSubcontractorPaymentsLoading] = useState(false);
  const [subcontractorDetails, setSubcontractorDetails] = useState(null);
  const [subcontractorDetailsLoading, setSubcontractorDetailsLoading] = useState(false);
  const [actionComment, setActionComment] = useState('');
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // 'approve', 'reject', 'mark-paid', or 'unmark-paid'
  const [markPaidLoading, setMarkPaidLoading] = useState(false);
  const [paymentToMarkPaid, setPaymentToMarkPaid] = useState(null);
  const [paymentForAction, setPaymentForAction] = useState(null); // Payment for approve/reject actions
  const [reverseChargeApplies, setReverseChargeApplies] = useState(false);
  const [reverseChargeOverrideReason, setReverseChargeOverrideReason] = useState('');
  const [showOrgComplianceModal, setShowOrgComplianceModal] = useState(false);
  const [showOrgComplianceHmrcPassword, setShowOrgComplianceHmrcPassword] = useState(false);
  const [orgSaveLoading, setOrgSaveLoading] = useState(false);
  const [orgTestLoading, setOrgTestLoading] = useState(false);
  const [orgFormData, setOrgFormData] = useState({
    company_number: '',
    utr: '',
    accounts_office_reference: '',
    paye_reference: '',
    building_name_no: '',
    street: '',
    city: '',
    postal_code: '',
    hmrc_gateway_user_id: '',
    hmrc_gateway_password: '',
  });
  const [orgFormFieldErrors, setOrgFormFieldErrors] = useState({});

  const clearOrgFormFieldError = (key) => {
    setOrgFormFieldErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // User management state
  const [employerUsers, setEmployerUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersPage, setUsersPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(100);
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersTotalPages, setUsersTotalPages] = useState(0);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userFormData, setUserFormData] = useState({
    email: '',
    full_name: '',
    phone_number: '',
    permissions: {
      view_payments: false,
      approve_edit_payments: false,
      make_payments: false,
      add_users: false,
      view_users: false,
      view_subcontractors: false,
      add_subcontractors: false
    }
  });
  const [userLoading, setUserLoading] = useState(false);
  
  // Monthly Statements state
  const [availableCISMonths, setAvailableCISMonths] = useState([]);
  const [selectedCISMonth, setSelectedCISMonth] = useState(null);
  const [selectedCISYear, setSelectedCISYear] = useState(null);
  const [, setMonthlyStatements] = useState({});
  const [, setMonthlyStatementsLoading] = useState(false);
  const [companyMonthlySummary, setCompanyMonthlySummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [, setSelectedSubcontractorForStatement] = useState(null);
  const [isMonthlyStatementModalOpen, setIsMonthlyStatementModalOpen] = useState(false);
  // Gateway credentials are now system-wide (stored in ENV), not per-user
  // Removed: gateway credentials modal and state
  const [currentMonthlyStatement, setCurrentMonthlyStatement] = useState(null);
  const [cisMonthPayments, setCisMonthPayments] = useState([]);
  const [cisMonthPaymentsLoading, setCisMonthPaymentsLoading] = useState(false);
  
  // HMRC Submission state
  const [hmrcSubmissionStatus, setHmrcSubmissionStatus] = useState(null);
  const [hmrcSubmitting, setHmrcSubmitting] = useState(false);
  const [pendingCISSubmission, setPendingCISSubmission] = useState(false);
  const [showHMRCDeclarationModal, setShowHMRCDeclarationModal] = useState(false);
  // Set of 'year-month' for CIS months already submitted to HMRC
  const [hmrcSubmittedMonthsSet, setHmrcSubmittedMonthsSet] = useState(new Set());
  
  // HMRC Verification state (per subcontractor)
  const [verifyingSubcontractors, setVerifyingSubcontractors] = useState({}); // {subcontractor_id: true/false}
  
  // Edit form state
  const [editFormData, setEditFormData] = useState({
    work_start_date: '',
    description: '',
    job_code: '',
    amount: '',
    week_commencing: '',
    days: {
      monday: { description: '', jobCode: '', amount: '' },
      tuesday: { description: '', jobCode: '', amount: '' },
      wednesday: { description: '', jobCode: '', amount: '' },
      thursday: { description: '', jobCode: '', amount: '' },
      friday: { description: '', jobCode: '', amount: '' },
      saturday: { description: '', jobCode: '', amount: '' },
      sunday: { description: '', jobCode: '', amount: '' }
    },
    materials: [{ name: '', job_code: '', description: '', amount: '' }]
  });

  // Permission helper functions (shared with profile shell nav)
  const isMainEmployer = () => employerIsMainUser(currentUser);

  const hasPermission = (permission) => employerHasPermission(currentUser, permission);

  const employerSectionTitle = useMemo(() => {
    if (!currentUser) return null;
    if (activeMenu === 'subcontractors' && (isMainEmployer() || hasPermission('view_subcontractors'))) {
      return 'My Subcontractors';
    }
    if (activeMenu === 'payments' && hasPermission('view_payments')) {
      return 'Payments';
    }
    if (activeMenu === 'cis-returns' && isMainEmployer()) {
      return 'Monthly CIS Statements';
    }
    if (activeMenu === 'users' && hasPermission('view_users')) {
      return 'User Management';
    }
    return null;
  }, [activeMenu, currentUser]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const org = currentUser?.organization || {};
      let gatewayUser = '';
      let gatewayPass = '';
      if (currentUser) {
        try {
          const resp = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl(API_CONFIG.endpoints.getOrganizationProfile)
          );
          if (resp.ok && !cancelled) {
            const data = await resp.json();
            gatewayUser = (data.hmrc_gateway_user_id || '').trim();
            gatewayPass = (data.hmrc_gateway_password || '').trim();
          }
        } catch {
          /* leave gateway fields empty */
        }
      }
      if (cancelled) return;
      setOrgFormData((prev) => ({
        ...prev,
        company_number: String(org.company_number || currentUser?.company_number || '').replace(/\D/g, '').slice(0, 8),
        utr: normalizeOrgUtrInput(org.utr || currentUser?.utr || ''),
        accounts_office_reference: org.accounts_office_reference || '',
        paye_reference: org.paye_reference || currentUser?.paye_reference || '',
        building_name_no: org.building_name_no || currentUser?.building_name_no || '',
        street: org.street || currentUser?.street || '',
        city: org.city || currentUser?.city || '',
        postal_code: org.postal_code || currentUser?.postal_code || '',
        hmrc_gateway_user_id: gatewayUser,
        hmrc_gateway_password: gatewayPass,
      }));
    })();
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  useEffect(() => {
    if (showOrgComplianceModal) {
      setOrgFormFieldErrors({});
    }
  }, [showOrgComplianceModal]);

  const orgProfile = currentUser?.organization || {};
  const hasOrgAddress = Boolean(
    (orgProfile.address && String(orgProfile.address).trim()) ||
    (orgProfile.building_name_no && String(orgProfile.building_name_no).trim()) ||
    (orgProfile.street && String(orgProfile.street).trim())
  );
  const payeRefOk = isNonEmptyPayeReference(orgProfile.paye_reference);
  const orgMissingForVerification = [
    !orgProfile.utr ? 'UTR' : null,
    !orgProfile.accounts_office_reference ? 'Accounts Office Reference' : null,
    !payeRefOk ? 'PAYE reference (914/VA11166)' : null,
    !orgProfile.hmrc_gateway_user_id_present ? 'Government Gateway User ID' : null,
    !orgProfile.hmrc_gateway_password_present ? 'Government Gateway Password' : null,
    !orgProfile.hmrc_connection_ok ? 'CIS Transaction Engine test' : null,
  ].filter(Boolean);
  const orgMissingForPayments = [
    !hasOrgAddress ? 'Business address' : null,
    !payeRefOk ? 'PAYE reference (914/VA11166)' : null,
  ].filter(Boolean);

  // Set default menu based on user permissions (skip when URL ?menu= specifies a valid section)
  useEffect(() => {
    if (parseEmployerDashboardMenuFromSearch(location.search, currentUser)) {
      return;
    }
    if (currentUser) {
      // If user only has view_subcontractors, default to subcontractors
      if (hasPermission('view_subcontractors') && !isMainEmployer() && !hasPermission('view_payments') && !hasPermission('view_users')) {
        setActiveMenu('subcontractors');
      }
      // If user only has view_payments, default to payments
      else if (hasPermission('view_payments') && !isMainEmployer() && !hasPermission('view_users') && !hasPermission('view_subcontractors')) {
        setActiveMenu('payments');
      }
      // If user is main employer, keep default (subcontractors)
      // If user has view_payments permission, ensure they can access payments
      else if (hasPermission('view_payments') && !isMainEmployer()) {
        // Only change if current menu is not accessible
        if (activeMenu === 'subcontractors' || activeMenu === 'cis-returns') {
          setActiveMenu('payments');
        }
      }
    }
  }, [currentUser, location.search]);

  useEffect(() => {
    const menu = parseEmployerDashboardMenuFromSearch(location.search, currentUser);
    if (menu) setActiveMenu(menu);
  }, [location.search, currentUser]);

  // Fetch pending requests and connected subcontractors on mount
  useEffect(() => {
    fetchPendingRequests();
    fetchConnectedSubcontractors();
    // Also fetch approved count on mount to initialize
    const fetchInitialApprovedCount = async () => {
      const token = localStorage.getItem('access_token');
      if (!token || !currentUser) return;
      try {
        const approvedUrl = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
        approvedUrl.searchParams.append('page', 1);
        approvedUrl.searchParams.append('per_page', 1);
        approvedUrl.searchParams.append('status_filter', 'active');
        approvedUrl.searchParams.append('return_subcontractors', 'true');
        const approvedResponse = await API_CONFIG.fetchWithAuth(approvedUrl.toString(), { method: 'GET' });
        if (approvedResponse.ok) {
          const approvedData = await approvedResponse.json();
          setApprovedCount(approvedData.total || 0);
        }
      } catch (error) {
        // Silently fail
      }
    };
    fetchInitialApprovedCount();
  }, [currentUser]);

  // Fetch payments when payments menu is active or pagination changes
  useEffect(() => {
    if (activeMenu === 'payments') {
      fetchPayments();
    }
  }, [activeMenu, paymentTab]);

  // Fetch active subcontractors for Monthly statements dropdown when on Payments; force employer view so we always get subcontractors
  useEffect(() => {
    if (activeMenu !== 'payments') return;
    const fetchRelationshipsForMonthly = async () => {
      try {
        const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
        url.searchParams.append('page', 1);
        url.searchParams.append('per_page', 100);
        url.searchParams.append('status_filter', 'active');
        url.searchParams.append('return_subcontractors', 'true');
        const res = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          const items = data.items || [];
          setMonthlyStatementsSubcontractors(items);
          if (items.length === 0 && Array.isArray(payments) && payments.length > 0) {
            const seen = new Set();
            const fromPayments = payments
              .filter((p) => p.subcontractor_id && !seen.has(p.subcontractor_id))
              .map((p) => {
                seen.add(p.subcontractor_id);
                return { subcontractor_id: p.subcontractor_id, subcontractor_name: p.subcontractor_name || 'N/A' };
              });
            if (fromPayments.length > 0) setMonthlyStatementsSubcontractors(fromPayments);
          }
        }
      } catch (e) {
        if (e.message !== 'Token expired') {
          setMonthlyStatementsSubcontractors([]);
          if (Array.isArray(payments) && payments.length > 0) {
            const seen = new Set();
            const fromPayments = payments
              .filter((p) => p.subcontractor_id && !seen.has(p.subcontractor_id))
              .map((p) => {
                seen.add(p.subcontractor_id);
                return { subcontractor_id: p.subcontractor_id, subcontractor_name: p.subcontractor_name || 'N/A' };
              });
            if (fromPayments.length > 0) setMonthlyStatementsSubcontractors(fromPayments);
          }
        }
      }
    };
    fetchRelationshipsForMonthly();
  }, [activeMenu, payments]);

  // Fetch monthly payment statements list when on Monthly tab and year/subcontractor changes
  useEffect(() => {
    if (activeMenu !== 'payments' || paymentTab !== 'monthly') return;
    const fetchMonthlyStatements = async () => {
      setMonthlyPaymentStatementsLoading(true);
      try {
        const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.listMonthlyPaymentStatements));
        url.searchParams.append('year', monthlyStatementsYear);
        url.searchParams.append('view_mode', 'employer');
        if (monthlyStatementsSubcontractorId) url.searchParams.append('subcontractor_id', monthlyStatementsSubcontractorId);
        const res = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
        if (res.ok) {
          const data = await res.json();
          setMonthlyStatementsItems(data.items || []);
        } else {
          setMonthlyStatementsItems([]);
        }
      } catch (e) {
        if (e.message !== 'Token expired') setMonthlyStatementsItems([]);
      } finally {
        setMonthlyPaymentStatementsLoading(false);
      }
    };
    fetchMonthlyStatements();
  }, [activeMenu, paymentTab, monthlyStatementsYear, monthlyStatementsSubcontractorId]);

  // Fetch HMRC submitted months when on Payments or CIS Returns tab
  useEffect(() => {
    if (activeMenu !== 'payments' && activeMenu !== 'cis-returns') return;
    const fetchHMRCSubmittedMonths = async () => {
      try {
        const response = await API_CONFIG.fetchWithAuth(
          API_CONFIG.getApiUrl('/unified-auth/hmrc/submissions')
        );
        if (response.ok) {
          const submissions = await response.json();
          const submitted = new Set(
            submissions
              .filter(s => s.success === true)
              .map(s => `${s.cis_month_year}-${s.cis_month_month}`)
          );
          setHmrcSubmittedMonthsSet(submitted);
        } else {
          setHmrcSubmittedMonthsSet(new Set());
        }
      } catch (e) {
        if (e.message !== 'Token expired') setHmrcSubmittedMonthsSet(new Set());
      }
    };
    fetchHMRCSubmittedMonths();
  }, [activeMenu]);

  // Fetch subcontractors when tab or pagination changes (or when user becomes available)
  useEffect(() => {
    if (activeMenu === 'subcontractors' && currentUser) {
      fetchConnectedSubcontractors();
    }
  }, [activeMenu, activeTab, currentUser]);

  // Fetch available CIS months when CIS Returns menu is active
  useEffect(() => {
    if (activeMenu === 'cis-returns') {
      // Clear any leftover localStorage values that might trigger auto-submit
      localStorage.removeItem('hmrc_redirect_cis_month');
      localStorage.removeItem('hmrc_redirect_after_connect');
      
      // Only reset if we don't have a selected month (preserve user's selection)
      // This allows auto-selection of current month without clearing it
      if (!selectedCISYear || !selectedCISMonth) {
        setCompanyMonthlySummary(null);
        setHmrcSubmissionStatus(null);
      }
      
      fetchAvailableCISMonths();
      // Refresh user data to ensure we have latest HMRC connection status
      // Use a small delay to ensure the function is available
      setTimeout(() => {
        refreshUserData();
      }, 100);
    }
  }, [activeMenu]);

  // Fetch company summary when CIS month is selected
  useEffect(() => {
    // Only fetch if we're on the CIS Returns menu AND a month is selected
    // AND we're not in the middle of initializing (check if availableCISMonths is loaded)
    if (activeMenu === 'cis-returns' && selectedCISYear && selectedCISMonth && availableCISMonths.length > 0) {
      fetchCompanyMonthlySummary();
      // Fetch submission status to check if already submitted
      fetchHMRCSubmissionStatus();
    }
  }, [activeMenu, selectedCISYear, selectedCISMonth, availableCISMonths.length]);
  
  // Fetch employer users when users menu is active
  useEffect(() => {
    if (activeMenu === 'users') {
      fetchEmployerUsers();
    }
  }, [activeMenu]);

  // Check for HMRC connection success after OAuth redirect
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const hmrcConnected = urlParams.get('hmrc_connected');
    const hmrcError = urlParams.get('hmrc_error');
    
    if (hmrcConnected === 'true') {
      setInviteSuccess('Redirect completed.');
      setTimeout(() => setInviteSuccess(''), 5000);
      
      refreshUserData().then(() => {
        // Clean up URL params
        window.history.replaceState({}, document.title, window.location.pathname);
        
        // Clean up any stored redirect state - but DON'T auto-submit
        // User should manually click "Submit to HMRC" button
        localStorage.removeItem('hmrc_redirect_cis_month');
        localStorage.removeItem('hmrc_redirect_after_connect');
      });
    } else if (hmrcError) {
      setInviteError(`HMRC connection failed: ${decodeURIComponent(hmrcError)}`);
      setTimeout(() => setInviteError(''), 5000);
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);
  
  // Also check for HMRC connection status when component mounts or user changes
  // REMOVED: This was causing issues - only refresh user data on OAuth redirect, not on tab change

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isModalOpen || isPaymentDetailModalOpen || isEditPaymentModalOpen || isUserModalOpen || isEditUserModalOpen || isMonthlyStatementModalOpen || isSubcontractorPaymentsModalOpen || showHMRCDeclarationModal) {
      document.body.classList.add('modal-open');
      document.body.style.overflow = 'hidden';
      document.documentElement.style.overflow = 'hidden';
    } else {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    }
    return () => {
      document.body.classList.remove('modal-open');
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, [isModalOpen, isPaymentDetailModalOpen, isEditPaymentModalOpen, isUserModalOpen, isEditUserModalOpen, isMonthlyStatementModalOpen, isSubcontractorPaymentsModalOpen, showHMRCDeclarationModal]);

  const fetchPendingRequests = async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !currentUser) return;

    setPendingLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPendingRequests),
        { method: 'GET' }
      );

      if (response.ok) {
        const data = await response.json();
        const pending = data.pending_requests || [];
        setPendingRequests(pending);
        setPendingCount(pending.length); // Store count separately
      }
    } catch (error) {
      // Silently fail - don't show error for pending requests
    } finally {
      setPendingLoading(false);
    }
  };

  const handleVerifySubcontractor = async (subcontractor) => {
    const subcontractorId = subcontractor.subcontractor_id;
    const subcontractorName = subcontractor.subcontractor_name || 'Subcontractor';
    
    // Set loading state for this specific subcontractor
    setVerifyingSubcontractors(prev => ({ ...prev, [subcontractorId]: true }));
    
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`/unified-auth/verify-subcontractor/${subcontractorId}`),
        { 
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      const data = await response.json();
      
      if (response.ok) {
        // Check if in debug mode
        if (data.debug) {
          // Debug mode - show XML generation success
          if (window.showToast) {
            window.showToast(
              `🔍 DEBUG MODE: XML generated successfully for ${subcontractorName}. Check backend logs for XML output.`,
              'info',
              7000
            );
          }
          console.log('Debug Response:', data);
        } else if (data.matched) {
          // Verification successful
          if (window.showToast) {
            window.showToast(
              `✓ ${subcontractorName} verified successfully! Tax rate: ${data.tax_rate}%`,
              'success',
              5000
            );
          }
          
          // Refresh the subcontractors list to show updated verification status
          // The fetchRelationships is called automatically by the useEffect when activeTab changes
          // But we can trigger it manually here
          const token = localStorage.getItem('access_token');
          if (token) {
            const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
            url.searchParams.append('page', subcontractorsPage);
            url.searchParams.append('per_page', subcontractorsPerPage);
            url.searchParams.append('status_filter', 'active');
            url.searchParams.append('return_subcontractors', 'true');
            
            const refreshResponse = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
            if (refreshResponse.ok) {
              const refreshData = await refreshResponse.json();
              setConnectedSubcontractors(refreshData.items || []);
              setSubcontractorsTotal(refreshData.total || 0);
              setSubcontractorsTotalPages(refreshData.total_pages || 0);
            }
          }
        } else {
          // Verification failed - no match
          if (window.showToast) {
            window.showToast(
              `⚠ ${subcontractorName} - HMRC returned NO MATCH. Please verify the details are correct.`,
              'warning',
              6000
            );
          }
        }
      } else {
        // API error - show user-friendly message (backend may still send technical details in some paths)
        const rawDetail = data.detail || '';
        const isTechnicalError = typeof rawDetail === 'string' && (
          rawDetail.includes('HMRC poll error') ||
          rawDetail.includes('departmental specific business logic') ||
          rawDetail.includes('Body tag') ||
          rawDetail.includes('HMRC verification failed:')
        );
        const errorMessage = isTechnicalError
          ? 'Verification failed. Please make sure the information is complete and correct.'
          : (rawDetail || 'Verification failed. Please make sure the information is complete and correct.');
        if (window.showToast) {
          window.showToast(`❌ ${errorMessage}`, 'error', 6000);
        }
      }
    } catch (error) {
      console.error('Error verifying subcontractor:', error);
      if (window.showToast) {
        window.showToast(
          '❌ Verification failed. Please make sure the information is complete and correct.',
          'error',
          5000
        );
      }
    } finally {
      // Clear loading state
      setVerifyingSubcontractors(prev => ({ ...prev, [subcontractorId]: false }));
    }
  };

  const fetchConnectedSubcontractors = async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !currentUser) return;

    setSubcontractorsLoading(true);
    try {
      const statusFilter = activeTab === 'approved' ? 'active' : activeTab === 'pending' ? 'pending' : null;
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
      url.searchParams.append('page', '1');
      url.searchParams.append('per_page', '100');
      url.searchParams.append('return_subcontractors', 'true');
      if (statusFilter) {
        url.searchParams.append('status_filter', statusFilter);
      }

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });

      if (response.ok) {
        const data = await response.json();
        setConnectedSubcontractors(data.items || []);
        setSubcontractorsTotal(data.total || 0);
        setSubcontractorsTotalPages(data.total_pages || 0);
        
        // Store count based on current tab
        if (activeTab === 'approved') {
          setApprovedCount(data.total || data.items?.length || 0);
        } else if (activeTab === 'pending') {
          // Pending tab: use pending-requests API (includes invites + connection requests), not relationships
          const pendingResp = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPendingRequests),
            { method: 'GET' }
          );
          if (pendingResp.ok) {
            const pendingJson = await pendingResp.json();
            const list = pendingJson.pending_requests || [];
            setPendingCount(list.length);
          }
        }
        
        // Also fetch the other count if we're on a tab to keep counts accurate
        if (activeTab === 'pending') {
          // Fetch approved count
          const approvedUrl = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getRelationships));
          approvedUrl.searchParams.append('page', 1);
          approvedUrl.searchParams.append('per_page', 1);
          approvedUrl.searchParams.append('status_filter', 'active');
          approvedUrl.searchParams.append('return_subcontractors', 'true');
          const approvedResponse = await API_CONFIG.fetchWithAuth(approvedUrl.toString(), { method: 'GET' });
          if (approvedResponse.ok) {
            const approvedData = await approvedResponse.json();
            setApprovedCount(approvedData.total || 0);
          }
        } else if (activeTab === 'approved') {
          // Fetch pending count: use pending-requests (includes invites + connection requests)
          const pendingResp = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPendingRequests),
            { method: 'GET' }
          );
          if (pendingResp.ok) {
            const pendingJson = await pendingResp.json();
            const list = pendingJson.pending_requests || [];
            setPendingCount(list.length);
          }
        }
      }
    } catch (error) {
      // Silently fail - don't show error
    } finally {
      setSubcontractorsLoading(false);
    }
  };

  const fetchPaymentTabCounts = async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !user) return;
    try {
      const response = await API_CONFIG.fetchWithAuth(
        `${API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPaymentRequestStatusCounts)}?view_mode=employer`,
        { method: 'GET' }
      );
      if (response.ok) {
        const data = await response.json();
        setPaymentTabCounts({
          pending: Number(data.pending) || 0,
          approved: Number(data.approved) || 0,
          rejected: Number(data.rejected) || 0,
          paid: Number(data.paid) || 0,
        });
      }
    } catch (error) {
      // Silently fail
    }
  };

  const fetchPayments = async () => {
    const token = localStorage.getItem('access_token');
    if (!token || !user) return;

    setPaymentsLoading(true);
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPaymentRequests));
      url.searchParams.append('page', '1');
      url.searchParams.append('per_page', '100');
      url.searchParams.append('view_mode', 'employer');
      if (
        paymentTab &&
        paymentTab !== 'monthly' &&
        ['pending', 'approved', 'rejected', 'paid'].includes(paymentTab)
      ) {
        url.searchParams.append('status', paymentTab);
      }

      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });

      if (response.ok) {
        const data = await response.json();
        setPayments(data.items || []);
        setPaymentsTotal(data.total || 0);
        setPaymentsTotalPages(data.total_pages || 0);
        fetchPaymentTabCounts();
      }
    } catch (error) {
      // Silently fail - don't show error
    } finally {
      setPaymentsLoading(false);
    }
  };

  const fetchSubcontractorPayments = async (subcontractorId) => {
    const token = localStorage.getItem('access_token');
    if (!token || !user) return;

    setSubcontractorPaymentsLoading(true);
    try {
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPaymentRequests));
      url.searchParams.append('view_mode', 'employer');
      // Fetch all payments and filter by subcontractor_id
      const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });

      if (response.ok) {
        const data = await response.json();
        // Filter payments by subcontractor_id
        const filtered = (data.items || []).filter(
          payment => payment.subcontractor_id === subcontractorId
        );
        setSubcontractorPayments(filtered);
      }
    } catch (error) {
      console.error('Error fetching subcontractor payments:', error);
      setSubcontractorPayments([]);
    } finally {
      setSubcontractorPaymentsLoading(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const fetchSubcontractorDetails = async (subcontractorId) => {
    const token = localStorage.getItem('access_token');
    if (!token || !user) return;

    setSubcontractorDetailsLoading(true);
    try {
      // Try to get details from payment data first (if available)
      // Otherwise, we'll use the basic info from relationships
      // For now, we'll extract what we can from the payments
      if (subcontractorPayments.length > 0) {
        // Get details from first payment if available
        const firstPayment = subcontractorPayments[0];
        if (firstPayment.subcontractor_details) {
          setSubcontractorDetails(firstPayment.subcontractor_details);
          setSubcontractorDetailsLoading(false);
          return;
        }
      }
      
      // If no payment details, we'll need to enhance the relationships endpoint
      // For now, set to null and use basic info from selectedSubcontractorForPayments
      setSubcontractorDetails(null);
    } catch (error) {
      console.error('Error fetching subcontractor details:', error);
      setSubcontractorDetails(null);
    } finally {
      setSubcontractorDetailsLoading(false);
    }
  };

  const handleViewSubcontractorPayments = async (subcontractor) => {
    setSelectedSubcontractorForPayments(subcontractor);
    setIsSubcontractorPaymentsModalOpen(true);
    await fetchSubcontractorPayments(subcontractor.subcontractor_id);
  };

  const handleViewPaymentDetails = (payment) => {
    setSelectedPayment(payment);
    setIsPaymentDetailModalOpen(true);
    setActionComment('');
  };

  const handleDownloadMonthlyPaymentSlipsPDF = async (year, month) => {
    const key = `${year}-${month}`;
    setMonthlyPaymentStatementDownloading(key);
    try {
      const token = localStorage.getItem('access_token');
      const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.downloadMonthlyPaymentStatementsPDF));
      url.searchParams.append('year', year);
      url.searchParams.append('month', month);
      url.searchParams.append('view_mode', 'employer');
      if (monthlyStatementsSubcontractorId) url.searchParams.append('subcontractor_id', monthlyStatementsSubcontractorId);
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const blob = await response.blob();
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `monthly_payment_slips_${year}_${month}.pdf`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename="?(.+)"?/i);
          if (match) filename = match[1];
        }
        const a = document.createElement('a');
        a.href = window.URL.createObjectURL(blob);
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(a.href);
        document.body.removeChild(a);
        if (window.showToast) window.showToast('Monthly statement PDF downloaded.', 'success', 3000);
      } else {
        const err = await response.json().catch(() => ({}));
        if (window.showToast) window.showToast(err.detail || 'Failed to download PDF', 'error', 5000);
      }
    } catch (e) {
      if (e.message !== 'Token expired' && window.showToast) window.showToast('Download failed.', 'error', 5000);
    } finally {
      setMonthlyPaymentStatementDownloading(null);
    }
  };

  // CIS month key for a payment (from approved_at or paid_at). Used to check if HMRC return was submitted for that month.
  // eslint-disable-next-line no-unused-vars
  const getCisMonthKeyForPayment = (payment) => {
    const raw = payment.approved_at || payment.paid_at;
    if (!raw) return null;
    const d = new Date(raw);
    if (isNaN(d.getTime())) return null;
    let year = d.getFullYear();
    let month = d.getMonth() + 1; // 1-12
    if (d.getDate() <= 5) {
      month -= 1;
      if (month < 1) {
        month = 12;
        year -= 1;
      }
    }
    return `${year}-${month}`;
  };

  const handleEditPayment = (payment) => {
    setSelectedPayment(payment);
    // Populate edit form with current payment data
    if (payment.payment_type === 'job_rate') {
      setEditFormData({
        work_start_date: payment.work_start_date ? new Date(payment.work_start_date).toISOString().split('T')[0] : '',
        description: payment.description || '',
        job_code: payment.job_code || '',
        amount: payment.amount || '',
        week_commencing: '',
        days: {
          monday: { description: '', jobCode: '', amount: '' },
          tuesday: { description: '', jobCode: '', amount: '' },
          wednesday: { description: '', jobCode: '', amount: '' },
          thursday: { description: '', jobCode: '', amount: '' },
          friday: { description: '', jobCode: '', amount: '' },
          saturday: { description: '', jobCode: '', amount: '' },
          sunday: { description: '', jobCode: '', amount: '' }
        },
        materials: payment.materials_data && payment.materials_data.length > 0 
          ? payment.materials_data.map(m => ({
              name: m.name || '',
              job_code: m.job_code || m.jobCode || '',
              description: m.description || '',
              amount: m.amount || ''
            }))
          : [{ name: '', job_code: '', description: '', amount: '' }]
      });
    } else {
      // By day payment
      const daysData = payment.days_data || {};
      setEditFormData({
        work_start_date: '',
        description: '',
        job_code: '',
        amount: '',
        week_commencing: payment.week_commencing ? new Date(payment.week_commencing).toISOString().split('T')[0] : '',
        days: {
          monday: daysData.monday || { description: '', jobCode: '', amount: '' },
          tuesday: daysData.tuesday || { description: '', jobCode: '', amount: '' },
          wednesday: daysData.wednesday || { description: '', jobCode: '', amount: '' },
          thursday: daysData.thursday || { description: '', jobCode: '', amount: '' },
          friday: daysData.friday || { description: '', jobCode: '', amount: '' },
          saturday: daysData.saturday || { description: '', jobCode: '', amount: '' },
          sunday: daysData.sunday || { description: '', jobCode: '', amount: '' }
        },
        materials: payment.materials_data && payment.materials_data.length > 0 
          ? payment.materials_data.map(m => ({
              name: m.name || '',
              job_code: m.job_code || m.jobCode || '',
              description: m.description || '',
              amount: m.amount || ''
            }))
          : [{ name: '', job_code: '', description: '', amount: '' }]
      });
    }
    setIsPaymentDetailModalOpen(false);
    setIsEditPaymentModalOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedPayment) return;

    setEditLoading(true);
    try {
      // Prepare update data
      const updateData = {};
      
      if (selectedPayment.payment_type === 'job_rate') {
        if (editFormData.work_start_date) {
          // Convert date string to ISO format with timezone
          const date = new Date(editFormData.work_start_date);
          updateData.work_start_date = date.toISOString();
        }
        if (editFormData.description !== undefined) updateData.description = editFormData.description;
        if (editFormData.job_code !== undefined) updateData.job_code = editFormData.job_code;
        if (editFormData.amount !== undefined) updateData.amount = parseFloat(editFormData.amount) || null;
      } else {
        // By day
        if (editFormData.week_commencing) {
          // Convert date string to ISO format with timezone
          const date = new Date(editFormData.week_commencing);
          updateData.week_commencing = date.toISOString();
        }
        // Convert days to the format expected by API
        const daysData = {};
        for (const [day, data] of Object.entries(editFormData.days)) {
          if (data.description || data.jobCode || data.amount) {
            daysData[day] = {
              description: data.description || '',
              jobCode: data.jobCode || '',
              amount: data.amount ? parseFloat(data.amount) : null
            };
          }
        }
        updateData.days_data = daysData;
      }
      
      // Materials
      const materials = editFormData.materials.filter(m => m.name || m.description || m.amount || m.job_code);
      if (materials.length > 0) {
        updateData.materials_data = materials.map(m => ({
          name: m.name || '',
          job_code: (m.job_code || '').trim() || null,
          description: m.description || '',
          amount: m.amount ? parseFloat(m.amount) : null
        }));
      } else {
        updateData.materials_data = [];
      }

      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.updatePaymentRequest}/${selectedPayment.payment_request_id}`),
        {
          method: 'PUT',
          body: JSON.stringify(updateData)
        }
      );

      if (response.ok) {
        setIsEditPaymentModalOpen(false);
        setSelectedPayment(null);
        // Refresh payments list
        await fetchPayments();
        setInviteSuccess('Payment request updated successfully');
        setTimeout(() => {
          setInviteSuccess('');
        }, 3000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to update payment request');
        setTimeout(() => {
          setInviteError('');
        }, 5000);
      }
    } catch (error) {
      setInviteError('Network error. Please try again.');
      setTimeout(() => {
        setInviteError('');
      }, 5000);
    } finally {
      setEditLoading(false);
    }
  };

  const handleApprovePayment = (payment = null) => {
    const paymentToApprove = payment || selectedPayment;
    if (!paymentToApprove) return;
    setPaymentForAction(paymentToApprove);
    setReverseChargeApplies(!!paymentToApprove.reverse_charge_default);
    setReverseChargeOverrideReason('');
    setConfirmAction('approve');
    setShowConfirmDialog(true);
  };

  const handleRejectPayment = (payment = null) => {
    const paymentToReject = payment || selectedPayment;
    if (!paymentToReject) return;
    setPaymentForAction(paymentToReject);
    setConfirmAction('reject');
    setShowConfirmDialog(true);
  };

  const handleMarkAsPaid = (payment) => {
    if (!payment) return;
    setPaymentToMarkPaid(payment);
    setConfirmAction('mark-paid');
    setShowConfirmDialog(true);
  };

  const handleUnmarkAsPaid = (payment) => {
    if (!payment) return;
    setPaymentToMarkPaid(payment);
    setConfirmAction('unmark-paid');
    setShowConfirmDialog(true);
  };

  // Bulk action handlers
  const handleSelectPayment = (paymentId) => {
    setSelectedPayments(prev => {
      if (prev.includes(paymentId)) {
        return prev.filter(id => id !== paymentId);
      } else {
        return [...prev, paymentId];
      }
    });
  };

  const handleSelectAllPayments = () => {
    // Get payments for current tab
    const currentTabPayments = payments.filter(p => {
      if (paymentTab === 'pending') return p.status === 'pending';
      if (paymentTab === 'approved') return p.status === 'approved';
      if (paymentTab === 'rejected') return p.status === 'rejected';
      return false;
    });
    
    if (selectedPayments.length === currentTabPayments.length && currentTabPayments.length > 0) {
      setSelectedPayments([]);
    } else {
      setSelectedPayments(currentTabPayments.map(p => p.payment_request_id));
    }
  };

  const handleBulkMarkAsPaid = async () => {
    if (selectedPayments.length === 0) {
      if (window.showToast) {
        window.showToast('Please select at least one payment to mark as paid', 'error');
      }
      return;
    }

    if (!window.confirm(`Mark ${selectedPayments.length} payment(s) as paid?`)) {
      return;
    }

    setBulkMarkPaidLoading(true);
    setInviteError('');
    setInviteSuccess('');

    try {
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      // Process each payment sequentially
      for (const paymentId of selectedPayments) {
        try {
          const endpoint = `${API_CONFIG.endpoints.markPaymentAsPaid}/${paymentId}/mark-paid`;
          const response = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl(endpoint),
            { method: 'POST' }
          );

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
            const errorData = await response.json();
            errors.push(errorData.detail || 'Failed to mark as paid');
          }
        } catch (error) {
          failCount++;
          errors.push(error.message);
        }
      }

      // Show results
      if (successCount > 0) {
        const successMsg = `Successfully marked ${successCount} payment(s) as paid`;
        setInviteSuccess(successMsg);
        if (window.showToast) {
          window.showToast(successMsg, 'success');
        }
      }
      
      if (failCount > 0) {
        const errorMsg = `Failed to mark ${failCount} payment(s) as paid: ${errors.slice(0, 3).join(', ')}`;
        setInviteError(errorMsg);
        if (window.showToast) {
          window.showToast(errorMsg, 'error');
        }
      }

      // Clear selections and refresh
      setSelectedPayments([]);
      fetchPayments();
      
      setTimeout(() => {
        setInviteSuccess('');
        setInviteError('');
      }, 5000);
    } catch (error) {
      const errorMsg = 'Error marking payments as paid: ' + error.message;
      setInviteError(errorMsg);
      if (window.showToast) {
        window.showToast(errorMsg, 'error');
      }
      setTimeout(() => setInviteError(''), 5000);
    } finally {
      setBulkMarkPaidLoading(false);
    }
  };

  const handleMakeBulkPayment = () => {
    if (selectedPayments.length === 0) {
      if (window.showToast) {
        window.showToast('Please select at least one payment to generate payment list', 'error');
      }
      return;
    }

    // Get selected payment data
    const selectedPaymentData = payments.filter(p => 
      selectedPayments.includes(p.payment_request_id) && p.status === 'approved'
    );

    if (selectedPaymentData.length === 0) {
      if (window.showToast) {
        window.showToast('No approved payments selected', 'error');
      }
      return;
    }

    // Generate payment run ID
    const paymentRunId = `PR-${Date.now()}-${Math.random().toString(36).substr(2, 9).toUpperCase()}`;
    const now = new Date();

    // Generate payment list
    const paymentList = selectedPaymentData.map((payment, index) => {
      const paymentId = typeof payment.payment_request_id === 'string' 
        ? payment.payment_request_id 
        : payment.payment_request_id;
      
      return {
        // Identifiers / control
        payment_id: paymentId,
        payment_run_id: paymentRunId,
        payer_account_id: payment.employer_id,
        payee_id: payment.subcontractor_id,
        
        // Payee details
        payee_organisation_name:
          payment.subcontractor_registered_name ||
          payment.subcontractor_company_name ||
          null,
        payee_first_name: payment.subcontractor_first_name || null,
        payee_surname: payment.subcontractor_surname || null,
        reference_number: payment.payment_reference || null,
        payer_first_name: payment.employer_first_name || null,
        payer_surname: payment.employer_surname || null,
        account_number: payment.subcontractor_bank_account_number || null,
        sort_code: payment.subcontractor_bank_sort_code || null,
        
        // Payment details
        amount: parseFloat(payment.total_payable || payment.total_amount || 0).toFixed(2),
        currency: 'GBP',
        requested_payment_date: null, // Optional
        
        // Execution / reconciliation
        payment_method: 'manual', // manual | bank_file | starling | revolut
        provider_payment_id: null,
        provider_batch_id: null,
        status: 'created', // created | submitted | paid | failed | cancelled
        submitted_at: null,
        paid_at: null,
        
        // Audit
        created_by: currentUser?.user_id || null,
        created_at: now.toISOString(),
        last_updated_at: now.toISOString()
      };
    });

    setPaymentListData(paymentList);
    setShowPaymentListModal(true);
  };

  const downloadPaymentList = (format = 'json') => {
    if (paymentListData.length === 0) return;

    let content, filename, mimeType;

    const toCsvCell = (value) => {
      if (value === null || value === undefined) return '';
      const text = String(value);
      const escaped = text.replace(/"/g, '""');
      if (/[",\n\r]/.test(escaped)) {
        return `"${escaped}"`;
      }
      return escaped;
    };

    if (format === 'json') {
      content = JSON.stringify(paymentListData, null, 2);
      filename = `bulk_payment_list_${Date.now()}.json`;
      mimeType = 'application/json';
    } else if (format === 'csv') {
      // Convert to CSV
      const headers = Object.keys(paymentListData[0]).join(',');
      const rows = paymentListData.map(p => 
        Object.values(p).map(v => {
          if (v === null || v === undefined) return '';
          if (typeof v === 'string' && v.includes(',')) return `"${v}"`;
          return v;
        }).join(',')
      );
      content = [headers, ...rows].join('\n');
      filename = `bulk_payment_list_${Date.now()}.csv`;
      mimeType = 'text/csv';
    } else if (format === 'starling_csv') {
      const templateRows = [
        ['DO NOT ALTER THIS TEMPLATE', '', '', '', '', '', ''],
        ['Sort Code', 'Account Number', 'First Name', 'Last Name', 'Business Name', 'Reference', 'Amount (GBP)']
      ];

      const paymentRows = paymentListData.map((payment) => {
        const firstName = String(payment.payee_first_name || '').trim();
        const lastName = String(payment.payee_surname || '').trim();
        const businessName = firstName || lastName
          ? ''
          : String(payment.payee_organisation_name || '').trim();
        const normalizedSortCode = String(payment.sort_code || '').replace(/\D/g, '');
        const normalizedAccountNumber = String(payment.account_number || '').replace(/\D/g, '');
        const parsedAmount = Number.parseFloat(payment.amount);
        const paymentReference = String(payment.payment_reference || '');

        return [
          normalizedSortCode,
          normalizedAccountNumber,
          firstName,
          lastName,
          businessName,
          paymentReference,
          Number.isFinite(parsedAmount) ? parsedAmount.toFixed(2) : '0.00'
        ];
      });

      content = [...templateRows, ...paymentRows]
        .map((row) => row.map(toCsvCell).join(','))
        .join('\n');
      filename = `starling_bulk_payment_upload_${Date.now()}.csv`;
      mimeType = 'text/csv';
    }

    const blob = new Blob([content], { type: mimeType });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getPaymentSubcontractorLabel = (payment) => {
    if (!payment) return 'Subcontractor';
    const person = [payment.subcontractor_first_name, payment.subcontractor_surname].filter(Boolean).join(' ');
    return person || payment.subcontractor_name || payment.subcontractor_email || 'Subcontractor';
  };

  const openBulkApproveModal = () => {
    if (selectedPayments.length === 0) {
      if (window.showToast) {
        window.showToast('Please select at least one payment to approve', 'error');
      }
      return;
    }

    const selected = payments.filter((p) => selectedPayments.includes(p.payment_request_id));
    const decisions = {};
    selected.forEach((p) => {
      decisions[p.payment_request_id] = {
        reverseChargeApplies: !!p.reverse_charge_default,
        overrideReason: '',
      };
    });
    setBulkApproveDecisions(decisions);
    setInviteError('');
    setShowBulkApproveModal(true);
  };

  const updateBulkApproveDecision = (paymentId, patch) => {
    setBulkApproveDecisions((prev) => ({
      ...prev,
      [paymentId]: { ...prev[paymentId], ...patch },
    }));
  };

  const confirmBulkApprove = async () => {
    if (selectedPayments.length === 0) return;

    setShowBulkApproveModal(false);
    setBulkApproveLoading(true);
    setInviteError('');
    setInviteSuccess('');

    const selectedById = Object.fromEntries(
      payments
        .filter((p) => selectedPayments.includes(p.payment_request_id))
        .map((p) => [p.payment_request_id, p])
    );

    try {
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      for (const paymentId of selectedPayments) {
        const payment = selectedById[paymentId];
        const decision = bulkApproveDecisions[paymentId] || {
          reverseChargeApplies: !!payment?.reverse_charge_default,
          overrideReason: '',
        };

        const payload = { comment: null };
        if (payment?.reverse_charge_eligible) {
          payload.reverse_charge_applies = decision.reverseChargeApplies;
          const defaultRc = !!payment.reverse_charge_default;
          if (decision.reverseChargeApplies !== defaultRc && decision.overrideReason?.trim()) {
            payload.reverse_charge_override_reason = decision.overrideReason.trim();
          }
        }

        try {
          const endpoint = `${API_CONFIG.endpoints.approvePaymentRequest}/${paymentId}/approve`;
          const response = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl(endpoint),
            {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(payload),
            }
          );

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
            const errorData = await response.json();
            errors.push(errorData.detail || 'Failed to approve');
          }
        } catch (error) {
          failCount++;
          errors.push(error.message);
        }
      }

      if (successCount > 0) {
        const successMsg = `Successfully approved ${successCount} payment(s)`;
        setInviteSuccess(successMsg);
        if (window.showToast) {
          window.showToast(successMsg, 'success');
        }
      }

      if (failCount > 0) {
        const errorMsg = `Failed to approve ${failCount} payment(s): ${errors.slice(0, 3).join(', ')}`;
        setInviteError(errorMsg);
        if (window.showToast) {
          window.showToast(errorMsg, 'error');
        }
      }

      setSelectedPayments([]);
      setBulkApproveDecisions({});
      fetchPayments();

      setTimeout(() => {
        setInviteSuccess('');
        setInviteError('');
      }, 5000);
    } catch (error) {
      const errorMsg = 'Error approving payments: ' + error.message;
      setInviteError(errorMsg);
      if (window.showToast) {
        window.showToast(errorMsg, 'error');
      }
      setTimeout(() => setInviteError(''), 5000);
    } finally {
      setBulkApproveLoading(false);
    }
  };

  const handleBulkApprove = openBulkApproveModal;

  const handleBulkReject = async () => {
    if (selectedPayments.length === 0) {
      if (window.showToast) {
        window.showToast('Please select at least one payment to reject', 'error');
      }
      return;
    }

    if (!window.confirm(`Reject ${selectedPayments.length} payment(s)?`)) {
      return;
    }

    setBulkRejectLoading(true);
    setInviteError('');
    setInviteSuccess('');

    try {
      let successCount = 0;
      let failCount = 0;
      const errors = [];

      // Process each payment sequentially
      for (const paymentId of selectedPayments) {
        try {
          const endpoint = `${API_CONFIG.endpoints.rejectPaymentRequest}/${paymentId}/reject`;
          const response = await API_CONFIG.fetchWithAuth(
            API_CONFIG.getApiUrl(endpoint),
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({ comment: null })
            }
          );

          if (response.ok) {
            successCount++;
          } else {
            failCount++;
            const errorData = await response.json();
            errors.push(errorData.detail || 'Failed to reject');
          }
        } catch (error) {
          failCount++;
          errors.push(error.message);
        }
      }

      // Show results
      if (successCount > 0) {
        const successMsg = `Successfully rejected ${successCount} payment(s)`;
        setInviteSuccess(successMsg);
        if (window.showToast) {
          window.showToast(successMsg, 'success');
        }
      }
      
      if (failCount > 0) {
        const errorMsg = `Failed to reject ${failCount} payment(s): ${errors.slice(0, 3).join(', ')}`;
        setInviteError(errorMsg);
        if (window.showToast) {
          window.showToast(errorMsg, 'error');
        }
      }

      // Clear selections and refresh
      setSelectedPayments([]);
      fetchPayments();
      
      setTimeout(() => {
        setInviteSuccess('');
        setInviteError('');
      }, 5000);
    } catch (error) {
      const errorMsg = 'Error rejecting payments: ' + error.message;
      setInviteError(errorMsg);
      if (window.showToast) {
        window.showToast(errorMsg, 'error');
      }
      setTimeout(() => setInviteError(''), 5000);
    } finally {
      setBulkRejectLoading(false);
    }
  };


  const confirmActionHandler = async () => {
    if (!confirmAction) return;

    const isApprove = confirmAction === 'approve';
    const isReject = confirmAction === 'reject';
    const isMarkPaid = confirmAction === 'mark-paid';
    const isUnmarkPaid = confirmAction === 'unmark-paid';
    
    // Determine which payment to use
    const payment = (isMarkPaid || isUnmarkPaid) ? paymentToMarkPaid : (paymentForAction || selectedPayment);
    if (!payment) return;

    const paymentId = payment.payment_request_id || payment.id;
    if (!paymentId) {
      setInviteError('Payment request ID is missing. Please close and try again.');
      return;
    }

    if (isApprove) {
      setApproveLoading(true);
    } else if (isReject) {
      setRejectLoading(true);
    } else if (isMarkPaid || isUnmarkPaid) {
      setMarkPaidLoading(true);
    }
    
    setInviteError('');
    setInviteSuccess('');
    // Keep modal open during loading - don't close it yet
    
    try {
      let endpoint;
      if (isApprove) {
        endpoint = `${API_CONFIG.endpoints.approvePaymentRequest}/${paymentId}/approve`;
      } else if (isReject) {
        endpoint = `${API_CONFIG.endpoints.rejectPaymentRequest}/${paymentId}/reject`;
      } else if (isMarkPaid) {
        endpoint = `${API_CONFIG.endpoints.markPaymentAsPaid}/${paymentId}/mark-paid`;
      } else if (isUnmarkPaid) {
        endpoint = `${API_CONFIG.endpoints.markPaymentAsPaid}/${paymentId}/unmark-paid`;
      }
      
      let requestBody = null;
      if (isApprove) {
        const approvePayload = { comment: actionComment || null };
        if (payment.reverse_charge_eligible) {
          approvePayload.reverse_charge_applies = reverseChargeApplies;
          const defaultRc = !!payment.reverse_charge_default;
          if (reverseChargeApplies !== defaultRc && reverseChargeOverrideReason.trim()) {
            approvePayload.reverse_charge_override_reason = reverseChargeOverrideReason.trim();
          }
        }
        requestBody = JSON.stringify(approvePayload);
      } else if (isReject) {
        requestBody = JSON.stringify({ comment: actionComment || null });
      } else if (isMarkPaid || isUnmarkPaid) {
        requestBody = null;
      }
      
      const fetchOptions = {
        method: 'POST'
      };
      if (requestBody) {
        fetchOptions.body = requestBody;
      }
      
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(endpoint),
        fetchOptions
      );

      if (response.ok) {
        // Close confirmation dialog first
        setShowConfirmDialog(false);
        setConfirmAction(null);
        setPaymentToMarkPaid(null);
        setPaymentForAction(null);
        
        setIsPaymentDetailModalOpen(false);
        setSelectedPayment(null);
        setActionComment('');
        setReverseChargeApplies(false);
        setReverseChargeOverrideReason('');
        fetchPayments();
        if (isMarkPaid) {
          if (selectedPayment && selectedPayment.payment_request_id === payment.payment_request_id) {
            setIsPaymentDetailModalOpen(false);
            setSelectedPayment(null);
          }
          setInviteSuccess('Payment marked as paid successfully');
        } else if (isUnmarkPaid) {
          if (selectedPayment && selectedPayment.payment_request_id === payment.payment_request_id) {
            setIsPaymentDetailModalOpen(false);
            setSelectedPayment(null);
          }
          setInviteSuccess('Payment returned to To be paid');
        } else {
          setInviteSuccess(`Payment request ${isApprove ? 'approved' : 'rejected'} successfully`);
        }
        setTimeout(() => setInviteSuccess(''), 3000);
      } else {
        // Keep modal open on error so user can see the error or retry (no auto-clear — stays until close or retry)
        let detail = null;
        try {
          const errorData = await response.json();
          detail = errorData.detail;
        } catch {
          detail = response.statusText || null;
        }
        const detailStr =
          typeof detail === 'string'
            ? detail
            : Array.isArray(detail)
              ? detail.map((d) => (typeof d === 'object' && d?.msg ? d.msg : String(d))).join(', ')
              : detail && typeof detail === 'object'
                ? detail.message || JSON.stringify(detail)
                : null;
        const errorMessage = isApprove ? 'approve' : isReject ? 'reject' : 'mark as paid';
        setInviteError(detailStr || `Failed to ${errorMessage} payment request`);
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
      }
    } finally {
      if (isApprove) {
        setApproveLoading(false);
      } else if (isReject) {
        setRejectLoading(false);
      } else if (isMarkPaid || isUnmarkPaid) {
        setMarkPaidLoading(false);
      }
      // Do not clear confirmAction/paymentForAction here: on success they are cleared in if (response.ok). On error we keep them so the modal stays open and the confirm button keeps its label (e.g. "Confirm Approve").
    }
  };


  // Filter payments by status (client-side filtering for tab convenience)
  // Note: For better performance with large datasets, this should be done on the backend
  const filteredPayments = payments.filter(payment => {
    return payment.status === paymentTab;
  });

  // Reset page when tab changes
  useEffect(() => {
    setPaymentsPage(1);
  }, [paymentTab]);

  // Reset payment tab to 'pending' when switching to Payments section
  useEffect(() => {
    if (activeMenu === 'payments') {
      setPaymentTab('pending');
    }
  }, [activeMenu]);

  // Reset page when activeTab changes
  useEffect(() => {
    setSubcontractorsPage(1);
  }, [activeTab]);

  const handleAcceptRequest = async (requestId) => {
    setRequestActionLoading({ ...requestActionLoading, [requestId]: 'accept' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.acceptConnectionRequest}/${requestId}/accept`),
        { method: 'POST' }
      );

      if (response.ok) {
        // Refresh pending requests and connected subcontractors
        await fetchPendingRequests();
        await fetchConnectedSubcontractors();
        setInviteSuccess('Connection request accepted successfully');
        setTimeout(() => setInviteSuccess(''), 5000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to accept request');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      setInviteError('Network error. Please try again.');
      setTimeout(() => setInviteError(''), 5000);
    } finally {
      setRequestActionLoading({ ...requestActionLoading, [requestId]: null });
    }
  };

  const handleRejectRequest = async (requestId) => {
    setRequestActionLoading({ ...requestActionLoading, [requestId]: 'reject' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.rejectConnectionRequest}/${requestId}/reject`),
        { method: 'POST' }
      );

      if (response.ok) {
        // Refresh pending requests
        await fetchPendingRequests();
        setInviteSuccess('Connection request rejected');
        setTimeout(() => setInviteSuccess(''), 5000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to reject request');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      setInviteError('Network error. Please try again.');
      setTimeout(() => setInviteError(''), 5000);
    } finally {
      setRequestActionLoading({ ...requestActionLoading, [requestId]: null });
    }
  };

  const handleRevokeEmailInvite = async (inviteId) => {
    if (
      !window.confirm(
        'Withdraw this invitation? The person will no longer be able to register or sign in using the invite link.'
      )
    ) {
      return;
    }
    setRequestActionLoading({ ...requestActionLoading, [inviteId]: 'revoke' });
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.revokeSubcontractorInvite}/${inviteId}`),
        { method: 'DELETE' }
      );

      if (response.ok) {
        await fetchPendingRequests();
        setInviteSuccess('Invitation withdrawn');
        setTimeout(() => setInviteSuccess(''), 5000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to withdraw invitation');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      setInviteError('Network error. Please try again.');
      setTimeout(() => setInviteError(''), 5000);
    } finally {
      setRequestActionLoading({ ...requestActionLoading, [inviteId]: null });
    }
  };

  // User Management Functions
  const fetchEmployerUsers = async () => {
    setUsersLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getEmployerUsers}?page=1&per_page=100`)
      );
      
      if (response.ok) {
        const data = await response.json();
        setEmployerUsers(data.items || []);
        setUsersTotal(data.total || 0);
        setUsersTotalPages(data.total_pages || 0);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to fetch users');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
        setTimeout(() => setInviteError(''), 5000);
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const handleCreateUser = async () => {
    if (!userFormData.email) {
      setInviteError('Please provide an email address');
      setTimeout(() => setInviteError(''), 5000);
      return;
    }

    setUserLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.createEmployerUser),
        {
          method: 'POST',
          body: JSON.stringify({ 
            email: userFormData.email,
            permissions: userFormData.permissions
          })
        }
      );

      if (response.ok) {
        setIsUserModalOpen(false);
        setUserFormData({ 
          email: '', 
          full_name: '', 
          phone_number: '',
          permissions: {
            view_payments: false,
            approve_edit_payments: false,
            make_payments: false,
            add_users: false,
            view_users: false,
            view_subcontractors: false,
            add_subcontractors: false
          }
        });
        await fetchEmployerUsers();
        setInviteSuccess('User invitation sent successfully. An email has been sent to the user to complete their profile.');
        setTimeout(() => setInviteSuccess(''), 5000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to create user');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
        setTimeout(() => setInviteError(''), 5000);
      }
    } finally {
      setUserLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserFormData({
      email: user.email,
      full_name: user.full_name,
      phone_number: user.phone_number || '',
      permissions: user.permissions || {
        view_payments: false,
        approve_edit_payments: false,
        make_payments: false,
        add_users: false,
        view_users: false,
        view_subcontractors: false,
        add_subcontractors: false
      }
    });
    setIsEditUserModalOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !userFormData.full_name) {
      setInviteError('Please fill in all required fields');
      setTimeout(() => setInviteError(''), 5000);
      return;
    }

    setUserLoading(true);
    try {
      const updateData = {
        full_name: userFormData.full_name,
        phone_number: userFormData.phone_number || null,
        status: selectedUser.status,
        permissions: userFormData.permissions
      };

      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.updateEmployerUser}/${selectedUser.user_id}`),
        {
          method: 'PUT',
          body: JSON.stringify(updateData)
        }
      );

      if (response.ok) {
        setIsEditUserModalOpen(false);
        setSelectedUser(null);
        setUserFormData({ 
          email: '', 
          full_name: '', 
          phone_number: '',
          permissions: {
            view_payments: false,
            approve_edit_payments: false,
            make_payments: false,
            add_users: false,
            view_users: false,
            view_subcontractors: false,
            add_subcontractors: false
          }
        });
        await fetchEmployerUsers();
        setInviteSuccess('User updated successfully');
        setTimeout(() => setInviteSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to update user');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
        setTimeout(() => setInviteError(''), 5000);
      }
    } finally {
      setUserLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }

    setUserLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.deleteEmployerUser}/${userId}`),
        { method: 'DELETE' }
      );

      if (response.ok) {
        await fetchEmployerUsers();
        setInviteSuccess('User deleted successfully');
        setTimeout(() => setInviteSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to delete user');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
        setTimeout(() => setInviteError(''), 5000);
      }
    } finally {
      setUserLoading(false);
    }
  };

  // Monthly Statement Functions
  const fetchAvailableCISMonths = async () => {
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getAvailableCISMonths}?limit=12`)
      );
      
      if (response.ok) {
        const data = await response.json();
        setAvailableCISMonths(data);
        // Auto-select the first (most recent) month if no month is currently selected
        // This provides a better UX by showing data immediately
        if (data.length > 0 && !selectedCISYear && !selectedCISMonth) {
          setSelectedCISYear(data[0].year);
          setSelectedCISMonth(data[0].month);
        }
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        console.error('Error fetching CIS months:', error);
      }
    }
  };

  const fetchCompanyMonthlySummary = async () => {
    if (!selectedCISYear || !selectedCISMonth) return;
    
    setSummaryLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getCompanyCISMonthlySummary}?year=${selectedCISYear}&month=${selectedCISMonth}`)
      );
      
      if (response.ok) {
        const data = await response.json();
        setCompanyMonthlySummary(data);
        // Also fetch payments for this CIS month
        fetchCISMonthPayments();
      } else {
        // Clear previous data when there's an error (e.g., no records found)
        setCompanyMonthlySummary(null);
        setCisMonthPayments([]);
        let errorData = {};
        try {
          errorData = await response.json();
        } catch (_) {
          /* ignore */
        }
        const detail = (errorData.detail || '').toString();
        const isNoPayments =
          response.status === 404 &&
          detail.toLowerCase().includes('no approved payments');
        // Nil CIS return is valid — no error toast for empty month
        if (!isNoPayments) {
          setInviteError(detail || 'Failed to fetch monthly summary');
          setTimeout(() => setInviteError(''), 5000);
        }
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
        setTimeout(() => setInviteError(''), 5000);
      }
    } finally {
      setSummaryLoading(false);
    }
  };

  const fetchCISMonthPayments = async () => {
    if (!selectedCISYear || !selectedCISMonth) return;
    
    setCisMonthPaymentsLoading(true);
    try {
      // Fetch all payments by getting all pages
      let allPayments = [];
      let currentPage = 1;
      let hasMore = true;
      
      while (hasMore) {
        const url = new URL(API_CONFIG.getApiUrl(API_CONFIG.endpoints.getPaymentRequests));
        url.searchParams.append('page', currentPage);
        url.searchParams.append('per_page', 100);
        url.searchParams.append('view_mode', 'employer');
        
        const response = await API_CONFIG.fetchWithAuth(url.toString(), { method: 'GET' });
        
        if (response.ok) {
          const data = await response.json();
          const pagePayments = data.items || [];
          allPayments = allPayments.concat(pagePayments);
          
          // Check if there are more pages
          hasMore = currentPage < (data.total_pages || 1);
          currentPage++;
        } else {
          hasMore = false;
        }
      }
        
      // Calculate CIS month range - match backend logic exactly
      // CIS months run from 6th of one month to 5th of next month
      // Example: November 2025 = Nov 6, 2025 to Dec 5, 2025
      let startYear = selectedCISYear;
      let startMonth = selectedCISMonth; // 1-12 format
      let endYear = selectedCISYear;
      let endMonth = selectedCISMonth + 1; // Next month
      
      // Handle year rollover
      if (selectedCISMonth === 12) {
        endYear = selectedCISYear + 1;
        endMonth = 1;
      }
      
      // Create date strings in YYYY-MM-DD format for comparison
      const startDateStr = `${startYear}-${String(startMonth).padStart(2, '0')}-06`;
      const endDateStr = `${endYear}-${String(endMonth).padStart(2, '0')}-05`;
      
      // Filter payments that are approved/paid and fall within CIS month
      const filteredPayments = allPayments.filter(payment => {
        // Check status
        if (payment.status !== 'approved' && payment.status !== 'paid') return false;
        
        // Use approved_at if available (as backend does), otherwise skip
        if (!payment.approved_at) return false;
        
        // Extract date part from ISO string (YYYY-MM-DD)
        const paymentDateStr = payment.approved_at.split('T')[0];
        
        // Simple string comparison works because YYYY-MM-DD format is lexicographically sortable
        return paymentDateStr >= startDateStr && paymentDateStr <= endDateStr;
      });
      
      // Sort by subcontractor name, then by approved date
      filteredPayments.sort((a, b) => {
        const nameA = (a.subcontractor_name || '').toLowerCase();
        const nameB = (b.subcontractor_name || '').toLowerCase();
        if (nameA !== nameB) return nameA.localeCompare(nameB);
        const dateA = new Date(a.approved_at || a.paid_at || a.submitted_at);
        const dateB = new Date(b.approved_at || b.paid_at || b.submitted_at);
        return dateA - dateB;
      });
      
      setCisMonthPayments(filteredPayments);
    } catch (error) {
      console.error('Error fetching CIS month payments:', error);
      setCisMonthPayments([]);
    } finally {
      setCisMonthPaymentsLoading(false);
    }
  };

  const fetchSubcontractorMonthlyStatement = async (subcontractorId) => {
    if (!selectedCISYear || !selectedCISMonth) return;
    
    setMonthlyStatementsLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.getSubcontractorMonthlyStatement}/${subcontractorId}?year=${selectedCISYear}&month=${selectedCISMonth}`)
      );
      
      if (response.ok) {
        const data = await response.json();
        setMonthlyStatements(prev => ({
          ...prev,
          [subcontractorId]: data
        }));
        return data;
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to fetch monthly statement');
        setTimeout(() => setInviteError(''), 5000);
        return null;
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
        setTimeout(() => setInviteError(''), 5000);
      }
      return null;
    } finally {
      setMonthlyStatementsLoading(false);
    }
  };

  const handleViewMonthlyStatement = async (subcontractor) => {
    setSelectedSubcontractorForStatement(subcontractor);
    const statement = await fetchSubcontractorMonthlyStatement(subcontractor.subcontractor_id);
    if (statement) {
      setCurrentMonthlyStatement(statement);
      setIsMonthlyStatementModalOpen(true);
    }
  };

  const handleDownloadMonthlyStatementPDF = async (subcontractorId) => {
    if (!selectedCISYear || !selectedCISMonth) return;
    
    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');
      const response = await fetch(
        API_CONFIG.getApiUrl(`${API_CONFIG.endpoints.downloadMonthlyStatementPDF}/${subcontractorId}/pdf?year=${selectedCISYear}&month=${selectedCISMonth}`),
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const monthName = availableCISMonths.find(m => m.year === selectedCISYear && m.month === selectedCISMonth)?.label.split(' ')[0] || 'monthly';
        a.download = `monthly_cis_statement_${monthName}_${selectedCISYear}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to download PDF');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (error) {
      setInviteError('Network error. Please try again.');
      setTimeout(() => setInviteError(''), 5000);
    }
  };

  // HMRC Submission functions
  const fetchHMRCSubmissionStatus = async () => {
    if (!selectedCISYear || !selectedCISMonth) return;
    
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`/unified-auth/hmrc/submissions?year=${selectedCISYear}&month=${selectedCISMonth}`)
      );
      
      if (response.ok) {
        const submissions = await response.json();
        // Only show SUCCESSFUL submissions - don't display old failed attempts
        // Failed submissions will show errors during the actual submission process via inviteError
        const submission = submissions.find(s => 
          s.cis_month_year === selectedCISYear && 
          s.cis_month_month === selectedCISMonth &&
          s.success === true
        );
        setHmrcSubmissionStatus(submission || null);
      } else {
        setHmrcSubmissionStatus(null);
      }
    } catch (error) {
      if (error.message !== 'Token expired') {
        console.error('Error fetching HMRC submission status:', error);
      }
      setHmrcSubmissionStatus(null);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleConnectHMRC = async () => {
    // Check if user is authenticated
    const token = localStorage.getItem('access_token');
    if (!token) {
      setInviteError('You must be logged in to connect to HMRC. Please log in and try again.');
      setTimeout(() => setInviteError(''), 5000);
      return;
    }
    
    try {
      // Get the authorization URL from backend via authenticated request
      // Pass redirect_page parameter to tell backend where to redirect after OAuth
      const redirectPage = 'payments';
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(`/unified-auth/hmrc/get-auth-url?redirect_page=${redirectPage}`), 
        {
          method: 'GET'
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        // Store the current CIS month state so we can return here after OAuth
        if (selectedCISYear && selectedCISMonth) {
          localStorage.setItem('hmrc_redirect_cis_month', `${selectedCISYear}-${selectedCISMonth}`);
        }
        // Redirect to HMRC authorization URL
        window.location.href = data.auth_url;
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to initiate HMRC connection');
        setTimeout(() => setInviteError(''), 5000);
      }
    } catch (err) {
      if (err.message === 'Token expired') {
        // fetchWithAuth already handles redirect to login
        return;
      }
      setInviteError('Network error. Please try again.');
      setTimeout(() => setInviteError(''), 5000);
    }
  };

  // Gateway credentials are now system-wide (stored in ENV), not per-user
  // Removed: handleStoreGatewayCredentials function

  // Client-side CIS month submittability (mirrors backend: period ends 5th of next month, submittable from 6th)
  const isCisMonthSubmittable = (year, month) => {
    if (!year || !month) return false;
    const now = new Date();
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    const submittableFrom = new Date(nextYear, nextMonth - 1, 6, 0, 0, 0);
    return now >= submittableFrom;
  };

  const getCisMonthSubmittableFrom = (year, month) => {
    if (!year || !month) return null;
    const nextMonth = month === 12 ? 1 : month + 1;
    const nextYear = month === 12 ? year + 1 : year;
    return new Date(nextYear, nextMonth - 1, 6);
  };

  const handleSubmitToHMRC = () => {
    if (!selectedCISYear || !selectedCISMonth) {
      setInviteError('Please select a CIS month first');
      setTimeout(() => setInviteError(''), 5000);
      return;
    }
    if (!isCisMonthSubmittable(selectedCISYear, selectedCISMonth)) {
      const from = getCisMonthSubmittableFrom(selectedCISYear, selectedCISMonth);
      const fromStr = from ? from.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
      setInviteError(`This return cannot be submitted until the period ends. It will be available from ${fromStr}.`);
      setTimeout(() => setInviteError(''), 8000);
      return;
    }
    setInviteError('');
    setShowHMRCDeclarationModal(true);
  };

  const handleConfirmHMRCDeclaration = async () => {
    setShowHMRCDeclarationModal(false);
    setHmrcSubmitting(true);
    setInviteError('');
    localStorage.removeItem('hmrc_redirect_cis_month');
    localStorage.removeItem('hmrc_redirect_after_connect');
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl('/unified-auth/hmrc/submit-cis'),
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            year: selectedCISYear,
            month: selectedCISMonth,
            information_correct_confirmed: true
          })
        }
      );
      if (response.ok) {
        const submission = await response.json();
        setHmrcSubmissionStatus(submission);
        const correlationId = submission.hmrc_submission_id || 'N/A';
        const responseMessage = submission.response_payload?.message || 'Submission successful';
        setInviteSuccess(`✅ CIS Return Submitted Successfully!\n\nCorrelation ID: ${correlationId}\n\nHMRC Response: ${responseMessage}`);
        setTimeout(() => setInviteSuccess(''), 15000);
        await fetchHMRCSubmissionStatus();
      } else {
        let errorMessage = 'Failed to submit CIS return to HMRC';
        try {
          const errorData = await response.json();
          errorMessage = errorData.detail || errorMessage;
        } catch (_) {
          errorMessage = response.status === 400
            ? 'The return may not be available yet. CIS returns can only be submitted after the period ends (from the 6th of the following month).'
            : errorMessage;
        }
        setInviteError(`❌ Submission Failed: ${errorMessage}`);
        setTimeout(() => setInviteError(''), 10000);
      }
    } catch (error) {
      if (error.message === 'Token expired') {
        return;
      }
      console.error('HMRC Submission Error:', error);
      const isNetworkError = error.message === 'Failed to fetch' || error.message?.toLowerCase().includes('network');
      const msg = isNetworkError
        ? 'Unable to reach the server. The return can only be submitted after the period ends (from the 6th of the following month). Please check your connection and try again.'
        : `❌ ${error.message || 'Please check your connection and try again.'}`;
      setInviteError(msg);
      setTimeout(() => setInviteError(''), 10000);
    } finally {
      setHmrcSubmitting(false);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const fetchHMRCTestData = async () => {
    // Check if user is authenticated before making request
    const token = localStorage.getItem('access_token');
    if (!token) {
      setError('Please log in to test HMRC connection');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Use API_CONFIG.fetchWithAuth but catch the redirect
      // This ensures proper token handling
      const response = await fetch(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.testOrganizationHMRCConnection),
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        setHmrcData(data);
        setError(''); // Clear any previous errors
      } else {
        // Handle different error statuses
        let errorMessage = 'Failed to fetch HMRC data';
        let errorDetail = null;
        
        try {
          const errorData = await response.json();
          errorDetail = errorData.detail || errorData.message || null;
          errorMessage = errorDetail || errorMessage;
        } catch (e) {
          // If response is not JSON, use status text
          errorMessage = response.statusText || errorMessage;
        }
        
        if (response.status === 401) {
          // Check if it's an HMRC token expiration error
          if (errorDetail && errorDetail.includes('HMRC access token has expired')) {
            errorMessage = errorDetail + ' Click "Become an Employer" in the menu to reconnect.';
          } else {
            errorMessage = errorDetail || 'Authentication failed. The token may be invalid or expired. Please refresh the page.';
          }
        } else if (response.status === 400) {
          errorMessage = errorDetail || 'HMRC is not connected. Please connect your HMRC account first.';
        }
        
        setError(errorMessage);
      }
    } catch (err) {
      // Network errors - don't redirect
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveOrganizationProfile = async () => {
    const { errors, isValid } = validateOrgComplianceForm(orgFormData, {
      requirePayeAndAccountsOffice: true,
    });
    if (!isValid) {
      setOrgFormFieldErrors(errors);
      if (window.showToast) window.showToast('Please fix the validation errors below.', 'error');
      return;
    }
    setOrgFormFieldErrors({});
    setOrgSaveLoading(true);
    try {
      const utrDigits = normalizeOrgUtrInput(orgFormData.utr);
      const companyDigits = String(orgFormData.company_number || '').replace(/\D/g, '').slice(0, 8);
      const payload = {
        company_number: companyDigits || null,
        utr: utrDigits.length === 10 ? utrDigits : null,
        accounts_office_reference: (orgFormData.accounts_office_reference || '').trim() || null,
        paye_reference: (orgFormData.paye_reference || '').trim() || null,
        building_name_no: orgFormData.building_name_no || null,
        street: orgFormData.street || null,
        city: orgFormData.city || null,
        postal_code: orgFormData.postal_code || null,
      };
      if ((orgFormData.hmrc_gateway_user_id || '').trim()) {
        payload.hmrc_gateway_user_id = orgFormData.hmrc_gateway_user_id.trim();
      }
      if ((orgFormData.hmrc_gateway_password || '').trim()) {
        payload.hmrc_gateway_password = orgFormData.hmrc_gateway_password.trim();
      }

      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.updateOrganizationProfile),
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.detail || 'Failed to save organisation profile');
      }
      const refreshed = await refreshUserData();
      if (refreshed) {
        setShowOrgComplianceModal(false);
      }
      if (window.showToast) window.showToast('organisation profile updated', 'success');
    } catch (e) {
      if (window.showToast) window.showToast(e.message || 'Failed to save organisation profile', 'error');
    } finally {
      setOrgSaveLoading(false);
    }
  };

  const handleTestOrganizationHMRCConnection = async () => {
    setOrgTestLoading(true);
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.testOrganizationHMRCConnection),
        { method: 'POST' }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        const detail = data.detail;
        let msg;
        if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
          msg = detail.message || data.message || 'HMRC test failed';
        } else if (Array.isArray(detail)) {
          msg = detail.map((d) => d.msg || d).join(', ');
        } else {
          msg = typeof detail === 'string' ? detail : (data.message || 'HMRC test failed');
        }
        throw new Error(msg);
      }
      if (data.hmrc_connection_ok === false) {
        const err = data.result?.error || 'HMRC connection test failed';
        throw new Error(err);
      }
      await refreshUserData();
      if (window.showToast) {
        window.showToast(
          "CIS Transaction Engine test passed: HMRC accepted your Gateway credentials together with this organisation's UTR and Accounts Office reference.",
          'success',
          7000
        );
      }
    } catch (e) {
      if (window.showToast) window.showToast(e.message || 'HMRC test failed', 'error');
    } finally {
      setOrgTestLoading(false);
    }
  };

  const handleInviteSubcontractor = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteError('');
    setInviteSuccess('');
    
    try {
      const response = await API_CONFIG.fetchWithAuth(
        API_CONFIG.getApiUrl(API_CONFIG.endpoints.inviteSubcontractor),
        {
          method: 'POST',
          body: JSON.stringify({ email: inviteEmail })
        }
      );
      
      if (response.ok) {
        const inviteJson = await response.json();
        setInviteSuccess(inviteJson.message || `Invitation sent successfully to ${inviteEmail}`);
        setInviteEmail('');
        await fetchConnectedSubcontractors();
        await fetchPendingRequests();
        // Close modal after successful invite
        setTimeout(() => {
          setIsModalOpen(false);
          setInviteSuccess('');
        }, 2000);
      } else {
        const errorData = await response.json();
        setInviteError(errorData.detail || 'Failed to send invitation');
      }
    } catch (err) {
      if (err.message !== 'Token expired') {
        setInviteError('Network error. Please try again.');
      }
    } finally {
      setInviteLoading(false);
    }
  };

  // Merged subcontractor list for Monthly statements dropdown (relationships + connected tab + payments fallback)
  const monthlyStatementsDropdownSubcontractors = useMemo(() => {
    const byId = new Map();
    [...(monthlyStatementsSubcontractors || []), ...(connectedSubcontractors || [])].forEach((s) => {
      const id = s.subcontractor_id;
      if (id && !byId.has(id)) byId.set(id, { subcontractor_id: id, subcontractor_name: s.subcontractor_name || 'N/A' });
    });
    let list = Array.from(byId.values());
    if (list.length === 0 && Array.isArray(payments) && payments.length > 0) {
      const seen = new Set();
      payments.forEach((p) => {
        if (p.subcontractor_id && !seen.has(p.subcontractor_id)) {
          seen.add(p.subcontractor_id);
          list = list.concat({ subcontractor_id: p.subcontractor_id, subcontractor_name: p.subcontractor_name || 'N/A' });
        }
      });
    }
    return list;
  }, [monthlyStatementsSubcontractors, connectedSubcontractors, payments]);

  const hmrcSubmittedMonthsKey = [...hmrcSubmittedMonthsSet].sort().join(',');

  useEffect(() => {
    const items = [];
    if (isMainEmployer() || hasPermission('view_subcontractors')) {
      items.push({
        id: 'subcontractors',
        label: 'Subcontractors',
        active: activeMenu === 'subcontractors',
        onActivate: () => setActiveMenu('subcontractors'),
      });
    }
    if (hasPermission('view_payments')) {
      items.push({
        id: 'payments',
        label: 'Payments',
        active: activeMenu === 'payments',
        onActivate: () => setActiveMenu('payments'),
      });
    }
    if (isMainEmployer()) {
      const readyCount = availableCISMonths.filter(
        (m) =>
          m.has_payments === true &&
          (m.submittable === true || isCisMonthSubmittable(m.year, m.month)) &&
          !hmrcSubmittedMonthsSet.has(`${m.year}-${m.month}`)
      ).length;
      items.push({
        id: 'cis-returns',
        label: 'CIS Returns',
        active: activeMenu === 'cis-returns',
        badge: readyCount > 0 ? readyCount : undefined,
        onActivate: () => setActiveMenu('cis-returns'),
      });
    }
    setDashboardNav(items);
    return () => clearDashboardNav();
  }, [
    activeMenu,
    availableCISMonths,
    hmrcSubmittedMonthsKey,
    setDashboardNav,
    clearDashboardNav,
    currentUser,
  ]);

  return (
    <div className="company-dashboard">
      {canUseSubcontractorDashboard && pendingEmployerInvitesActionCount > 0 && (
        <div
          style={{
            backgroundColor: 'rgba(255, 193, 7, 0.15)',
            border: '1px solid rgba(255, 193, 7, 0.45)',
            borderRadius: '8px',
            padding: '14px 16px',
            marginBottom: '16px',
            color: '#ffe08a',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
          }}
          role="status"
          aria-live="polite"
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <div style={{ fontSize: '18px' }} aria-hidden="true">⚠️</div>
            <div>
              <div style={{ fontWeight: 700, marginBottom: '2px' }}>Pending employer invitations</div>
              <div style={{ fontSize: '13px' }}>
                You have {pendingEmployerInvitesActionCount} invitation{pendingEmployerInvitesActionCount === 1 ? '' : 's'} waiting for you to review and accept.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="action-button"
            onClick={() => {
              window.location.assign('/dashboard/subcontractor?menu=employers');
            }}
          >
            Review now
          </button>
        </div>
      )}
      {(orgMissingForVerification.length > 0 || orgMissingForPayments.length > 0) && (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            marginBottom: '16px',
          }}
        >
          <button
            type="button"
            className="action-button complete-setup-button" style={{ padding: '15px 20px' }}
            onClick={() => setShowOrgComplianceModal(true)}
          >
            Complete Organisation Setup
          </button>
        </div>
      )}

      {showOrgComplianceModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => !orgSaveLoading && !orgTestLoading && setShowOrgComplianceModal(false)}>
          <div className="modal-content org-compliance-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Organisation Compliance Setup</h2>
              <button type="button" className="modal-close" onClick={() => !orgSaveLoading && !orgTestLoading && setShowOrgComplianceModal(false)} aria-label="Close">×</button>
            </div>
            <div className="modal-body org-compliance-modal-body">
              <p className="org-compliance-intro">
                Enter your tax, address and HMRC details for verification and payment approval. Organisation name and type were set when you first opened the dashboard.
              </p>

              <h3 className="org-compliance-section-title">Tax &amp; PAYE</h3>
              <div className="form-row">
                <div className="form-field-group">
                  <label className="form-label" htmlFor="org-compliance-utr">UTR</label>
                  <input
                    id="org-compliance-utr"
                    className={`form-input${orgFormFieldErrors.utr ? ' form-input--error' : ''}`}
                    type="text"
                    inputMode="numeric"
                    autoComplete="off"
                    maxLength={10}
                    pattern="[0-9]{10}"
                    value={orgFormData.utr}
                    onChange={(e) => {
                      const v = normalizeOrgUtrInput(e.target.value);
                      setOrgFormData((prev) => ({ ...prev, utr: v }));
                      clearOrgFormFieldError('utr');
                    }}
                    placeholder="10 digit UTR (optional)"
                    style={{ fontFamily: 'monospace', letterSpacing: '2px' }}
                  />
                  <span className="org-compliance-field-hint">Ten digits only, as on HMRC letters.</span>
                  {orgFormFieldErrors.utr && (
                    <span className="form-field-error-msg">{orgFormFieldErrors.utr}</span>
                  )}
                </div>
                <div className="form-field-group">
                  <label className="form-label" htmlFor="org-compliance-aoref">Accounts Office reference</label>
                  <input
                    id="org-compliance-aoref"
                    className={`form-input${orgFormFieldErrors.accounts_office_reference ? ' form-input--error' : ''}`}
                    maxLength={ACCOUNTS_OFFICE_REFERENCE_MAX_LENGTH}
                    value={orgFormData.accounts_office_reference}
                    onChange={(e) => {
                      setOrgFormData((prev) => ({ ...prev, accounts_office_reference: e.target.value }));
                      clearOrgFormFieldError('accounts_office_reference');
                    }}
                    onBlur={(e) => {
                      const v = (e.target.value || '').trim();
                      if (!v) {
                        clearOrgFormFieldError('accounts_office_reference');
                        return;
                      }
                      if (!isValidAccountsOfficeReference(v)) {
                        setOrgFormFieldErrors((prev) => ({
                          ...prev,
                          accounts_office_reference: ACCOUNTS_OFFICE_REFERENCE_INVALID_MESSAGE,
                        }));
                      } else {
                        clearOrgFormFieldError('accounts_office_reference');
                      }
                    }}
                    placeholder="e.g. 914PV00196767"
                    title="10–14 characters: 3 digits then letters/numbers (HMRC Accounts Office ref)"
                    autoComplete="off"
                    style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
                  />
                  <span className="org-compliance-field-hint">
                    10–14 characters, 3 digits then letters and numbers (as on HMRC employer notices).
                  </span>
                  {orgFormFieldErrors.accounts_office_reference && (
                    <span className="form-field-error-msg">{orgFormFieldErrors.accounts_office_reference}</span>
                  )}
                </div>
              </div>
              <div className="form-row">
                <div className="form-field-group">
                  <label className="form-label" htmlFor="org-compliance-paye">PAYE reference</label>
                  <input
                    id="org-compliance-paye"
                    className={`form-input${orgFormFieldErrors.paye_reference ? ' form-input--error' : ''}`}
                    maxLength={PAYE_REFERENCE_MAX_LENGTH}
                    value={orgFormData.paye_reference}
                    onChange={(e) => {
                      setOrgFormData((prev) => ({ ...prev, paye_reference: e.target.value }));
                      clearOrgFormFieldError('paye_reference');
                    }}
                    onBlur={(e) => {
                      const v = (e.target.value || '').trim();
                      if (!v) {
                        clearOrgFormFieldError('paye_reference');
                        return;
                      }
                      if (!isValidPayeReference(v)) {
                        setOrgFormFieldErrors((prev) => ({
                          ...prev,
                          paye_reference: PAYE_REFERENCE_INVALID_MESSAGE,
                        }));
                      } else {
                        clearOrgFormFieldError('paye_reference');
                      }
                    }}
                    placeholder="e.g. 914/VA11166"
                    title="Format: 3 digits, forward slash, reference code (e.g. 914/VA11166)"
                    autoComplete="off"
                    style={{ fontFamily: 'monospace', letterSpacing: '1px' }}
                  />
                  <span className="org-compliance-field-hint">
                    Tax office number, slash, employer reference (e.g. 914/VA11166).
                  </span>
                  {orgFormFieldErrors.paye_reference && (
                    <span className="form-field-error-msg">{orgFormFieldErrors.paye_reference}</span>
                  )}
                </div>
              </div>

              <h3 className="org-compliance-section-title">Registered address</h3>
              <div className="form-row">
                <div className="form-field-group">
                  <label className="form-label" htmlFor="org-compliance-building">Building name / number</label>
                  <input
                    id="org-compliance-building"
                    className="form-input"
                    value={orgFormData.building_name_no}
                    onChange={(e) => setOrgFormData((prev) => ({ ...prev, building_name_no: e.target.value }))}
                    placeholder="Building name or number"
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label" htmlFor="org-compliance-street">Street</label>
                  <input
                    id="org-compliance-street"
                    className="form-input"
                    value={orgFormData.street}
                    onChange={(e) => setOrgFormData((prev) => ({ ...prev, street: e.target.value }))}
                    placeholder="Street"
                  />
                </div>
              </div>
              <div className="form-field-group">
                <label className="form-label" htmlFor="org-compliance-city">City / Town</label>
                <input
                  id="org-compliance-city"
                  className="form-input"
                  value={orgFormData.city}
                  onChange={(e) => setOrgFormData((prev) => ({ ...prev, city: e.target.value }))}
                  placeholder="City or town"
                />
              </div>
              <div className="form-field-group">
                <label className="form-label" htmlFor="org-compliance-postcode">Postcode</label>
                <input
                  id="org-compliance-postcode"
                  className={`form-input${orgFormFieldErrors.postal_code ? ' form-input--error' : ''}`}
                  value={orgFormData.postal_code}
                  onChange={(e) => {
                    setOrgFormData((prev) => ({ ...prev, postal_code: e.target.value }));
                    clearOrgFormFieldError('postal_code');
                  }}
                  placeholder="e.g. SW1A 1AA"
                  autoComplete="postal-code"
                  style={{ maxWidth: '280px' }}
                />
                {orgFormFieldErrors.postal_code && (
                  <span className="form-field-error-msg">{orgFormFieldErrors.postal_code}</span>
                )}
              </div>

              <h3 className="org-compliance-section-title">HMRC Government Gateway</h3>
              <p className="org-compliance-hint">
                Credentials are encrypted in the database. Saved values load here so you can review or replace them.
                Leave both fields blank when saving to keep what is already on file; type new values only to replace them.
              </p>
              <div className="form-row">
                <div className="form-field-group">
                  <label className="form-label" htmlFor="org-compliance-gateway-user">Gateway user ID</label>
                  <input
                    id="org-compliance-gateway-user"
                    name="paycis-hmrc-gateway-user-id"
                    className="form-input"
                    value={orgFormData.hmrc_gateway_user_id}
                    onChange={(e) => setOrgFormData((prev) => ({ ...prev, hmrc_gateway_user_id: e.target.value }))}
                    placeholder={orgProfile.hmrc_gateway_user_id_present ? 'On file — edit to replace' : 'Enter your Gateway user ID'}
                    autoComplete="off"
                    autoCorrect="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                </div>
                <div className="form-field-group">
                  <label className="form-label" htmlFor="org-compliance-gateway-pass">Gateway password</label>
                  <div className="org-compliance-password-field">
                    <input
                      id="org-compliance-gateway-pass"
                      name="paycis-hmrc-gateway-password"
                      type={showOrgComplianceHmrcPassword ? 'text' : 'password'}
                      className="form-input"
                      value={orgFormData.hmrc_gateway_password}
                      onChange={(e) => setOrgFormData((prev) => ({ ...prev, hmrc_gateway_password: e.target.value }))}
                      placeholder={orgProfile.hmrc_gateway_password_present ? 'On file — edit to replace' : 'Enter password when saving'}
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="org-compliance-password-toggle"
                      onClick={() => setShowOrgComplianceHmrcPassword((v) => !v)}
                      aria-label={showOrgComplianceHmrcPassword ? 'Hide password' : 'Show password'}
                    >
                      {showOrgComplianceHmrcPassword ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                          <line x1="1" y1="1" x2="23" y2="23" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                          <circle cx="12" cy="12" r="3" />
                        </svg>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              {/* <div
                className="org-compliance-gateway-test-explainer"
                style={{
                  marginTop: '0.5rem',
                  marginBottom: '0.75rem',
                  padding: '12px 14px',
                  background: 'rgba(0,0,0,0.04)',
                  borderRadius: '8px',
                  maxWidth: '52rem',
                  fontSize: '0.9rem',
                  lineHeight: 1.45,
                }}
              >
                <p style={{ margin: '0 0 0.5rem', fontWeight: 600 }}>What the test button checks</p>
                <ul style={{ margin: 0, paddingLeft: '1.25rem' }}>
                  <li style={{ marginBottom: '0.35rem' }}>
                    It sends a <em>minimal CIS subcontractor verification</em> XML message to HMRC&apos;s Transaction Engine (GovTalk). That is the technical route used for live CIS XML—not the same as signing in on the Government Gateway website.
                  </li>
                  <li style={{ marginBottom: '0.35rem' }}>
                    HMRC validates your Gateway user ID and password <em>together with</em> the contractor UTR, Accounts Office reference, and PAYE reference (tax office number and employer ref, e.g. 914/VA11166). This service does not offer a &quot;password only&quot; connectivity check.
                  </li>
                  <li>
                    HMRC&apos;s Transaction Engine does not have a &quot;Gateway ID + password only&quot; message: each request must be a complete CIS XML type, and a CIS verification payload must include a <code style={{ fontSize: '0.85em' }}>Subcontractor</code> block by schema. We use dummy name/UTR there so the envelope is valid; HMRC still decides pass/fail mainly from your contractor UTR, Accounts Office reference, and Gateway authentication—not from that fake subcontractor matching a real person.
                  </li>
                </ul>
              </div> */}

              <div className="org-compliance-modal-actions">
                <button
                  type="button"
                  className="modal-btn-outline"
                  onClick={handleTestOrganizationHMRCConnection}
                  disabled={orgTestLoading || orgSaveLoading}
                >
                  {orgTestLoading ? 'Testing Transaction Engine...' : 'Test CIS Transaction Engine'}
                </button>
                <div className="org-compliance-modal-actions-right">
                  <button
                    type="button"
                    className="modal-btn-outline"
                    onClick={() => setShowOrgComplianceModal(false)}
                    disabled={orgSaveLoading || orgTestLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="action-button"
                    onClick={handleSaveOrganizationProfile}
                    disabled={orgSaveLoading || orgTestLoading}
                    style={{ marginBottom: '0px', padding: '13px 20px' }}
                  >
                    {orgSaveLoading ? 'Saving...' : 'Save Organisation'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* <div style={{ marginBottom: '20px' }}>
        <h1 style={{ margin: 0 }}>Employer Dashboard</h1>
      </div> */}
      <div className="dashboard-content">
        {(() => {
          const dashboardSidebar = (
        <div className={`dashboard-sidebar${mobileBottomNav ? ' dashboard-sidebar--mobile-fixed' : ''}`}>
          <div className="sidebar-menu">
            {/* Subcontractors menu - visible if user has view_subcontractors permission or is main employer */}
            {(isMainEmployer() || hasPermission('view_subcontractors')) && (
              <div 
                className={`sidebar-menu-item ${activeMenu === 'subcontractors' ? 'active' : ''}`}
                onClick={() => setActiveMenu('subcontractors')}
              >
                <span className="menu-icon">👥</span>
                <span className="menu-text">Subcontractors</span>
              </div>
            )}
            {/* Payments menu - only visible if user has view_payments permission */}
            {hasPermission('view_payments') && (
              <div 
                className={`sidebar-menu-item ${activeMenu === 'payments' ? 'active' : ''}`}
                onClick={() => setActiveMenu('payments')}
              >
                <span className="menu-icon">💰</span>
                <span className="menu-text">Payments</span>
              </div>
            )}
            {/* CIS Returns menu - only visible if user is main employer */}
            {isMainEmployer() && (
              <div 
                className={`sidebar-menu-item ${activeMenu === 'cis-returns' ? 'active' : ''}`}
                onClick={() => setActiveMenu('cis-returns')}
                style={{ position: 'relative' }}
              >
                <span className="menu-icon">📋</span>
                <span className="menu-text">CIS Returns</span>
                {(() => {
                  const readyCount = availableCISMonths.filter(
                    m =>
                      m.has_payments === true &&
                      (m.submittable === true || isCisMonthSubmittable(m.year, m.month)) &&
                      !hmrcSubmittedMonthsSet.has(`${m.year}-${m.month}`)
                  ).length;
                  if (readyCount > 0) {
                    return (
                      <span style={{
                        position: 'absolute',
                        top: '6px',
                        right: '12px',
                        background: '#4caf50',
                        color: 'white',
                        fontSize: '11px',
                        fontWeight: '600',
                        borderRadius: '10px',
                        padding: '2px 6px',
                        minWidth: '18px',
                        textAlign: 'center'
                      }}>
                        {readyCount}
                      </span>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
        </div>
          );
          return (
            <>
              {!mobileBottomNav && dashboardSidebar}
              {mobileBottomNav && ReactDOM.createPortal(dashboardSidebar, document.body)}
            </>
          );
        })()}

        <div className="dashboard-main">
          {/* Show message if user has no accessible menus */}
          {!isMainEmployer() && !hasPermission('view_payments') && !hasPermission('view_users') && !hasPermission('view_subcontractors') && (
            <div className="empty-state" style={{ marginTop: '50px' }}>
              <h2>No Access</h2>
              <p>You don't have permission to access any sections. Please contact your administrator.</p>
            </div>
          )}
          
          {activeMenu === 'subcontractors' && (isMainEmployer() || hasPermission('view_subcontractors')) && (
            <>
              {/* Subcontractors Section with Tabs */}
              <div className="relationships-section">
                {(isMainEmployer() || hasPermission('add_subcontractors')) && (
                  <div className="section-header section-header--actions-only dashboard-tab-actions--mobile">
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => setIsModalOpen(true)}
                    >
                      + Invite Subcontractors
                    </button>
                  </div>
                )}
                {/* Tabs (+ invite on web, right) */}
                <div
                  className={`tabs-container${(isMainEmployer() || hasPermission('add_subcontractors')) ? ' tabs-container--with-inline-actions' : ''}`}
                >
                  <div className="tabs-container__tabs">
                    <button
                      type="button"
                      className={`tab-button ${activeTab === 'approved' ? 'active' : ''}`}
                      onClick={() => {
                        setSubcontractorsPage(1);
                        setActiveTab('approved');
                      }}
                    >
                      Approved ({approvedCount})
                    </button>
                    <button
                      type="button"
                      className={`tab-button ${activeTab === 'pending' ? 'active' : ''}`}
                      onClick={() => {
                        setSubcontractorsPage(1);
                        setActiveTab('pending');
                      }}
                    >
                      Pending ({pendingCount})
                    </button>
                  </div>
                  {(isMainEmployer() || hasPermission('add_subcontractors')) && (
                    <div className="tabs-container__actions dashboard-tab-actions--desktop">
                      <button
                        type="button"
                        className="action-button"
                        onClick={() => setIsModalOpen(true)}
                      >
                        + Invite Subcontractors
                      </button>
                    </div>
                  )}
                </div>

                {/* Tab Content */}
                {activeTab === 'approved' && (
                  <>
                    {subcontractorsLoading ? (
                      <div className="loading-state">Loading...</div>
                    ) : connectedSubcontractors.length > 0 ? (
                      <div className="table-container table-container--subcontractors-mobile-fullbleed">
                        <table className="relationships-table">
                          <thead>
                            <tr>
                              <th>Name</th>
                              <th>Status</th>
                              <th>Match</th>
                              <th>Date</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {connectedSubcontractors.map((subcontractor) => {
                              const cisVerifiedAt =
                                subcontractor.subcontractor_cis_verified_at ||
                                subcontractor.hmrc_verified_at;
                              const {
                                statusLabel: statusColLabel,
                                matchLabel: matchColLabel,
                                statusBadgeClass,
                              } = getEmployerSubcontractorStatusMatchPresentation(subcontractor);
                              const attempted = relationshipCisVerificationAttempted(subcontractor);
                              const hmrcOk = relationshipCisVerificationMatched(subcontractor);
                              const isUnverified = !attempted;
                              const isNoMatch = attempted && !hmrcOk;
                              const mobileVerifiedDate = cisVerifiedAt
                                ? new Date(cisVerifiedAt).toLocaleDateString('en-GB')
                                : '';
                              const registeredAt = subcontractor.subcontractor_registered_at;
                              const registeredDateDisplay = registeredAt
                                ? new Date(registeredAt).toLocaleDateString('en-GB', {
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric',
                                  })
                                : 'N/A';

                              return (
                                <tr key={subcontractor.subcontractor_id} className="table-row connected">
                                  <td className="subcontractors-mobile-cell subcontractors-mobile-cell--left">
                                    <div className="subcontractors-mobile-left">
                                      <button
                                        type="button"
                                        onClick={() => handleViewSubcontractorPayments(subcontractor)}
                                        className="subcontractors-mobile-name"
                                      >
                                        {subcontractor.subcontractor_name || 'N/A'}
                                      </button>
                                      <div className="subcontractors-mobile-meta">
                                        <span className="subcontractors-mobile-rate">{statusColLabel}</span>
                                        {matchColLabel ? (
                                          <>
                                            <span className="subcontractors-mobile-dot">•</span>
                                            <span className="subcontractors-mobile-status">{matchColLabel}</span>
                                          </>
                                        ) : null}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="subcontractors-mobile-cell subcontractors-mobile-cell--right">
                                    <div className="subcontractors-mobile-right">
                                      <button
                                        type="button"
                                        onClick={() => handleVerifySubcontractor(subcontractor)}
                                        disabled={verifyingSubcontractors[subcontractor.subcontractor_id]}
                                        className={`subcontractors-mobile-verify-btn ${
                                          isNoMatch || isUnverified ? 'subcontractors-mobile-verify-btn--primary' : 'subcontractors-mobile-verify-btn--secondary'
                                        }`}
                                      >
                                        {verifyingSubcontractors[subcontractor.subcontractor_id]
                                          ? 'Verifying...'
                                          : isNoMatch || isUnverified
                                            ? (isNoMatch ? 'Re-verify' : 'Verify')
                                            : 'Re-verify'}
                                      </button>
                                      <div className="subcontractors-mobile-verified-date">
                                        {mobileVerifiedDate}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="subcontractors-desktop-cell">
                                    <button
                                      onClick={() => handleViewSubcontractorPayments(subcontractor)}
                                      style={{
                                        background: 'none',
                                        border: 'none',
                                        color: '#57bedd',
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        padding: 0,
                                        fontSize: 'inherit',
                                        fontFamily: 'inherit'
                                      }}
                                    >
                                      {subcontractor.subcontractor_name || 'N/A'}
                                    </button>
                                  </td>
                                  <td className="subcontractors-desktop-cell">
                                    <span className={statusBadgeClass}>{statusColLabel}</span>
                                  </td>
                                  <td className="subcontractors-desktop-cell">{matchColLabel}</td>
                                  <td className="subcontractors-desktop-cell">{registeredDateDisplay}</td>
                                  <td className="subcontractors-desktop-cell">
                                    {isUnverified || isNoMatch ? (
                                      <button
                                        onClick={() => handleVerifySubcontractor(subcontractor)}
                                        disabled={verifyingSubcontractors[subcontractor.subcontractor_id]}
                                        style={{
                                          padding: '6px 12px',
                                          backgroundColor: verifyingSubcontractors[subcontractor.subcontractor_id] ? '#ccc' : '#57bedd',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: verifyingSubcontractors[subcontractor.subcontractor_id] ? 'not-allowed' : 'pointer',
                                          fontSize: '13px',
                                          fontWeight: '500',
                                          transition: 'background-color 0.2s',
                                          opacity: verifyingSubcontractors[subcontractor.subcontractor_id] ? 0.7 : 1
                                        }}
                                        onMouseOver={(e) => {
                                          if (!verifyingSubcontractors[subcontractor.subcontractor_id]) {
                                            e.target.style.backgroundColor = '#57bedd';
                                          }
                                        }}
                                        onMouseOut={(e) => {
                                          if (!verifyingSubcontractors[subcontractor.subcontractor_id]) {
                                            e.target.style.backgroundColor = '#57bedd';
                                          }
                                        }}
                                      >
                                        {verifyingSubcontractors[subcontractor.subcontractor_id] ? 'Verifying...' : (isNoMatch ? 'Re-verify' : 'Verify')}
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleVerifySubcontractor(subcontractor)}
                                        disabled={verifyingSubcontractors[subcontractor.subcontractor_id]}
                                        style={{
                                          padding: '6px 12px',
                                          backgroundColor: verifyingSubcontractors[subcontractor.subcontractor_id] ? '#ccc' : '#6c757d',
                                          color: 'white',
                                          border: 'none',
                                          borderRadius: '4px',
                                          cursor: verifyingSubcontractors[subcontractor.subcontractor_id] ? 'not-allowed' : 'pointer',
                                          fontSize: '13px',
                                          fontWeight: '500',
                                          transition: 'background-color 0.2s',
                                          opacity: verifyingSubcontractors[subcontractor.subcontractor_id] ? 0.7 : 1
                                        }}
                                        onMouseOver={(e) => {
                                          if (!verifyingSubcontractors[subcontractor.subcontractor_id]) {
                                            e.target.style.backgroundColor = '#5a6268';
                                          }
                                        }}
                                        onMouseOut={(e) => {
                                          if (!verifyingSubcontractors[subcontractor.subcontractor_id]) {
                                            e.target.style.backgroundColor = '#6c757d';
                                          }
                                        }}
                                      >
                                        {verifyingSubcontractors[subcontractor.subcontractor_id] ? 'Verifying...' : 'Re-verify'}
                                      </button>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>You don't have any connected subcontractors yet.</p>
                        <p>Invite subcontractors using the form above.</p>
                      </div>
                    )}
                    {/* Pagination */}
                    {false && connectedSubcontractors.length > 0 && (
                      <div className="pagination-container">
                        <div className="pagination-info">
                          Showing {((subcontractorsPage - 1) * subcontractorsPerPage) + 1} to {Math.min(subcontractorsPage * subcontractorsPerPage, subcontractorsTotal)} of {subcontractorsTotal} results
                        </div>
                        <div className="pagination-controls">
                          <select
                            value={subcontractorsPerPage}
                            onChange={(e) => {
                              setSubcontractorsPerPage(Number(e.target.value));
                              setSubcontractorsPage(1);
                            }}
                            className="pagination-select"
                          >
                            <option value={10}>10 per page</option>
                            <option value={20}>20 per page</option>
                            <option value={50}>50 per page</option>
                          </select>
                          <button
                            onClick={() => setSubcontractorsPage(prev => Math.max(1, prev - 1))}
                            disabled={subcontractorsPage === 1}
                            className="pagination-button"
                          >
                            Previous
                          </button>
                          <span className="pagination-page-info">
                            Page {subcontractorsPage} of {subcontractorsTotalPages || 1}
                          </span>
                          <button
                            onClick={() => setSubcontractorsPage(prev => Math.min(subcontractorsTotalPages, prev + 1))}
                            disabled={subcontractorsPage >= subcontractorsTotalPages}
                            className="pagination-button"
                          >
                            Next
                          </button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                {activeTab === 'pending' && (
                  <>
                    {pendingLoading ? (
                      <div className="loading-state">Loading...</div>
                    ) : pendingRequests.length > 0 ? (
                      <div className="table-container">
                        <table className="relationships-table subcontractors-pending-requests-table">
                          <thead>
                            <tr>
                              <th className="subcontractors-pending-col-name">Name</th>
                              <th className="subcontractors-pending-col-email">Email</th>
                              <th className="subcontractors-pending-col-date">Requested Date</th>
                              <th className="subcontractors-pending-col-status">Status</th>
                              <th className="subcontractors-pending-col-actions">Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {pendingRequests.map((request) => {
                              const pendingStatusText =
                                request.type === 'invite'
                                  ? 'Awaiting registration'
                                  : request.initiated_by === 'employer'
                                    ? 'Awaiting subcontractor (terms)'
                                    : 'Pending your approval';
                              const addr = (request.subcontractor_address || '').trim();
                              return (
                              <tr key={`${request.type || 'request'}-${request.request_id}`} className="table-row pending">
                                <td className="subcontractors-pending-col-name">
                                  {request.subcontractor_name || (request.type === 'invite' ? '—' : 'N/A')}
                                </td>
                                <td className="subcontractors-pending-col-email">
                                  <div className="subcontractors-pending-email-line">{request.subcontractor_email}</div>
                                  <div className="subcontractors-pending-mobile-stack">
                                    <span className="status-badge status-pending">{pendingStatusText}</span>
                                    {request.type === 'connection_request' && (
                                      <div className="subcontractors-pending-mobile-address">
                                        {addr || '—'}
                                      </div>
                                    )}
                                  </div>
                                </td>
                                <td className="subcontractors-pending-col-date">
                                  {formatUkShortDate(request.requested_at) || 'N/A'}
                                </td>
                                <td className="subcontractors-pending-col-status">
                                  <span className={`status-badge status-pending`}>{pendingStatusText}</span>
                                </td>
                                <td className="subcontractors-pending-col-actions">
                                  <div className="table-actions subcontractors-pending-actions" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {request.type === 'connection_request' && (isMainEmployer() || hasPermission('add_subcontractors')) && (
                                      <>
                                        {request.initiated_by !== 'employer' && (
                                          <button
                                            type="button"
                                            className="subcontractors-pending-action-btn subcontractors-pending-action-btn--accept"
                                            onClick={() => handleAcceptRequest(request.request_id)}
                                            disabled={requestActionLoading[request.request_id]}
                                            title="Accept"
                                            style={{
                                              background: 'transparent',
                                              border: '1px solid rgba(40, 167, 69, 0.5)',
                                              color: '#28a745',
                                              padding: '6px 10px',
                                              cursor: requestActionLoading[request.request_id] ? 'not-allowed' : 'pointer',
                                              borderRadius: '4px',
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'center',
                                              minWidth: '32px',
                                              height: '32px',
                                              opacity: requestActionLoading[request.request_id] ? 0.6 : 1
                                            }}
                                          >
                                            {requestActionLoading[request.request_id] === 'accept' ? (
                                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <circle cx="12" cy="12" r="10"></circle>
                                                <polyline points="12 6 12 12 16 14"></polyline>
                                              </svg>
                                            ) : (
                                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                              </svg>
                                            )}
                                          </button>
                                        )}
                                        <button
                                          type="button"
                                          className="subcontractors-pending-action-btn subcontractors-pending-action-btn--reject"
                                          onClick={() => handleRejectRequest(request.request_id)}
                                          disabled={requestActionLoading[request.request_id]}
                                          title={request.initiated_by === 'employer' ? 'Cancel invitation' : 'Reject'}
                                          style={{
                                            background: 'transparent',
                                            border: '2px solid rgba(220, 53, 69, 0.9)',
                                            color: '#dc3545',
                                            padding: '6px 10px',
                                            cursor: requestActionLoading[request.request_id] ? 'not-allowed' : 'pointer',
                                            borderRadius: '4px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            minWidth: '32px',
                                            height: '32px',
                                            opacity: requestActionLoading[request.request_id] ? 0.6 : 1
                                          }}
                                        >
                                          {requestActionLoading[request.request_id] === 'reject' ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="12" cy="12" r="10"></circle>
                                              <polyline points="12 6 12 12 16 14"></polyline>
                                            </svg>
                                          ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <line x1="18" y1="6" x2="6" y2="18"></line>
                                              <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                          )}
                                        </button>
                                      </>
                                    )}
                                    {request.type === 'invite' && (isMainEmployer() || hasPermission('add_subcontractors')) && (
                                      <button
                                        type="button"
                                        className="subcontractors-pending-action-btn subcontractors-pending-btn-withdraw"
                                        onClick={() => handleRevokeEmailInvite(request.request_id)}
                                        disabled={requestActionLoading[request.request_id]}
                                        title="Withdraw invitation"
                                        style={{
                                          background: '#dc3545',
                                          border: '2px solid rgba(220, 53, 69, 0.9)',
                                          color: '#fff',
                                          padding: '6px 10px',
                                          cursor: requestActionLoading[request.request_id] ? 'not-allowed' : 'pointer',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          opacity: requestActionLoading[request.request_id] ? 0.6 : 1
                                        }}
                                      >
                                        <span className="subcontractors-pending-btn-label-desktop">
                                          {requestActionLoading[request.request_id] === 'revoke' ? 'Withdrawing…' : 'Withdraw invite'}
                                        </span>
                                        <span className="subcontractors-pending-btn-icon-mobile" aria-hidden="true">
                                          {requestActionLoading[request.request_id] === 'revoke' ? (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <circle cx="12" cy="12" r="10"></circle>
                                              <polyline points="12 6 12 12 16 14"></polyline>
                                            </svg>
                                          ) : (
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                              <line x1="18" y1="6" x2="6" y2="18"></line>
                                              <line x1="6" y1="6" x2="18" y2="18"></line>
                                            </svg>
                                          )}
                                        </span>
                                      </button>
                                    )}
                                    {request.type === 'invite' && !isMainEmployer() && !hasPermission('add_subcontractors') && (
                                      <span style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px' }}>Invite sent – awaiting sign-up</span>
                                    )}
                                    {request.type === 'connection_request' && !isMainEmployer() && !hasPermission('add_subcontractors') && (
                                      <span style={{ color: '#999', fontSize: '12px' }}>-</span>
                                    )}
                                  </div>
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="empty-state">
                        <p>No pending connection requests or invites</p>
                        <p>Connection requests from subcontractors and people you've invited will appear here until they accept and join.</p>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Invite Subcontractors Modal */}
              {isModalOpen && ReactDOM.createPortal(
                <div className="modal-overlay" onClick={() => setIsModalOpen(false)}>
                  <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                    <div className="modal-header">
                      <h2>Invite Subcontractors</h2>
                      <button className="modal-close" onClick={() => setIsModalOpen(false)}>×</button>
                    </div>
                    <div className="modal-body">
                      <p>Send an invitation to subcontractors to join PayCIS. They'll receive an email with a registration link.</p>
                      
                      <form onSubmit={handleInviteSubcontractor}>
                        <div className="invite-form">
                          <input
                            type="email"
                            value={inviteEmail}
                            onChange={(e) => setInviteEmail(e.target.value)}
                            placeholder="Enter subcontractor email address"
                            required
                            className="invite-input"
                          />
                          <button
                            type="submit"
                            disabled={inviteLoading}
                            className="action-button"
                          >
                            {inviteLoading ? 'Sending...' : 'Send Invite'}
                          </button>
                        </div>
                        
                        {inviteError && (
                          <div className="message error">
                            <strong>Error:</strong> {inviteError}
                          </div>
                        )}
                        
                        {inviteSuccess && (
                          <div className="message success">
                            <strong>Success:</strong> {inviteSuccess}
                          </div>
                        )}
                      </form>
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </>
          )}

          {activeMenu === 'payments' && hasPermission('view_payments') && (
            <div className="payments-section">
              {/* Payments Content */}
              <>
                  {/* Payment Tabs */}
                  <div className="tabs-container">
                    <div className="tabs-container__tabs">
                <button
                  type="button"
                  className={`tab-button ${paymentTab === 'pending' ? 'active' : ''}`}
                  onClick={() => setPaymentTab('pending')}
                >
                  Pending ({paymentTabCounts.pending})
                </button>
                <button
                  type="button"
                  className={`tab-button ${paymentTab === 'approved' ? 'active' : ''}`}
                  onClick={() => setPaymentTab('approved')}
                >
                  To be paid ({paymentTabCounts.approved})
                </button>
                <button
                  type="button"
                  className={`tab-button tab-button--payments-rejected ${paymentTab === 'rejected' ? 'active' : ''}`}
                  onClick={() => setPaymentTab('rejected')}
                >
                  Rejected ({paymentTabCounts.rejected})
                </button>
                <button
                  type="button"
                  className={`tab-button ${paymentTab === 'paid' ? 'active' : ''}`}
                  onClick={() => setPaymentTab('paid')}
                >
                  Paid ({paymentTabCounts.paid})
                </button>
                <button
                  type="button"
                  className={`tab-button ${paymentTab === 'monthly' ? 'active' : ''}`}
                  onClick={() => setPaymentTab('monthly')}
                >
                  Monthly statements
                </button>
                    </div>
              </div>

              {/* Success/Error Messages */}
              {inviteSuccess && (
                <div className="message success" style={{ marginTop: '20px', marginBottom: '20px' }}>
                  {inviteSuccess}
                </div>
              )}
              {inviteError && (
                <div className="message error" style={{ marginTop: '20px', marginBottom: '20px' }}>
                  {inviteError}
                </div>
              )}

              {/* Bulk Action Buttons */}
              {paymentTab !== 'paid' && paymentTab !== 'monthly' && hasPermission('approve_edit_payments') && (
                <div className='bulk-action-buttons-custom' style={{ marginBottom: '15px', display: 'flex', alignItems: 'center', gap: '15px', flexWrap: 'wrap !important', flexDirection: 'row !important' }}>
                  {/* Pending Tab: Approve and Reject */}
                  {paymentTab === 'pending' && (
                    <>
                      <button
                        onClick={handleBulkApprove}
                        disabled={bulkApproveLoading || selectedPayments.length === 0}
                        style={{
                          padding: '10px 20px',
                          background: selectedPayments.length > 0 ? '#28a745' : '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: selectedPayments.length > 0 ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '14px',
                          opacity: bulkApproveLoading ? 0.6 : 1
                        }}
                      >
                        {bulkApproveLoading ? 'Approving...' : `Approve Selected (${selectedPayments.length})`}
                      </button>
                      <button
                        onClick={handleBulkReject}
                        disabled={bulkRejectLoading || selectedPayments.length === 0}
                        style={{
                          padding: '10px 20px',
                          background: selectedPayments.length > 0 ? '#dc3545' : '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: selectedPayments.length > 0 ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '14px',
                          opacity: bulkRejectLoading ? 0.6 : 1
                        }}
                      >
                        {bulkRejectLoading ? 'Rejecting...' : `Reject Selected (${selectedPayments.length})`}
                      </button>
                    </>
                  )}
                  
                  {/* To be Paid Tab: Mark as Paid, Make Bulk Payment, and Reject */}
                  {paymentTab === 'approved' && (
                    <>
                      <button
                        onClick={handleBulkMarkAsPaid}
                        disabled={bulkMarkPaidLoading || selectedPayments.length === 0}
                        style={{
                          padding: '10px 20px',
                          background: selectedPayments.length > 0 ? '#28a745' : '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: selectedPayments.length > 0 ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '14px',
                          opacity: bulkMarkPaidLoading ? 0.6 : 1
                        }}
                      >
                        {bulkMarkPaidLoading ? 'Marking...' : `Mark Paid`}
                      </button>
                      <button
                        onClick={handleMakeBulkPayment}
                        disabled={selectedPayments.length === 0}
                        style={{
                          padding: '10px 20px',
                          background: selectedPayments.length > 0 ? '#007bff' : '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: selectedPayments.length > 0 ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '14px'
                        }}
                      >
                        Bulk Pay
                      </button> 
                      <button
                        onClick={handleBulkReject}
                        disabled={bulkRejectLoading || selectedPayments.length === 0}
                        style={{
                          padding: '10px 20px',
                          background: selectedPayments.length > 0 ? '#dc3545' : '#6c757d',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: selectedPayments.length > 0 ? 'pointer' : 'not-allowed',
                          fontWeight: '600',
                          fontSize: '14px',
                          opacity: bulkRejectLoading ? 0.6 : 1
                        }}
                      >
                        {bulkRejectLoading ? 'Rejecting...' : `Reject`}
                      </button>
                    </>
                  )}
                  
                  {/* Rejected Tab: Approve (to undo rejection) */}
                  {paymentTab === 'rejected' && (
                    <button
                      onClick={handleBulkApprove}
                      disabled={bulkApproveLoading || selectedPayments.length === 0}
                      style={{
                        padding: '10px 20px',
                        background: selectedPayments.length > 0 ? '#28a745' : '#6c757d',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: selectedPayments.length > 0 ? 'pointer' : 'not-allowed',
                        fontWeight: '600',
                        fontSize: '14px',
                        opacity: bulkApproveLoading ? 0.6 : 1
                      }}
                    >
                      {bulkApproveLoading ? 'Approving...' : `Approve Selected (${selectedPayments.length})`}
                    </button>
                  )}
                  
                  {selectedPayments.length > 0 && (
                    <button
                      onClick={() => setSelectedPayments([])}
                      style={{
                        padding: '10px 15px',
                        background: 'transparent',
                        color: '#fff',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Clear Selection
                    </button>
                  )}
                </div>
              )}

              {/* Monthly statements tab: year + subcontractor filter, table with download */}
              {paymentTab === 'monthly' && (
                <div style={{ marginTop: '16px' }}>
                  <div className="monthly-statements-filters">
                    <label className="monthly-statements-filters__field monthly-statements-filters__field--year">
                      <span>Year:</span>
                      <select
                        value={monthlyStatementsYear}
                        onChange={(e) => setMonthlyStatementsYear(Number(e.target.value))}
                        className="monthly-statements-filters__select"
                      >
                        {[new Date().getFullYear(), new Date().getFullYear() - 1, new Date().getFullYear() - 2, new Date().getFullYear() - 3].map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </label>
                    <label className="monthly-statements-filters__field monthly-statements-filters__field--sub">
                      <span>Subcontractor:</span>
                      <select
                        value={monthlyStatementsSubcontractorId || ''}
                        onChange={(e) => setMonthlyStatementsSubcontractorId(e.target.value || null)}
                        className="monthly-statements-filters__select"
                      >
                        <option value="">All</option>
                        {monthlyStatementsDropdownSubcontractors.map((sub) => (
                          <option key={sub.subcontractor_id} value={sub.subcontractor_id}>
                            {sub.subcontractor_name || sub.subcontractor_id || 'N/A'}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  {monthlyPaymentStatementsLoading ? (
                    <div className="loading-state">Loading monthly statements...</div>
                  ) : monthlyStatementsItems.length === 0 ? (
                    <p style={{ color: 'rgba(255,255,255,0.7)' }}>No paid payments for the selected year and filter.</p>
                  ) : (
                    <div className="table-container">
                      <table className="relationships-table monthly-statements-table">
                        <thead>
                          <tr>
                            <th>Period</th>
                            <th className="monthly-statements-col-payments">Payments</th>
                            <th>Total net</th>
                            <th>Download</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyStatementsItems.map((row) => {
                            const key = `${row.year}-${row.month}`;
                            const downloading = monthlyPaymentStatementDownloading === key;
                            return (
                              <tr key={key} className="table-row">
                                <td>{row.label}</td>
                                <td className="monthly-statements-col-payments">{row.payment_count}</td>
                                <td>£{typeof row.total_net === 'number' ? row.total_net.toFixed(2) : row.total_net}</td>
                                <td>
                                  <button
                                    type="button"
                                    className="view-button"
                                    disabled={downloading}
                                    onClick={() => handleDownloadMonthlyPaymentSlipsPDF(row.year, row.month)}
                                    title={downloading ? 'Downloading...' : 'Download PDF'}
                                    style={{
                                      background: 'transparent',
                                      border: '1px solid rgba(40, 167, 69, 0.5)',
                                      color: downloading ? '#6c757d' : '#28a745',
                                      padding: '6px 10px',
                                      cursor: downloading ? 'not-allowed' : 'pointer',
                                      borderRadius: '4px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      minWidth: '32px',
                                      height: '32px',
                                      opacity: downloading ? 0.7 : 1
                                    }}
                                  >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                      <polyline points="7 10 12 15 17 10"></polyline>
                                      <line x1="12" y1="15" x2="12" y2="3"></line>
                                    </svg>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Requests Table */}
              {paymentTab !== 'monthly' && (paymentsLoading ? (
                <div className="loading-state">Loading payment requests...</div>
              ) : filteredPayments.length > 0 ? (
                <div className="table-container">
                  <table className="relationships-table">
                    <thead>
                      <tr>
                        {paymentTab !== 'paid' && hasPermission('approve_edit_payments') && (
                          <th className="payments-col-select" style={{ width: '40px' }}>
                            <input
                              type="checkbox"
                              checked={
                                filteredPayments.length > 0 &&
                                selectedPayments.length > 0 &&
                                selectedPayments.length === filteredPayments.filter(p => {
                                  if (paymentTab === 'pending') return p.status === 'pending';
                                  if (paymentTab === 'approved') return p.status === 'approved';
                                  if (paymentTab === 'rejected') return p.status === 'rejected';
                                  return false;
                                }).length
                              }
                              onChange={handleSelectAllPayments}
                              style={{ cursor: 'pointer' }}
                            />
                          </th>
                        )}
                        <th>Name</th>
                        {paymentTab === 'approved' && (
                          <>
                            <th className="payments-col-bank">Sort Code</th>
                            <th className="payments-col-bank">Acct No</th>
                          </>
                        )}
                        {paymentTab === 'pending' ? (
                          <>
                            <th className="payments-col-date">Date</th>
                            <th className="payments-col-amount">Net Payable</th>
                            <th className="payments-col-actions">Actions</th>
                          </>
                        ) : (
                          <>
                            <th className="payments-col-amount">Net Payable</th>
                            <th className="payments-col-date">Date</th>
                            {paymentTab === 'approved' && <th className="payments-col-actions">Actions</th>}
                            {paymentTab === 'rejected' && <th>Status</th>}
                            {paymentTab === 'rejected' && <th className="payments-col-actions">Actions</th>}
                            {paymentTab === 'paid' && (
                              <th className="payments-col-paid-actions">Actions</th>
                            )}
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredPayments.map((payment) => (
                        <tr key={payment.payment_request_id} className={`table-row ${payment.status}`}>
                          {paymentTab !== 'paid' && hasPermission('approve_edit_payments') && (
                            <td className="payments-col-select">
                              <input
                                type="checkbox"
                                checked={selectedPayments.includes(payment.payment_request_id)}
                                onChange={() => handleSelectPayment(payment.payment_request_id)}
                                style={{ cursor: 'pointer' }}
                              />
                            </td>
                          )}
                          <td>
                            <div>
                              <button
                                onClick={() => {
                                  // Debug: log the payment data
                                  console.log('Payment data:', payment);
                                  const contactData = {
                                    name: payment.subcontractor_name || 'N/A',
                                    first_name: payment.subcontractor_first_name || null,
                                    surname: payment.subcontractor_surname || null,
                                    registered_name: payment.subcontractor_registered_name || null,
                                    email: payment.subcontractor_email || null,
                                    phone: payment.subcontractor_phone || null,
                                    id: payment.subcontractor_id || null,
                                    address: payment.subcontractor_address || null,
                                    building_name_no: payment.subcontractor_building_name_no || null,
                                    street: payment.subcontractor_street || null,
                                    city: payment.subcontractor_city || null,
                                    postal_code: payment.subcontractor_postal_code || null,
                                    vat_number: payment.subcontractor_vat_number || null,
                                    utr: payment.subcontractor_utr || null,
                                    ni_number: payment.subcontractor_ni_number || null,
                                    trade: payment.subcontractor_trade || null,
                                    company_name: payment.subcontractor_company_name || null,
                                    cis_rate: payment.subcontractor_cis_rate !== null && payment.subcontractor_cis_rate !== undefined ? payment.subcontractor_cis_rate : null,
                                    day_rate: payment.subcontractor_day_rate !== null && payment.subcontractor_day_rate !== undefined ? payment.subcontractor_day_rate : null
                                  };
                                  console.log('Contact data being set:', contactData);
                                  setSelectedSubcontractorContact(contactData);
                                  setIsContactSheetOpen(true);
                                }}
                                style={{
                                  background: 'none',
                                  border: 'none',
                                  color: '#57bedd',
                                  cursor: 'pointer',
                                  textDecoration: 'underline',
                                  padding: 0,
                                  fontSize: 'inherit',
                                  fontFamily: 'inherit',
                                  textAlign: 'left'
                                }}
                              >
                                {payment.subcontractor_name || 'N/A'}
                              </button>
                              <div className="payments-mobile-date">
                                {formatUkShortDate(
                                  paymentTab === 'paid' && payment.paid_at
                                    ? payment.paid_at
                                    : payment.submitted_at
                                ) || 'N/A'}
                              </div>
                            </div>
                          </td>
                          {paymentTab === 'approved' && (
                            <>
                              <td className="payments-col-bank">
                                {payment.subcontractor_sort_code || payment.subcontractor_bank_sort_code || 'N/A'}
                              </td>
                              <td className="payments-col-bank">
                                {payment.subcontractor_account_number || payment.subcontractor_bank_account_number || 'N/A'}
                              </td>
                            </>
                          )}
                          {paymentTab === 'pending' ? (
                            <>
                              <td className="payments-col-date">
                                {formatUkShortDate(payment.submitted_at) || 'N/A'}
                              </td>
                              <td className="payments-col-amount">
                                <button
                                  type="button"
                                  onClick={() => handleViewPaymentDetails(payment)}
                                  title="View payment details"
                                  className="payments-table-amount-link"
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    color: '#57bedd',
                                    cursor: 'pointer',
                                    textDecoration: 'underline',
                                    padding: 0,
                                    fontSize: 'inherit',
                                    fontFamily: 'inherit',
                                    fontWeight: '600'
                                  }}
                                >
                                  {formatPaymentNetPayable(payment)}
                                </button>
                              </td>
                              <td className="payments-col-actions">
                                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                  {hasPermission('approve_edit_payments') && (
                                    <button
                                      className="view-button"
                                      onClick={() => handleApprovePayment(payment)}
                                      title="Mark as Approved"
                                      style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(40, 167, 69, 0.5)',
                                        color: '#28a745',
                                        padding: '6px 10px',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '32px',
                                        height: '32px'
                                      }}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="20 6 9 17 4 12"></polyline>
                                      </svg>
                                    </button>
                                  )}
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="payments-col-amount">
                                {paymentTab === 'approved' || paymentTab === 'paid' ? (
                                  <button
                                    type="button"
                                    onClick={() => handleViewPaymentDetails(payment)}
                                    title="View payment details"
                                    className="payments-table-amount-link"
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      color: '#57bedd',
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      padding: 0,
                                      fontSize: 'inherit',
                                      fontFamily: 'inherit',
                                      fontWeight: '600'
                                    }}
                                  >
                                    {formatPaymentNetPayable(payment)}
                                  </button>
                                ) : (
                                  <>{formatPaymentNetPayable(payment)}</>
                                )}
                              </td>
                              <td className="payments-col-date">
                                {formatUkShortDate(
                                  paymentTab === 'paid' && payment.paid_at
                                    ? payment.paid_at
                                    : payment.submitted_at
                                ) || 'N/A'}
                              </td>
                              {paymentTab === 'approved' && (
                                <td className="payments-col-actions">
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {hasPermission('make_payments') && (
                                      <button
                                        className="view-button"
                                        onClick={() => handleMarkAsPaid(payment)}
                                        title="Mark as Paid"
                                        style={{
                                          background: 'transparent',
                                          border: '1px solid rgba(40, 167, 69, 0.5)',
                                          color: '#28a745',
                                          padding: '6px 10px',
                                          cursor: 'pointer',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          minWidth: '32px',
                                          height: '32px'
                                        }}
                                      >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                              {paymentTab === 'rejected' && (
                                <td>
                                  <span className={`status-badge status-${payment.status}`}>
                                    {payment.status === 'approved' ? 'To be paid' : payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                                  </span>
                                </td>
                              )}
                              {paymentTab === 'rejected' && (
                                <td>
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    <button
                                      className="view-button"
                                      onClick={() => handleViewPaymentDetails(payment)}
                                      title="View Details"
                                      style={{
                                        background: 'transparent',
                                        border: '1px solid rgba(91, 192, 222, 0.5)',
                                        color: '#57bedd',
                                        padding: '6px 10px',
                                        cursor: 'pointer',
                                        borderRadius: '4px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        minWidth: '32px',
                                        height: '32px'
                                      }}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                        <circle cx="12" cy="12" r="3"></circle>
                                      </svg>
                                    </button>
                                  </div>
                                </td>
                              )}
                              {paymentTab === 'paid' && (
                                <td className="payments-col-paid-actions">
                                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                    {hasPermission('make_payments') && (
                                      <button
                                        className="view-button"
                                        onClick={() => handleUnmarkAsPaid(payment)}
                                        title="Unmark Paid"
                                        style={{
                                          background: 'transparent',
                                          border: '1px solid rgba(255, 193, 7, 0.5)',
                                          color: '#ffc107',
                                          padding: '6px 10px',
                                          cursor: 'pointer',
                                          borderRadius: '4px',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          minWidth: '32px',
                                          height: '32px'
                                        }}
                                      >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                          <polyline points="20 6 9 17 4 12"></polyline>
                                        </svg>
                                      </button>
                                    )}
                                  </div>
                                </td>
                              )}
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <h2>No Payment Requests</h2>
                  <p>No payment requests found for the selected status.</p>
                </div>
              ))}
              {/* Pagination */}
              {false && paymentTab !== 'monthly' && filteredPayments.length > 0 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {((paymentsPage - 1) * paymentsPerPage) + 1} to {Math.min(paymentsPage * paymentsPerPage, paymentsTotal)} of {paymentsTotal} results
                  </div>
                  <div className="pagination-controls">
                    <select
                      value={paymentsPerPage}
                      onChange={(e) => {
                        setPaymentsPerPage(Number(e.target.value));
                        setPaymentsPage(1);
                      }}
                      className="pagination-select"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <button
                      onClick={() => setPaymentsPage(prev => Math.max(1, prev - 1))}
                      disabled={paymentsPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <span className="pagination-page-info">
                      Page {paymentsPage} of {paymentsTotalPages || 1}
                    </span>
                    <button
                      onClick={() => setPaymentsPage(prev => Math.min(paymentsTotalPages, prev + 1))}
                      disabled={paymentsPage >= paymentsTotalPages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
              </>
            </div>
          )}

          {/* CIS Returns Section - Only visible if user is main employer */}
          {activeMenu === 'cis-returns' && isMainEmployer() && (
            <div className="cis-returns-section">
                  {/* Return ready to submit notification */}
                  {(() => {
                    const readyMonths = availableCISMonths.filter(
                      m =>
                        m.has_payments === true &&
                        (m.submittable === true || isCisMonthSubmittable(m.year, m.month)) &&
                        !hmrcSubmittedMonthsSet.has(`${m.year}-${m.month}`)
                    );
                    if (readyMonths.length > 0) {
                      return (
                        <div style={{
                          background: 'rgba(76, 175, 80, 0.15)',
                          border: '1px solid rgba(76, 175, 80, 0.4)',
                          borderRadius: '8px',
                          padding: '14px 18px',
                          marginBottom: '20px',
                          color: '#81c784',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '10px'
                        }}>
                          <span style={{ fontSize: '20px' }}>✓</span>
                          <span>
                            {readyMonths.length === 1
                              ? 'A CIS return is ready to submit. Select the month below and click Submit.'
                              : `${readyMonths.length} CIS returns are ready to submit. Select a month below and click Submit.`}
                          </span>
                        </div>
                      );
                    }
                    return null;
                  })()}

                  {/* Success/Error Messages */}
                  {inviteSuccess && (
                    <div className="message success" style={{ marginTop: '20px', marginBottom: '20px' }}>
                      {inviteSuccess}
                    </div>
                  )}
                  {inviteError && (
                    <div className="message error" style={{ marginTop: '20px', marginBottom: '20px' }}>
                      {inviteError}
                    </div>
                  )}

                  {/* CIS Month Selector */}
                  <div className="form-field-group" style={{ marginBottom: '30px' }}>
                    <label className="form-label">
                      Select CIS Tax Month:
                    </label>
                    <select
                      value={selectedCISMonth && selectedCISYear ? `${selectedCISYear}-${selectedCISMonth}` : ''}
                      onChange={(e) => {
                        // Clear previous month's data when month changes
                        setCompanyMonthlySummary(null);
                        setMonthlyStatements({});
                        setInviteError('');
                        
                        if (e.target.value) {
                          const [year, month] = e.target.value.split('-').map(Number);
                          setSelectedCISYear(year);
                          setSelectedCISMonth(month);
                        } else {
                          setSelectedCISYear(null);
                          setSelectedCISMonth(null);
                        }
                      }}
                      className="form-input"
                      style={{
                        maxWidth: '400px'
                      }}
                    >
                      <option value="">Select a month...</option>
                      {availableCISMonths.map((month) => (
                        <option 
                          key={`${month.year}-${month.month}`} 
                          value={`${month.year}-${month.month}`}
                        >
                          {month.label}
                          {month.has_payments === false ? ' — no payments (nil return)' : ''}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Company Monthly Summary (includes nil CIS return when there are no payments) */}
                  {selectedCISYear && selectedCISMonth && (
                    <>
                      {summaryLoading ? (
                        <div className="loading-state">Loading monthly summary...</div>
                      ) : (
                        <div style={{ marginBottom: '40px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h3 style={{ color: 'white', fontSize: '20px', margin: 0 }}>
                              Company Summary -{' '}
                              {companyMonthlySummary?.cis_month_label ||
                                availableCISMonths.find(m => m.year === selectedCISYear && m.month === selectedCISMonth)?.label ||
                                `month ${selectedCISMonth} / ${selectedCISYear}`}
                            </h3>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                              {hmrcSubmissionStatus && (hmrcSubmissionStatus.success === true || hmrcSubmissionStatus.success) ? (
                                <div style={{ 
                                  background: 'rgba(76, 175, 80, 0.2)', 
                                  border: '1px solid rgba(76, 175, 80, 0.5)',
                                  borderRadius: '6px',
                                  padding: '8px 16px',
                                  color: '#4caf50',
                                  fontSize: '14px'
                                }}>
                                  ✓ Submitted on {hmrcSubmissionStatus.submitted_at ? new Date(hmrcSubmissionStatus.submitted_at).toLocaleDateString() : 'N/A'}
                                  {(hmrcSubmissionStatus.hmrc_submission_id && hmrcSubmissionStatus.hmrc_submission_id !== 'unknown') && (
                                    <div style={{ fontSize: '12px', marginTop: '4px', opacity: 0.8 }}>
                                      ID: {hmrcSubmissionStatus.hmrc_submission_id}
                                    </div>
                                  )}
                                </div>
                              ) : (() => {
                                const selectedMonthInfo = availableCISMonths.find(m => m.year === selectedCISYear && m.month === selectedCISMonth);
                                const isSubmittable = selectedMonthInfo?.submittable === true || isCisMonthSubmittable(selectedCISYear, selectedCISMonth);
                                const submittableFromRaw = selectedMonthInfo?.submittable_from ? new Date(selectedMonthInfo.submittable_from) : getCisMonthSubmittableFrom(selectedCISYear, selectedCISMonth);
                                const submittableFromFormatted = submittableFromRaw ? submittableFromRaw.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : null;
                                const isNilMonth = selectedMonthInfo?.has_payments === false;
                                return (
                                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
                                    {!isSubmittable && submittableFromFormatted && (
                                      <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.85)' }}>
                                        Return available from {submittableFromFormatted}
                                      </div>
                                    )}
                                    {isSubmittable && (
                                      <div style={{ fontSize: '13px', color: '#4caf50', fontWeight: 500 }}>
                                        {isNilMonth
                                          ? '✓ Nil return can be submitted (no payments in this period)'
                                          : '✓ Return ready to submit'}
                                      </div>
                                    )}
                                    <button
                                      onClick={handleSubmitToHMRC}
                                      disabled={hmrcSubmitting || summaryLoading || !isSubmittable}
                                      style={{
                                        background: (!isSubmittable || hmrcSubmitting) ? 'rgba(91, 192, 222, 0.5)' : '#57bedd',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        padding: '10px 20px',
                                        fontSize: '14px',
                                        fontWeight: '600',
                                        cursor: (hmrcSubmitting || summaryLoading || !isSubmittable) ? 'not-allowed' : 'pointer',
                                        opacity: (hmrcSubmitting || summaryLoading || !isSubmittable) ? 0.6 : 1
                                      }}
                                    >
                                      {hmrcSubmitting
                                        ? 'Submitting to HMRC...'
                                        : (isNilMonth ? 'Submit CIS Return to HMRC' : 'Submit CIS Return to HMRC')}
                                    </button>
                                  </div>
                                );
                              })()}
                            </div>
                          </div>

                          {companyMonthlySummary ? (
                          <>
                          <div style={{
                            background: 'rgba(255, 255, 255, 0.05)',
                            borderRadius: '8px',
                            padding: '20px',
                            marginBottom: '30px'
                          }}>
                            <h4 style={{ color: 'white', marginBottom: '15px', fontSize: '16px' }}>Overall Totals</h4>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                              <div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total Labour</div>
                                <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                                  £{companyMonthlySummary.total_labor.toFixed(2)}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total Materials</div>
                                <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                                  £{companyMonthlySummary.total_materials.toFixed(2)}
                                </div>
                              </div>
                              <div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total Gross</div>
                                <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                                  £{companyMonthlySummary.total_gross.toFixed(2)}
                                </div>
                              </div>
                              {companyMonthlySummary.total_vat && (
                                <div>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total VAT</div>
                                  <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                                    £{companyMonthlySummary.total_vat.toFixed(2)}
                                  </div>
                                </div>
                              )}
                              {companyMonthlySummary.total_vat_paid_to_subcontractor != null && (
                                <div>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total VAT Paid</div>
                                  <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                                    £{companyMonthlySummary.total_vat_paid_to_subcontractor.toFixed(2)}
                                  </div>
                                </div>
                              )}
                              {companyMonthlySummary.total_reverse_charge_vat != null && companyMonthlySummary.total_reverse_charge_vat > 0 && (
                                <div>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total Reverse Charge VAT</div>
                                  <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                                    £{companyMonthlySummary.total_reverse_charge_vat.toFixed(2)}
                                  </div>
                                </div>
                              )}
                              {companyMonthlySummary.total_cis_deducted && (
                                <div>
                                  <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total CIS Deducted</div>
                                  <div style={{ color: 'white', fontSize: '18px', fontWeight: '600' }}>
                                    £{companyMonthlySummary.total_cis_deducted.toFixed(2)}
                                  </div>
                                </div>
                              )}
                              <div>
                                <div style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '13px' }}>Total Net Paid</div>
                                <div style={{ color: '#57bedd', fontSize: '20px', fontWeight: '700' }}>
                                  £{companyMonthlySummary.total_net_paid.toFixed(2)}
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Monthly CIS Return - All Payments */}
                          {cisMonthPaymentsLoading ? (
                            <div className="loading-state">Loading payments...</div>
                          ) : cisMonthPayments.length > 0 ? (
                            <div>
                              <h4 style={{ color: 'white', marginBottom: '15px', fontSize: '16px' }}>Monthly CIS Return - Payment Details</h4>
                              <div className="table-container">
                                <table className="relationships-table">
                                  <thead>
                                    <tr>
                                      <th>Subcontractor</th>
                                      <th>Payment Date</th>
                                      <th>Job Code</th>
                                      <th>Description</th>
                                      <th>Labour</th>
                                      <th>Materials</th>
                                      <th>VAT Rate</th>
                                      <th>VAT</th>
                                      <th>Reverse Charge</th>
                                      <th>VAT Paid to Sub</th>
                                      <th>CIS Deduction</th>
                                      <th>Net Paid</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {cisMonthPayments.map((payment) => (
                                      <tr key={payment.payment_request_id} className="table-row">
                                        <td>
                                          <div>
                                            <div>
                                              {[payment.subcontractor_first_name, payment.subcontractor_surname].filter(Boolean).join(' ') || 'N/A'}
                                            </div>
                                            <div className="relationship-date" style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                                              {payment.subcontractor_name || ''}
                                            </div>
                                            <div className="relationship-date" style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                                              {payment.subcontractor_email || ''}
                                            </div>
                                          </div>
                                        </td>
                                        <td>
                                          {payment.approved_at 
                                            ? new Date(payment.approved_at).toLocaleDateString()
                                            : 'N/A'}
                                        </td>
                                        <td>{payment.job_code || '-'}</td>
                                        <td style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                          {payment.description || '-'}
                                        </td>
                                        <td>£{payment.labor_total ? parseFloat(payment.labor_total).toFixed(2) : '0.00'}</td>
                                        <td>£{payment.materials_total ? parseFloat(payment.materials_total).toFixed(2) : '0.00'}</td>
                                        <td>{payment.vat_rate != null ? `${payment.vat_rate}%` : '-'}</td>
                                        <td>
                                          {payment.vat_amount && parseFloat(payment.vat_amount) > 0
                                            ? `£${parseFloat(payment.vat_amount).toFixed(2)}`
                                            : '-'}
                                        </td>
                                        <td>{payment.reverse_charge_applies ? 'Yes' : payment.reverse_charge_applies === false ? 'No' : '-'}</td>
                                        <td>
                                          {payment.vat_paid_to_subcontractor != null
                                            ? `£${parseFloat(payment.vat_paid_to_subcontractor).toFixed(2)}`
                                            : payment.reverse_charge_applies
                                              ? '£0.00'
                                              : payment.vat_amount && parseFloat(payment.vat_amount) > 0
                                                ? `£${parseFloat(payment.vat_amount).toFixed(2)}`
                                                : '-'}
                                        </td>
                                        <td>
                                          {payment.cis_amount && parseFloat(payment.cis_amount) > 0
                                            ? `£${parseFloat(payment.cis_amount).toFixed(2)}`
                                            : '-'}
                                        </td>
                                        <td style={{ fontWeight: '600' }}>
                                          £{payment.total_payable ? parseFloat(payment.total_payable).toFixed(2) : '0.00'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          ) : (
                            <div className="empty-state" style={{ marginTop: '20px' }}>
                              <p>No payments found for the selected CIS month.</p>
                            </div>
                          )}
                          </>
                          ) : (
                            <div style={{
                              background: 'rgba(255, 255, 255, 0.06)',
                              border: '1px solid rgba(91, 192, 222, 0.35)',
                              borderRadius: '8px',
                              padding: '18px 20px',
                              marginBottom: '24px',
                              color: 'rgba(255,255,255,0.9)',
                              lineHeight: 1.5
                            }}>
                              <p style={{ margin: '0 0 8px 0', fontWeight: 600 }}>No approved CIS payments in this tax month</p>
                              <p style={{ margin: 0, fontSize: '14px', opacity: 0.9 }}>
                                HMRC still expects a return for the period when you operate CIS. You can submit a <strong>nil CIS300</strong> using the button above once the submission window is open (from the 6th after the period ends).
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  )}

                  {!selectedCISYear || !selectedCISMonth ? (
                    <div className="empty-state">
                      <p>Please select a CIS tax month to view monthly statements.</p>
                    </div>
                  ) : null}
            </div>
          )}

          {/* User Management Section - Only visible if user has view_users permission */}
          {false && activeMenu === 'users' && hasPermission('view_users') && (
            <div className="payments-section">
              {hasPermission('add_users') && (
                <>
                  <div className="section-header section-header--actions-only dashboard-tab-actions--mobile">
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => {
                        setUserFormData({
                          email: '',
                          full_name: '',
                          phone_number: '',
                          permissions: {
                            view_payments: false,
                            approve_edit_payments: false,
                            make_payments: false,
                            add_users: false,
                            view_users: false,
                            view_subcontractors: false,
                            add_subcontractors: false,
                          },
                        });
                        setInviteError('');
                        setInviteSuccess('');
                        setIsUserModalOpen(true);
                      }}
                    >
                      + Add User
                    </button>
                  </div>
                  <div className="tabs-container tabs-container--actions-only-desktop dashboard-tab-actions--desktop">
                    <button
                      type="button"
                      className="action-button"
                      onClick={() => {
                        setUserFormData({
                          email: '',
                          full_name: '',
                          phone_number: '',
                          permissions: {
                            view_payments: false,
                            approve_edit_payments: false,
                            make_payments: false,
                            add_users: false,
                            view_users: false,
                            view_subcontractors: false,
                            add_subcontractors: false,
                          },
                        });
                        setInviteError('');
                        setInviteSuccess('');
                        setIsUserModalOpen(true);
                      }}
                    >
                      + Add User
                    </button>
                  </div>
                </>
              )}
              {/* Success/Error Messages */}
              {inviteSuccess && (
                <div className="message success" style={{ marginTop: '20px', marginBottom: '20px' }}>
                  {inviteSuccess}
                </div>
              )}
              {inviteError && (
                <div className="message error" style={{ marginTop: '20px', marginBottom: '20px' }}>
                  {inviteError}
                </div>
              )}

              {/* Users Table */}
              {usersLoading ? (
                <div className="loading-state">Loading users...</div>
              ) : employerUsers.length > 0 ? (
                <div className="table-container">
                  <table className="relationships-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th>Status</th>
                        <th>Permissions</th>
                        <th>Last Login</th>
                        <th>Created</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {employerUsers.map((userItem) => (
                        <tr key={userItem.user_id} className="table-row">
                          <td>{userItem.full_name}</td>
                          <td>{userItem.email}</td>
                          <td>{userItem.phone_number || 'N/A'}</td>
                          <td>
                            <span className={`status-badge status-${userItem.status}`}>
                              {userItem.status === 'approved' ? 'To be paid' : userItem.status.charAt(0).toUpperCase() + userItem.status.slice(1)}
                            </span>
                          </td>
                          <td>
                            {userItem.permissions ? (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', fontSize: '12px' }}>
                                {userItem.permissions.view_payments && <span style={{ background: '#57bedd', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>View Payments</span>}
                                {userItem.permissions.approve_edit_payments && <span style={{ background: '#4db6ac', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>Approve/Edit</span>}
                                {userItem.permissions.make_payments && <span style={{ background: '#28a745', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>Make Payments</span>}
                                {userItem.permissions.add_users && <span style={{ background: '#ffc107', color: '#333', padding: '2px 6px', borderRadius: '3px' }}>Add Users</span>}
                                {userItem.permissions.view_users && <span style={{ background: '#17a2b8', color: 'white', padding: '2px 6px', borderRadius: '3px' }}>View Users</span>}
                                {(!userItem.permissions || Object.values(userItem.permissions || {}).every(v => !v)) && <span style={{ color: '#999' }}>No permissions</span>}
                              </div>
                            ) : (
                              <span style={{ color: '#999' }}>No permissions</span>
                            )}
                          </td>
                          <td>
                            {userItem.last_login_at
                              ? new Date(userItem.last_login_at).toLocaleDateString()
                              : 'Never'}
                          </td>
                          <td>
                            {userItem.created_at
                              ? new Date(userItem.created_at).toLocaleDateString()
                              : 'N/A'}
                          </td>
                          <td>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              {/* Edit button: main employers or sub-users with add_users permission, but not for own account */}
                              {((isMainEmployer() || hasPermission('add_users')) && userItem.user_id !== user?.user_id) && (
                                <button
                                  className="view-button"
                                  onClick={() => handleEditUser(userItem)}
                                  title="Edit User"
                                  style={{
                                    background: 'transparent',
                                    border: '1px solid rgba(91, 192, 222, 0.5)',
                                    color: '#57bedd',
                                    padding: '6px 10px',
                                    cursor: 'pointer',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '32px',
                                    height: '32px'
                                  }}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                                  </svg>
                                </button>
                              )}
                              {/* Delete button: only main employers can delete users, and not their own account */}
                              {isMainEmployer() && userItem.user_id !== user?.user_id && (
                                <button
                                  className="view-button"
                                  onClick={() => handleDeleteUser(userItem.user_id)}
                                  disabled={userLoading}
                                  title="Delete User"
                                  style={{
                                    background: 'rgba(220, 53, 69, 0.2)',
                                    color: '#dc3545',
                                    border: '1px solid rgba(220, 53, 69, 0.4)',
                                    padding: '6px 10px',
                                    cursor: userLoading ? 'not-allowed' : 'pointer',
                                    borderRadius: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minWidth: '32px',
                                    height: '32px',
                                    opacity: userLoading ? 0.6 : 1
                                  }}
                                >
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polyline points="3 6 5 6 21 6"></polyline>
                                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                                    <line x1="10" y1="11" x2="10" y2="17"></line>
                                    <line x1="14" y1="11" x2="14" y2="17"></line>
                                  </svg>
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <h2>No Users</h2>
                  <p>You haven't added any users yet. Click "Add User" to get started.</p>
                </div>
              )}

              {/* Pagination */}
              {false && employerUsers.length > 0 && (
                <div className="pagination-container">
                  <div className="pagination-info">
                    Showing {((usersPage - 1) * usersPerPage) + 1} to {Math.min(usersPage * usersPerPage, usersTotal)} of {usersTotal} results
                  </div>
                  <div className="pagination-controls">
                    <select
                      value={usersPerPage}
                      onChange={(e) => {
                        setUsersPerPage(Number(e.target.value));
                        setUsersPage(1);
                      }}
                      className="pagination-select"
                    >
                      <option value={10}>10 per page</option>
                      <option value={20}>20 per page</option>
                      <option value={50}>50 per page</option>
                    </select>
                    <button
                      onClick={() => setUsersPage(prev => Math.max(1, prev - 1))}
                      disabled={usersPage === 1}
                      className="pagination-button"
                    >
                      Previous
                    </button>
                    <span className="pagination-page-info">
                      Page {usersPage} of {usersTotalPages || 1}
                    </span>
                    <button
                      onClick={() => setUsersPage(prev => Math.min(usersTotalPages, prev + 1))}
                      disabled={usersPage >= usersTotalPages}
                      className="pagination-button"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {/* Payment Detail Modal */}
      {isPaymentDetailModalOpen && selectedPayment && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => {
          setIsPaymentDetailModalOpen(false);
          setSelectedPayment(null);
          setActionComment('');
          setShowConfirmDialog(false);
          setConfirmAction(null);
          setPaymentForAction(null);
          setPaymentToMarkPaid(null);
          setInviteError('');
        }}>
          <div className="modal-content payment-detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header payment-detail-modal__header">
              <h2>Payment Details</h2>
              {selectedPayment.status === 'pending' && (
                <button
                  type="button"
                  className="payment-detail-modal-edit"
                  onClick={() => handleEditPayment(selectedPayment)}
                  style={{
                    background: '#57bedd',
                    border: '1px solid #57bedd',
                    color: '#ffffff',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    borderRadius: '6px',
                    cursor: 'pointer',
                  }}
                >
                  Edit
                </button>
              )}
              <button
                type="button"
                className="modal-close payment-detail-modal__header-close"
                aria-label="Close"
                onClick={() => {
                  setIsPaymentDetailModalOpen(false);
                  setSelectedPayment(null);
                  setActionComment('');
                  setShowConfirmDialog(false);
                  setConfirmAction(null);
                  setPaymentForAction(null);
                  setPaymentToMarkPaid(null);
                  setInviteError('');
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="payment-detail-section">
                <h4>Subcontractor Information</h4>
                {selectedPayment.subcontractor_first_name && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">First name:</span>
                    <span className="payment-detail-value">{selectedPayment.subcontractor_first_name}</span>
                  </div>
                )}
                {selectedPayment.subcontractor_surname && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Surname:</span>
                    <span className="payment-detail-value">{selectedPayment.subcontractor_surname}</span>
                  </div>
                )}
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Organisation name:</span>
                  <span className="payment-detail-value">{selectedPayment.subcontractor_name || 'N/A'}</span>
                </div>
                {selectedPayment.subcontractor_registered_name &&
                  selectedPayment.subcontractor_registered_name !== selectedPayment.subcontractor_name && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Registered name:</span>
                    <span className="payment-detail-value">{selectedPayment.subcontractor_registered_name}</span>
                  </div>
                )}
                {selectedPayment.subcontractor_company_name &&
                  selectedPayment.subcontractor_company_name !== selectedPayment.subcontractor_name && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Company name:</span>
                    <span className="payment-detail-value">{selectedPayment.subcontractor_company_name}</span>
                  </div>
                )}
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Email:</span>
                  <span className="payment-detail-value">{selectedPayment.subcontractor_email || 'N/A'}</span>
                </div>
              </div>

              <div className="payment-detail-section">
                <h4>Contractor Information</h4>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">First name:</span>
                  <span className="payment-detail-value">{selectedPayment.employer_first_name || currentUser?.first_name || 'N/A'}</span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Surname:</span>
                  <span className="payment-detail-value">{selectedPayment.employer_surname || currentUser?.surname || 'N/A'}</span>
                </div>
              </div>

              {(selectedPayment.status === 'approved' || selectedPayment.status === 'paid') && (
                selectedPayment.subcontractor_account_holder_name || selectedPayment.subcontractor_bank_account_name ||
                selectedPayment.subcontractor_sort_code || selectedPayment.subcontractor_bank_sort_code ||
                selectedPayment.subcontractor_account_number || selectedPayment.subcontractor_bank_account_number
              ) && (
                <div className="payment-detail-section">
                  <h4>Bank Details (for manual payment)</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                    {(selectedPayment.subcontractor_account_holder_name || selectedPayment.subcontractor_bank_account_name) && (
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Account Holder Name:</span>
                        <span className="payment-detail-value">
                          {selectedPayment.subcontractor_account_holder_name || selectedPayment.subcontractor_bank_account_name}
                        </span>
                      </div>
                    )}
                    {(selectedPayment.subcontractor_sort_code || selectedPayment.subcontractor_bank_sort_code) && (
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Sort Code:</span>
                        <span className="payment-detail-value">
                          {selectedPayment.subcontractor_sort_code || selectedPayment.subcontractor_bank_sort_code}
                        </span>
                      </div>
                    )}
                    {(selectedPayment.subcontractor_account_number || selectedPayment.subcontractor_bank_account_number) && (
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">Account Number:</span>
                        <span className="payment-detail-value">
                          {selectedPayment.subcontractor_account_number || selectedPayment.subcontractor_bank_account_number}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="payment-detail-section">
                <h4>Payment Information</h4>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Payment Type:</span>
                  <span className="payment-detail-value">
                    {selectedPayment.payment_type === 'job_rate' ? 'Job Rate' : 'Pay By Day'}
                  </span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Status:</span>
                  <span className={`status-badge status-${selectedPayment.status}`}>
                    {selectedPayment.status === 'approved' ? 'To be paid' : selectedPayment.status.charAt(0).toUpperCase() + selectedPayment.status.slice(1)}
                  </span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Submitted Date:</span>
                  <span className="payment-detail-value">
                    {formatUkShortDate(selectedPayment.submitted_at) || 'N/A'}
                  </span>
                </div>
              </div>

              {selectedPayment.payment_type === 'job_rate' ? (
                <div className="payment-detail-section">
                  <h4>Job Rate Details</h4>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Work Start Date:</span>
                    <span className="payment-detail-value">
                      {formatUkShortDate(selectedPayment.work_start_date) || 'N/A'}
                    </span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Description:</span>
                    <span className="payment-detail-value">{selectedPayment.description || 'N/A'}</span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Job Code:</span>
                    <span className="payment-detail-value">{selectedPayment.job_code || 'N/A'}</span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Amount:</span>
                    <span className="payment-detail-value">
                      £{selectedPayment.amount ? parseFloat(selectedPayment.amount).toFixed(2) : '0.00'}
                    </span>
                  </div>
                  {/* Labor Total next to job rate details */}
                  {selectedPayment.labor_total !== undefined && selectedPayment.labor_total !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Labour Total:</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.labor_total).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              ) : (
                <div className="payment-detail-section">
                  <h4>Time Sheet Details</h4>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Week Commencing:</span>
                    <span className="payment-detail-value">
                      {formatUkShortDate(selectedPayment.week_commencing) || 'N/A'}
                    </span>
                  </div>
                  {selectedPayment.days_data && (
                    <div className="payment-timesheet">
                      <div className="payment-timesheet-header">
                        <div className="payment-timesheet-col">Day</div>
                        <div className="payment-timesheet-col">Description</div>
                        <div className="payment-timesheet-col">Job Code</div>
                        <div className="payment-timesheet-col">Amount</div>
                      </div>
                      {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => {
                        const dayData = selectedPayment.days_data[day];
                        if (!dayData || (!dayData.description && !dayData.jobCode && !dayData.amount)) return null;
                        return (
                          <div key={day} className="payment-timesheet-row">
                            <div className="payment-timesheet-col">
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </div>
                            <div className="payment-timesheet-col">{dayData.description || '-'}</div>
                            <div className="payment-timesheet-col">{dayData.jobCode || '-'}</div>
                            <div className="payment-timesheet-col">
                              {dayData.amount ? `£${parseFloat(dayData.amount).toFixed(2)}` : '-'}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  {/* Labor Total next to timesheet */}
                  {selectedPayment.labor_total !== undefined && selectedPayment.labor_total !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Labour Total:</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.labor_total).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedPayment.materials_data && selectedPayment.materials_data.length > 0 && (
                <div className="payment-detail-section">
                  <h4>Materials</h4>
                  <div className="payment-timesheet materials-table">
                    <div className="payment-timesheet-header">
                      <div className="payment-timesheet-col">Material Name</div>
                      <div className="payment-timesheet-col">Description</div>
                      <div className="payment-timesheet-col">Job Code</div>
                      <div className="payment-timesheet-col">Amount</div>
                    </div>
                    {selectedPayment.materials_data.map((material, index) => (
                      <div key={index} className="payment-timesheet-row">
                        <div className="payment-timesheet-col">{material.name || '-'}</div>
                        <div className="payment-timesheet-col">{material.description || '-'}</div>
                        <div className="payment-timesheet-col">{material.job_code || material.jobCode || '-'}</div>
                        <div className="payment-timesheet-col">
                          {material.amount ? `£${parseFloat(material.amount).toFixed(2)}` : '-'}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Materials Total next to materials table */}
                  {selectedPayment.materials_total !== undefined && selectedPayment.materials_total !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Materials Total:</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.materials_total).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Payment Calculation Breakdown */}
              {(selectedPayment.gross_amount || selectedPayment.total_payable) && (
                <div className="payment-detail-section">
                  <h4>Payment Calculation</h4>
                  {selectedPayment.gross_amount !== undefined && selectedPayment.gross_amount !== null && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Gross Amount (Labour + Materials):</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.gross_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedPayment.vat_applicable && selectedPayment.vat_amount !== undefined && selectedPayment.vat_amount !== null && selectedPayment.vat_amount > 0 && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">VAT ({selectedPayment.vat_rate || 20}%):</span>
                      <span className="payment-detail-value">
                        £{parseFloat(selectedPayment.vat_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedPayment.reverse_charge_applies && (
                    <>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">VAT Treatment:</span>
                        <span className="payment-detail-value">Domestic Reverse Charge</span>
                      </div>
                      <div className="payment-detail-item">
                        <span className="payment-detail-label">VAT Paid to Subcontractor:</span>
                        <span className="payment-detail-value">
                          £{parseFloat(selectedPayment.vat_paid_to_subcontractor || 0).toFixed(2)}
                        </span>
                      </div>
                    </>
                  )}
                  {selectedPayment.subcontractor_reverse_charge_suggestion != null && selectedPayment.status === 'pending' && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Subcontractor RC suggestion:</span>
                      <span className="payment-detail-value">
                        {selectedPayment.subcontractor_reverse_charge_suggestion ? 'Yes' : 'No'}
                      </span>
                    </div>
                  )}
                  {selectedPayment.cis_rate > 0 && selectedPayment.cis_amount !== undefined && selectedPayment.cis_amount !== null && selectedPayment.cis_amount > 0 && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">CIS Deduction ({selectedPayment.cis_rate}% on Labour):</span>
                      <span className="payment-detail-value">
                        -£{parseFloat(selectedPayment.cis_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {selectedPayment.total_payable !== undefined && selectedPayment.total_payable !== null && (
                    <div className="payment-detail-item" style={{ 
                      borderTop: '2px solid rgba(255, 255, 255, 0.2)', 
                      paddingTop: '15px',
                      marginTop: '10px'
                    }}>
                      <span className="payment-detail-label" style={{ fontWeight: '700', fontSize: '16px' }}>Net Payable:</span>
                      <span className="payment-detail-value" style={{ fontWeight: '700', fontSize: '16px' }}>
                        £{parseFloat(selectedPayment.total_payable).toFixed(2)}
                      </span>
                    </div>
                  )}
                  {/* Fallback to Grand Total if total_payable is not available */}
                  {(!selectedPayment.total_payable && selectedPayment.total_amount) && (
                    <div className="payment-detail-item" style={{ 
                      borderTop: '2px solid rgba(255, 255, 255, 0.2)', 
                      paddingTop: '15px',
                      marginTop: '10px'
                    }}>
                      <span className="payment-detail-label" style={{ fontWeight: '700', fontSize: '16px' }}>Grand Total:</span>
                      <span className="payment-detail-value" style={{ fontWeight: '700', fontSize: '16px' }}>
                        £{parseFloat(selectedPayment.total_amount).toFixed(2)}
                      </span>
                    </div>
                  )}
                </div>
              )}

              {selectedPayment.employer_comment && (
                <div className="payment-detail-section">
                  <h4>Your Comment</h4>
                  <p className="payment-comment">{selectedPayment.employer_comment}</p>
                </div>
              )}

              {inviteError && (
                <div className="message error" style={{ marginTop: '20px' }}>
                  {inviteError}
                </div>
              )}

              {inviteSuccess && (
                <div className="message success" style={{ marginTop: '20px' }}>
                  {inviteSuccess}
                </div>
              )}
            </div>
            {(selectedPayment.status === 'approved' || selectedPayment.status === 'paid') && (
              <div className="payment-detail-modal-footer payment-detail-modal-footer--primary-actions">
                {selectedPayment.status === 'approved' && (
                  <button
                    type="button"
                    className="payment-detail-modal-mark-paid"
                    onClick={() => handleMarkAsPaid(selectedPayment)}
                    style={{
                      background: '#28a745',
                      border: '1px solid #218838',
                      color: '#ffffff',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    Mark Paid
                  </button>
                )}
                {selectedPayment.status === 'paid' && (
                  <button
                    type="button"
                    className="payment-detail-modal-unmark-paid"
                    onClick={() => handleUnmarkAsPaid(selectedPayment)}
                    style={{
                      background: '#ffc107',
                      border: '1px solid #e0a800',
                      color: '#1a1f24',
                      padding: '10px 20px',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '6px',
                      cursor: 'pointer',
                    }}
                  >
                    Unmark Paid
                  </button>
                )}
              </div>
            )}
            {selectedPayment.status === 'pending' && hasPermission('approve_edit_payments') && (
              <div className="payment-detail-modal-footer">
                <div className="payment-detail-section payment-detail-modal-footer__action">
                  {/* <h4>Action</h4> */}
                  <div className="payment-action-comment">
                    {/* <label>Comment (Optional):</label> */}
                    <textarea
                      value={actionComment}
                      onChange={(e) => setActionComment(e.target.value)}
                      placeholder="Add a comment for the subcontractor..."
                      rows="2"
                      className="payment-comment-input"
                    />
                  </div>
                  <div className="payment-action-buttons">
                    <button
                      className="reject-button"
                      onClick={() => handleRejectPayment(selectedPayment)}
                      disabled={approveLoading || rejectLoading}
                    >
                      {rejectLoading ? 'Rejecting...' : 'Reject'}
                    </button>
                    <button
                      className="accept-button"
                      onClick={() => handleApprovePayment(selectedPayment)}
                      disabled={approveLoading || rejectLoading}
                    >
                      {approveLoading ? 'Approving...' : 'Approve'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>,
        document.body
      )}

      {/* Bulk Approve — reverse charge review */}
      {showBulkApproveModal && ReactDOM.createPortal(
        <div
          className="modal-overlay"
          onClick={() => {
            if (!bulkApproveLoading) {
              setShowBulkApproveModal(false);
              setBulkApproveDecisions({});
            }
          }}
        >
          <div
            className="modal-content"
            onClick={(e) => e.stopPropagation()}
            style={{ maxWidth: '720px', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
          >
            <div className="modal-header">
              <h2>Review &amp; Approve Payments</h2>
              <button
                type="button"
                className="modal-close"
                disabled={bulkApproveLoading}
                onClick={() => {
                  setShowBulkApproveModal(false);
                  setBulkApproveDecisions({});
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body" style={{ overflowY: 'auto', flex: 1 }}>
              <p style={{ margin: '0 0 16px', fontSize: '15px', color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.5 }}>
                Confirm Domestic Reverse Charge VAT for each eligible payment before approving{' '}
                <strong>{selectedPayments.length}</strong> payment{selectedPayments.length !== 1 ? 's' : ''}.
                Defaults use your organisation setting, relationship history, and subcontractor suggestions.
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {payments
                  .filter((p) => selectedPayments.includes(p.payment_request_id))
                  .map((payment) => {
                    const pid = payment.payment_request_id;
                    const decision = bulkApproveDecisions[pid] || {
                      reverseChargeApplies: !!payment.reverse_charge_default,
                      overrideReason: '',
                    };
                    const amount = getPaymentNetPayable(payment);
                    const defaultRc = !!payment.reverse_charge_default;
                    const showOverride =
                      payment.reverse_charge_eligible &&
                      decision.reverseChargeApplies !== defaultRc;

                    return (
                      <div
                        key={pid}
                        style={{
                          padding: '14px 16px',
                          background: 'rgba(255, 255, 255, 0.06)',
                          borderRadius: '8px',
                          border: '1px solid rgba(91, 192, 222, 0.35)',
                        }}
                      >
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                          <div>
                            <div style={{ fontWeight: 600, color: 'white' }}>
                              {getPaymentSubcontractorLabel(payment)}
                            </div>
                            {payment.subcontractor_name && (
                              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)', marginTop: '2px' }}>
                                {payment.subcontractor_name}
                              </div>
                            )}
                          </div>
                          <div style={{ textAlign: 'right', color: 'rgba(255, 255, 255, 0.85)', fontSize: '14px' }}>
                            {amount != null ? `£${amount.toFixed(2)}` : '—'}
                            {payment.vat_rate != null && (
                              <div style={{ fontSize: '12px', color: 'rgba(255, 255, 255, 0.6)' }}>
                                VAT {payment.vat_rate}%
                              </div>
                            )}
                          </div>
                        </div>

                        {payment.reverse_charge_eligible ? (
                          <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: 600, color: 'white' }}>
                              Does Domestic Reverse Charge VAT apply?
                            </p>
                            <label
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                color: 'rgba(255, 255, 255, 0.9)',
                                cursor: 'pointer',
                                fontSize: '14px',
                              }}
                            >
                              <input
                                type="checkbox"
                                checked={decision.reverseChargeApplies}
                                onChange={(e) =>
                                  updateBulkApproveDecision(pid, { reverseChargeApplies: e.target.checked })
                                }
                              />
                              Domestic Reverse Charge applies
                            </label>
                            {payment.subcontractor_reverse_charge_suggestion != null && (
                              <p style={{ margin: '8px 0 0', fontSize: '12px', color: 'rgba(255, 255, 255, 0.65)' }}>
                                Subcontractor suggestion:{' '}
                                {payment.subcontractor_reverse_charge_suggestion ? 'Yes' : 'No'}
                              </p>
                            )}
                            {showOverride && (
                              <div style={{ marginTop: '10px' }}>
                                <label
                                  style={{
                                    display: 'block',
                                    marginBottom: '6px',
                                    fontSize: '12px',
                                    color: 'rgba(255, 255, 255, 0.8)',
                                  }}
                                >
                                  Reason for override (optional)
                                </label>
                                <textarea
                                  value={decision.overrideReason}
                                  onChange={(e) =>
                                    updateBulkApproveDecision(pid, { overrideReason: e.target.value })
                                  }
                                  rows={2}
                                  placeholder="Why you are overriding the suggested default..."
                                  style={{
                                    width: '100%',
                                    padding: '8px',
                                    borderRadius: '6px',
                                    border: '1px solid rgba(255,255,255,0.2)',
                                    background: 'rgba(0,0,0,0.2)',
                                    color: 'white',
                                    fontSize: '13px',
                                  }}
                                />
                              </div>
                            )}
                          </div>
                        ) : (
                          <p style={{ margin: '10px 0 0', fontSize: '12px', color: 'rgba(255, 255, 255, 0.55)' }}>
                            Reverse charge not applicable for this payment.
                          </p>
                        )}
                      </div>
                    );
                  })}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'flex-end',
                padding: '16px 20px',
                borderTop: '1px solid rgba(255, 255, 255, 0.1)',
              }}
            >
              <button
                type="button"
                className="reject-button"
                disabled={bulkApproveLoading}
                onClick={() => {
                  setShowBulkApproveModal(false);
                  setBulkApproveDecisions({});
                }}
                style={{ minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="accept-button"
                disabled={bulkApproveLoading}
                onClick={confirmBulkApprove}
                style={{ minWidth: '140px' }}
              >
                {bulkApproveLoading
                  ? 'Approving...'
                  : `Approve ${selectedPayments.length} payment${selectedPayments.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => {
          setShowConfirmDialog(false);
          setConfirmAction(null);
          setPaymentForAction(null);
          setPaymentToMarkPaid(null);
          setInviteError('');
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Confirm Action</h2>
              <button className="modal-close" onClick={() => {
                setShowConfirmDialog(false);
                setConfirmAction(null);
                setPaymentForAction(null);
                setPaymentToMarkPaid(null);
                setInviteError('');
              }}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '20px', fontSize: '16px', color: 'rgba(255, 255, 255, 0.9)' }}>
                {confirmAction === 'approve' && 'Are you sure you want to approve this payment request?'}
                {confirmAction === 'reject' && 'Are you sure you want to reject this payment request?'}
                {confirmAction === 'mark-paid' && 'Are you sure you want to mark this payment as paid?'}
                {confirmAction === 'unmark-paid' && 'Are you sure you want to unmark this payment? It will return to \'To be paid\'.'}
              </p>
              {confirmAction === 'reject' && (
                <p style={{ marginBottom: '20px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  This action cannot be undone. The subcontractor will be notified of the rejection.
                </p>
              )}
              {confirmAction === 'mark-paid' && (
                <p style={{ marginBottom: '20px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  The subcontractor will be notified that the payment has been marked as paid.
                </p>
              )}
              {confirmAction === 'unmark-paid' && (
                <p style={{ marginBottom: '20px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  You can mark it as paid again later from the "To be paid" tab.
                </p>
              )}
              {confirmAction === 'approve' && paymentForAction?.reverse_charge_eligible && (
                <div style={{
                  marginBottom: '20px',
                  padding: '16px',
                  background: 'rgba(255, 255, 255, 0.06)',
                  borderRadius: '8px',
                  border: '1px solid rgba(91, 192, 222, 0.35)',
                }}>
                  <p style={{ margin: '0 0 12px 0', fontWeight: 600, color: 'white' }}>
                    Does Domestic Reverse Charge VAT apply to this payment?
                  </p>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', color: 'rgba(255, 255, 255, 0.9)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={reverseChargeApplies}
                      onChange={(e) => setReverseChargeApplies(e.target.checked)}
                    />
                    Domestic Reverse Charge applies
                  </label>
                  {paymentForAction.subcontractor_reverse_charge_suggestion != null && (
                    <p style={{ margin: '10px 0 0', fontSize: '13px', color: 'rgba(255, 255, 255, 0.65)' }}>
                      Subcontractor suggestion: {paymentForAction.subcontractor_reverse_charge_suggestion ? 'Yes' : 'No'}
                    </p>
                  )}
                  {reverseChargeApplies !== !!paymentForAction.reverse_charge_default && (
                    <div style={{ marginTop: '12px' }}>
                      <label style={{ display: 'block', marginBottom: '6px', fontSize: '13px', color: 'rgba(255, 255, 255, 0.8)' }}>
                        Reason for override (optional)
                      </label>
                      <textarea
                        value={reverseChargeOverrideReason}
                        onChange={(e) => setReverseChargeOverrideReason(e.target.value)}
                        rows={2}
                        placeholder="Why you are overriding the suggested default..."
                        style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(0,0,0,0.2)', color: 'white' }}
                      />
                    </div>
                  )}
                </div>
              )}
              {inviteError && (
                <div style={{ 
                  marginBottom: '20px', 
                  padding: '12px', 
                  background: 'rgba(244, 67, 54, 0.2)', 
                  border: '1px solid rgba(244, 67, 54, 0.5)', 
                  borderRadius: '6px',
                  color: '#f44336',
                  fontSize: '14px'
                }}>
                  {inviteError}
                </div>
              )}
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '20px' }}>
                <button
                  className="reject-button"
                  onClick={() => {
                    setShowConfirmDialog(false);
                    setConfirmAction(null);
                    setPaymentForAction(null);
                    setPaymentToMarkPaid(null);
                    setInviteError('');
                  }}
                  style={{ minWidth: '100px' }}
                >
                  Cancel
                </button>
                <button
                  className={confirmAction === 'approve' ? 'accept-button' : confirmAction === 'reject' ? 'reject-button' : 'accept-button'}
                  onClick={confirmActionHandler}
                  disabled={approveLoading || rejectLoading || markPaidLoading}
                  style={{ 
                    minWidth: '100px',
                    ...(confirmAction === 'mark-paid' ? { 
                      background: 'rgba(40, 167, 69, 0.2)',
                      border: '1px solid rgba(40, 167, 69, 0.5)',
                      color: '#28a745'
                    } : confirmAction === 'unmark-paid' ? {
                      background: 'rgba(255, 193, 7, 0.2)',
                      border: '1px solid rgba(255, 193, 7, 0.5)',
                      color: '#ffc107'
                    } : {})
                  }}
                >
                  {approveLoading && confirmAction === 'approve' && 'Approving...'}
                  {rejectLoading && confirmAction === 'reject' && 'Rejecting...'}
                  {markPaidLoading && confirmAction === 'mark-paid' && 'Marking as Paid...'}
                  {markPaidLoading && confirmAction === 'unmark-paid' && 'Unmarking...'}
                  {!approveLoading && !rejectLoading && !markPaidLoading && confirmAction === 'approve' && 'Confirm Approve'}
                  {!approveLoading && !rejectLoading && !markPaidLoading && confirmAction === 'reject' && 'Confirm Reject'}
                  {!approveLoading && !rejectLoading && !markPaidLoading && confirmAction === 'mark-paid' && 'Mark Paid'}
                  {!approveLoading && !rejectLoading && !markPaidLoading && confirmAction === 'unmark-paid' && 'Unmark Paid'}
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* HMRC Information Correct Declaration Modal */}
      {showHMRCDeclarationModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowHMRCDeclarationModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
            <div className="modal-header">
              <h2>Declaration</h2>
              <button className="modal-close" onClick={() => setShowHMRCDeclarationModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '24px', fontSize: '16px', color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.5 }}>
                Can you confirm that the information given on this return is correct and complete to the best of your knowledge and belief?
              </p>
              <p style={{ marginBottom: '24px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                By confirming, your CIS return for the selected period will be submitted to HMRC.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px' }}>
                <button
                  className="reject-button"
                  onClick={() => setShowHMRCDeclarationModal(false)}
                  style={{ minWidth: '100px' }}
                >
                  Cancel
                </button>
                <button
                  className="accept-button"
                  onClick={handleConfirmHMRCDeclaration}
                  style={{ minWidth: '140px' }}
                >
                  Confirm and Submit
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit Payment Request Modal */}
      {isEditPaymentModalOpen && selectedPayment && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => {
          setIsEditPaymentModalOpen(false);
          setSelectedPayment(null);
        }}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', maxHeight: '90vh', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2>Edit Payment Request</h2>
              <button className="modal-close" onClick={() => {
                setIsEditPaymentModalOpen(false);
                setSelectedPayment(null);
              }}>×</button>
            </div>
            <div className="modal-body">
              {selectedPayment.payment_type === 'job_rate' ? (
                <div className="job-rate-form-step">
                  <div className="job-rate-form-container">
                    <h3 className="job-rate-form-title">Edit Job Rate Payment</h3>
                    
                    <div className="job-rate-form-section">
                      <div className="form-field-group">
                        <label className="form-label">Invoice Date</label>
                        <input
                          type="date"
                          className="form-input"
                          value={editFormData.work_start_date}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, work_start_date: e.target.value }))}
                        />
                      </div>

                      <div className="form-field-group">
                        <label className="form-label">Description of works carried out</label>
                        <textarea
                          className="form-textarea"
                          rows="6"
                          value={editFormData.description}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Enter description of works..."
                        />
                      </div>

                      <div className="form-row">
                        <div className="form-field-group">
                          <label className="form-label">Job Code</label>
                          <input
                            type="text"
                            className="form-input"
                            value={editFormData.job_code}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, job_code: e.target.value }))}
                            placeholder="Enter job code"
                          />
                        </div>

                        <div className="form-field-group">
                          <label className="form-label">Amount</label>
                          <input
                            type="number"
                            className="form-input"
                            value={editFormData.amount}
                            onChange={(e) => setEditFormData(prev => ({ ...prev, amount: e.target.value }))}
                            placeholder="0.00"
                            step="0.01"
                            min="0"
                          />
                        </div>
                      </div>
                    </div>
                    
                    {/* Materials Section */}
                    <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <div style={{ marginBottom: '15px' }}>
                        <h4 style={{ color: 'white', marginBottom: '10px', fontSize: '16px' }}>Materials</h4>
                      </div>
                      <div className="materials-table">
                        <div className="materials-header">
                          <div className="materials-col materials-col-name">Material Name</div>
                          <div className="materials-col materials-col-description">Description</div>
                          <div className="materials-col materials-col-jobcode">Job Code</div>
                          <div className="materials-col materials-col-amount">Amount</div>
                          <div className="materials-col materials-col-action">Action</div>
                        </div>
                        
                        {editFormData.materials.map((material, index) => (
                          <div key={index} className="materials-row">
                            <input
                              type="text"
                              className="materials-input materials-col-name"
                              value={material.name}
                              onChange={(e) => {
                                const updated = [...editFormData.materials];
                                updated[index] = { ...updated[index], name: e.target.value };
                                setEditFormData(prev => ({ ...prev, materials: updated }));
                              }}
                              placeholder="Material name"
                            />
                            <input
                              type="text"
                              className="materials-input materials-col-description"
                              value={material.description}
                              onChange={(e) => {
                                const updated = [...editFormData.materials];
                                updated[index] = { ...updated[index], description: e.target.value };
                                setEditFormData(prev => ({ ...prev, materials: updated }));
                              }}
                              placeholder="Description"
                            />
                            <input
                              type="text"
                              className="materials-input materials-col-jobcode"
                              value={material.job_code}
                              onChange={(e) => {
                                const updated = [...editFormData.materials];
                                updated[index] = { ...updated[index], job_code: e.target.value };
                                setEditFormData(prev => ({ ...prev, materials: updated }));
                              }}
                              placeholder="Job code"
                            />
                            <div className="materials-amount-wrapper materials-col-amount">
                              <span className="materials-amount-prefix">£</span>
                              <input
                                type="number"
                                className="materials-input materials-amount-input"
                                value={material.amount}
                                onChange={(e) => {
                                  const updated = [...editFormData.materials];
                                  updated[index] = { ...updated[index], amount: e.target.value };
                                  setEditFormData(prev => ({ ...prev, materials: updated }));
                                }}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                              />
                            </div>
                            <div className="materials-col-action">
                              {editFormData.materials.length > 1 && (
                                <button
                                  type="button"
                                  className="materials-remove-button"
                                  onClick={() => {
                                    const updated = editFormData.materials.filter((_, i) => i !== index);
                                    setEditFormData(prev => ({ ...prev, materials: updated }));
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <button
                        type="button"
                        className="materials-add-button"
                        onClick={() => {
                          setEditFormData(prev => ({
                            ...prev,
                            materials: [...prev.materials, { name: '', job_code: '', description: '', amount: '' }]
                          }));
                        }}
                      >
                        + Add Another Material
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="by-day-form-step">
                  <div className="by-day-form-container">
                    <h3 className="by-day-form-title">Edit Pay By Day Payment</h3>
                    
                    <div className="by-day-form-section">
                      <div className="form-field-group">
                        <label className="form-label">For week commencing:</label>
                        <input
                          type="date"
                          className="form-input"
                          value={editFormData.week_commencing}
                          onChange={(e) => setEditFormData(prev => ({ ...prev, week_commencing: e.target.value }))}
                        />
                      </div>

                      <div className="timesheet-table">
                        <div className="timesheet-header">
                          <div className="timesheet-col"></div>
                          <div className="timesheet-col timesheet-col-description">Description of tasks</div>
                          <div className="timesheet-col timesheet-col-jobcode">Job Code</div>
                          <div className="timesheet-col timesheet-col-amount">Amount</div>
                        </div>
                        
                        {['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'].map((day) => (
                          <div key={day} className="timesheet-row">
                            <div className="timesheet-day-label">
                              {day.charAt(0).toUpperCase() + day.slice(1)}
                            </div>
                            <input
                              type="text"
                              className="timesheet-input timesheet-col-description"
                              value={editFormData.days[day].description}
                              onChange={(e) => {
                                setEditFormData(prev => ({
                                  ...prev,
                                  days: {
                                    ...prev.days,
                                    [day]: { ...prev.days[day], description: e.target.value }
                                  }
                                }));
                              }}
                              placeholder="Description"
                            />
                            <input
                              type="text"
                              className="timesheet-input timesheet-col-jobcode"
                              value={editFormData.days[day].jobCode}
                              onChange={(e) => {
                                setEditFormData(prev => ({
                                  ...prev,
                                  days: {
                                    ...prev.days,
                                    [day]: { ...prev.days[day], jobCode: e.target.value }
                                  }
                                }));
                              }}
                              placeholder="Job Code"
                            />
                            <div className="timesheet-amount-wrapper">
                              <span className="timesheet-amount-prefix">£</span>
                              <input
                                type="number"
                                className="timesheet-input timesheet-col-amount"
                                value={editFormData.days[day].amount}
                                onChange={(e) => {
                                  setEditFormData(prev => ({
                                    ...prev,
                                    days: {
                                      ...prev.days,
                                      [day]: { ...prev.days[day], amount: e.target.value }
                                    }
                                  }));
                                }}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                              />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Materials Section */}
                    <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                      <div style={{ marginBottom: '15px' }}>
                        <h4 style={{ color: 'white', marginBottom: '10px', fontSize: '16px' }}>Materials</h4>
                      </div>
                      <div className="materials-table">
                        <div className="materials-header">
                          <div className="materials-col materials-col-name">Material Name</div>
                          <div className="materials-col materials-col-description">Description</div>
                          <div className="materials-col materials-col-jobcode">Job Code</div>
                          <div className="materials-col materials-col-amount">Amount</div>
                          <div className="materials-col materials-col-action">Action</div>
                        </div>
                        
                        {editFormData.materials.map((material, index) => (
                          <div key={index} className="materials-row">
                            <input
                              type="text"
                              className="materials-input materials-col-name"
                              value={material.name}
                              onChange={(e) => {
                                const updated = [...editFormData.materials];
                                updated[index] = { ...updated[index], name: e.target.value };
                                setEditFormData(prev => ({ ...prev, materials: updated }));
                              }}
                              placeholder="Material name"
                            />
                            <input
                              type="text"
                              className="materials-input materials-col-description"
                              value={material.description}
                              onChange={(e) => {
                                const updated = [...editFormData.materials];
                                updated[index] = { ...updated[index], description: e.target.value };
                                setEditFormData(prev => ({ ...prev, materials: updated }));
                              }}
                              placeholder="Description"
                            />
                            <input
                              type="text"
                              className="materials-input materials-col-jobcode"
                              value={material.job_code}
                              onChange={(e) => {
                                const updated = [...editFormData.materials];
                                updated[index] = { ...updated[index], job_code: e.target.value };
                                setEditFormData(prev => ({ ...prev, materials: updated }));
                              }}
                              placeholder="Job code"
                            />
                            <div className="materials-amount-wrapper materials-col-amount">
                              <span className="materials-amount-prefix">£</span>
                              <input
                                type="number"
                                className="materials-input materials-amount-input"
                                value={material.amount}
                                onChange={(e) => {
                                  const updated = [...editFormData.materials];
                                  updated[index] = { ...updated[index], amount: e.target.value };
                                  setEditFormData(prev => ({ ...prev, materials: updated }));
                                }}
                                placeholder="0.00"
                                step="0.01"
                                min="0"
                              />
                            </div>
                            <div className="materials-col-action">
                              {editFormData.materials.length > 1 && (
                                <button
                                  type="button"
                                  className="materials-remove-button"
                                  onClick={() => {
                                    const updated = editFormData.materials.filter((_, i) => i !== index);
                                    setEditFormData(prev => ({ ...prev, materials: updated }));
                                  }}
                                >
                                  Remove
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                      
                      <button
                        type="button"
                        className="materials-add-button"
                        onClick={() => {
                          setEditFormData(prev => ({
                            ...prev,
                            materials: [...prev.materials, { name: '', job_code: '', description: '', amount: '' }]
                          }));
                        }}
                      >
                        + Add Another Material
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {inviteError && (
                <div className="message error" style={{ marginTop: '20px' }}>
                  {inviteError}
                </div>
              )}

              {inviteSuccess && (
                <div className="message success" style={{ marginTop: '20px' }}>
                  {inviteSuccess}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="back-button"
                onClick={() => {
                  setIsEditPaymentModalOpen(false);
                  setSelectedPayment(null);
                }}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="continue-button"
                onClick={handleSaveEdit}
                disabled={editLoading}
              >
                {editLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Create User Modal */}
      {isUserModalOpen && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setIsUserModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Add New User</h2>
              <button className="modal-close" onClick={() => setIsUserModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              {inviteError && (
                <div className="message error" style={{ marginBottom: '20px' }}>
                  {inviteError}
                </div>
              )}
              
              {inviteSuccess && (
                <div className="message success" style={{ marginBottom: '20px' }}>
                  {inviteSuccess}
                </div>
              )}

              <div className="form-field-group">
                <label className="form-label">Email *</label>
                <input
                  type="email"
                  className="form-input"
                  value={userFormData.email}
                  onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                />
                <p style={{ marginTop: '8px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  An invitation email will be sent to this address. The user will complete their profile and set their password via the link in the email.
                </p>
              </div>

              <div className="form-field-group">
                <label className="form-label" style={{ marginBottom: '15px' }}>User Permissions</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions.view_payments}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, view_payments: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>View Payments (View only)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions.approve_edit_payments}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, approve_edit_payments: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Approve/Edit Payments</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions.make_payments}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, make_payments: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Make Payments</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions.add_users}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, add_users: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Add Users</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions.view_users}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, view_users: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>View Users (View only)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions.view_subcontractors}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, view_subcontractors: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>View Subcontractors (View only)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions.add_subcontractors}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, add_subcontractors: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Add Subcontractors</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="back-button"
                onClick={() => setIsUserModalOpen(false)}
                disabled={userLoading}
              >
                Cancel
              </button>
              <button
                className="continue-button"
                onClick={handleCreateUser}
                disabled={userLoading}
              >
                {userLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Edit User Modal */}
      {isEditUserModalOpen && selectedUser && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setIsEditUserModalOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Edit User</h2>
              <button className="modal-close" onClick={() => setIsEditUserModalOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-field-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={userFormData.email}
                  disabled
                  style={{ opacity: 0.6, cursor: 'not-allowed' }}
                />
              </div>
              <div className="form-field-group">
                <label className="form-label">Full Name *</label>
                <input
                  type="text"
                  className="form-input"
                  value={userFormData.full_name}
                  onChange={(e) => setUserFormData({ ...userFormData, full_name: e.target.value })}
                  placeholder="John Doe"
                  required
                />
              </div>
              <div className="form-field-group">
                <label className="form-label">Phone Number</label>
                <input
                  type="tel"
                  className="form-input"
                  value={userFormData.phone_number}
                  onChange={(e) => setUserFormData({ ...userFormData, phone_number: e.target.value })}
                  placeholder="+44 123 456 7890"
                />
              </div>
              <div className="form-field-group">
                <label className="form-label">Status</label>
                <select
                  className="form-input"
                  value={selectedUser.status}
                  onChange={(e) => setSelectedUser({ ...selectedUser, status: e.target.value })}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div className="form-field-group">
                <label className="form-label" style={{ marginBottom: '15px' }}>User Permissions</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions?.view_payments || false}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, view_payments: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>View Payments (View only)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions?.approve_edit_payments || false}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, approve_edit_payments: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Approve/Edit Payments</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions?.make_payments || false}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, make_payments: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Make Payments</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions?.add_users || false}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, add_users: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Add Users</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions?.view_users || false}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, view_users: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>View Users (View only)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions?.view_subcontractors || false}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, view_subcontractors: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>View Subcontractors (View only)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={userFormData.permissions?.add_subcontractors || false}
                      onChange={(e) => setUserFormData({
                        ...userFormData,
                        permissions: { ...userFormData.permissions, add_subcontractors: e.target.checked }
                      })}
                      style={{ marginRight: '10px', width: '18px', height: '18px', cursor: 'pointer' }}
                    />
                    <span>Add Subcontractors</span>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="back-button"
                onClick={() => {
                  setIsEditUserModalOpen(false);
                  setSelectedUser(null);
                }}
                disabled={userLoading}
              >
                Cancel
              </button>
              <button
                className="continue-button"
                onClick={handleUpdateUser}
                disabled={userLoading}
              >
                {userLoading ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Monthly Statement Detail Modal */}
      {isMonthlyStatementModalOpen && currentMonthlyStatement && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => {
          setIsMonthlyStatementModalOpen(false);
          setCurrentMonthlyStatement(null);
          setSelectedSubcontractorForStatement(null);
        }}>
          <div className="modal-content payment-detail-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>Monthly CIS Statement - {currentMonthlyStatement.cis_month_label}</h2>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                <button
                  className="view-button"
                  onClick={() => handleDownloadMonthlyStatementPDF(currentMonthlyStatement.subcontractor_id)}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(244, 67, 54, 0.5)',
                    color: '#f44336',
                    padding: '8px 16px',
                    fontSize: '14px'
                  }}
                >
                  Download PDF
                </button>
                <button className="modal-close" onClick={() => {
                  setIsMonthlyStatementModalOpen(false);
                  setCurrentMonthlyStatement(null);
                  setSelectedSubcontractorForStatement(null);
                }}>×</button>
              </div>
            </div>
            <div className="modal-body">
              {/* Company and Subcontractor Info */}
              <div className="payment-detail-section">
                <h4>Company Details</h4>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Company Name:</span>
                  <span className="payment-detail-value">{currentMonthlyStatement.company_name}</span>
                </div>
                {currentMonthlyStatement.company_address && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Address:</span>
                    <span className="payment-detail-value">{currentMonthlyStatement.company_address}</span>
                  </div>
                )}
              </div>

              <div className="payment-detail-section">
                <h4>Subcontractor Details</h4>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">First name:</span>
                  <span className="payment-detail-value">{currentMonthlyStatement.subcontractor_first_name || 'N/A'}</span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Surname:</span>
                  <span className="payment-detail-value">{currentMonthlyStatement.subcontractor_surname || 'N/A'}</span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Organisation name:</span>
                  <span className="payment-detail-value">{currentMonthlyStatement.subcontractor_name || 'N/A'}</span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Email:</span>
                  <span className="payment-detail-value">{currentMonthlyStatement.subcontractor_email}</span>
                </div>
                {currentMonthlyStatement.subcontractor_utr && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">UTR:</span>
                    <span className="payment-detail-value">{currentMonthlyStatement.subcontractor_utr}</span>
                  </div>
                )}
              </div>

              <div className="payment-detail-section">
                <h4>Period</h4>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">CIS Tax Month:</span>
                  <span className="payment-detail-value">{currentMonthlyStatement.cis_month_range}</span>
                </div>
              </div>

              {/* Monthly Totals */}
              <div className="payment-detail-section">
                <h4>Monthly Totals</h4>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Total Labour:</span>
                  <span className="payment-detail-value">£{currentMonthlyStatement.total_labor.toFixed(2)}</span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Total Materials:</span>
                  <span className="payment-detail-value">£{currentMonthlyStatement.total_materials.toFixed(2)}</span>
                </div>
                <div className="payment-detail-item">
                  <span className="payment-detail-label">Total Gross:</span>
                  <span className="payment-detail-value">£{currentMonthlyStatement.total_gross.toFixed(2)}</span>
                </div>
                {currentMonthlyStatement.total_vat && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Total VAT:</span>
                    <span className="payment-detail-value">£{currentMonthlyStatement.total_vat.toFixed(2)}</span>
                  </div>
                )}
                {currentMonthlyStatement.total_cis_deducted && (
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Total CIS Deducted:</span>
                    <span className="payment-detail-value">£{currentMonthlyStatement.total_cis_deducted.toFixed(2)}</span>
                  </div>
                )}
                <div className="payment-detail-item" style={{ borderTop: '2px solid rgba(255, 255, 255, 0.2)', paddingTop: '15px', marginTop: '10px' }}>
                  <span className="payment-detail-label" style={{ fontWeight: '700', fontSize: '16px' }}>Total Net Paid:</span>
                  <span className="payment-detail-value" style={{ fontWeight: '700', fontSize: '18px', color: '#57bedd' }}>
                    £{currentMonthlyStatement.total_net_paid.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Payment Items */}
              {currentMonthlyStatement.payment_items && currentMonthlyStatement.payment_items.length > 0 && (
                <div className="payment-detail-section">
                  <h4>Payment Details ({currentMonthlyStatement.payment_items.length} payment{currentMonthlyStatement.payment_items.length !== 1 ? 's' : ''})</h4>
                  <div className="table-container" style={{ marginTop: '15px' }}>
                    <table className="relationships-table">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Job Code</th>
                          <th>Description</th>
                          <th>Labour</th>
                          <th>Materials</th>
                          <th>VAT</th>
                          <th>CIS</th>
                          <th>Net Payable</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentMonthlyStatement.payment_items.map((item, index) => (
                          <tr key={item.payment_request_id || index} className="table-row">
                            <td>
                              {item.approved_at 
                                ? new Date(item.approved_at).toLocaleDateString()
                                : 'N/A'}
                            </td>
                            <td>{item.job_code || '-'}</td>
                            <td>{item.description || '-'}</td>
                            <td>£{item.labor_amount.toFixed(2)}</td>
                            <td>£{item.materials_amount.toFixed(2)}</td>
                            <td>{item.vat_amount ? `£${item.vat_amount.toFixed(2)}` : '-'}</td>
                            <td>{item.cis_amount ? `£${item.cis_amount.toFixed(2)}` : '-'}</td>
                            <td>£{item.net_payable.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* HMRC Gateway Credentials Status - Removed: Now system-wide (stored in ENV) */}
              <div className="payment-detail-section" style={{ marginTop: '30px', borderTop: '2px solid rgba(255, 255, 255, 0.2)', paddingTop: '20px' }}>
                <h4>HMRC Gateway Credentials</h4>
                <p style={{ fontSize: '13px', opacity: 0.8, marginBottom: '15px' }}>
                  Gateway credentials are now system-wide (stored in ENV variables), not per-user.
                  Please ensure HMRC_GATEWAY_ID and HMRC_GATEWAY_PASSWORD are configured in your environment.
                </p>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Subcontractor Payments Modal */}
      {isSubcontractorPaymentsModalOpen && selectedSubcontractorForPayments && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => {
          setIsSubcontractorPaymentsModalOpen(false);
          setSelectedSubcontractorForPayments(null);
          setSubcontractorPayments([]);
          setSubcontractorDetails(null);
        }}>
          <div className="modal-content payment-detail-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
            <div className="modal-header">
              <h2>
                {[selectedSubcontractorForPayments.subcontractor_first_name, selectedSubcontractorForPayments.subcontractor_surname]
                  .filter(Boolean)
                  .join(' ') || selectedSubcontractorForPayments.subcontractor_name || 'N/A'}
              </h2>
              <button className="modal-close" onClick={() => {
                setIsSubcontractorPaymentsModalOpen(false);
                setSelectedSubcontractorForPayments(null);
                setSubcontractorPayments([]);
                setSubcontractorDetails(null);
              }}>×</button>
            </div>
            <div className="modal-body">
              {/* Subcontractor Details Section */}
              <div className="payment-detail-section" style={{ marginBottom: '30px' }}>
                <h4>Subcontractor Information</h4>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">First name:</span>
                    <span className="payment-detail-value">
                      {selectedSubcontractorForPayments.subcontractor_first_name || 'N/A'}
                    </span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Surname:</span>
                    <span className="payment-detail-value">
                      {selectedSubcontractorForPayments.subcontractor_surname || 'N/A'}
                    </span>
                  </div>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Organisation name:</span>
                    <span className="payment-detail-value">
                      {selectedSubcontractorForPayments.subcontractor_name || 'N/A'}
                    </span>
                  </div>
                  {(selectedSubcontractorForPayments.subcontractor_registered_name) &&
                    selectedSubcontractorForPayments.subcontractor_registered_name !== selectedSubcontractorForPayments.subcontractor_name && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Registered name:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_registered_name}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_company_name) &&
                    selectedSubcontractorForPayments.subcontractor_company_name !== selectedSubcontractorForPayments.subcontractor_name && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Company name:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_company_name}</span>
                    </div>
                  )}
                  {selectedSubcontractorForPayments.subcontractor_email && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Email:</span>
                      <span className="payment-detail-value">
                        {selectedSubcontractorForPayments.subcontractor_email}
                      </span>
                    </div>
                  )}
                  {selectedSubcontractorForPayments.subcontractor_phone && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Phone:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_phone}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_business_type) && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Business Type:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_business_type || 'N/A'}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_trade_type) && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Trade Type:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_trade_type || 'N/A'}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_city) && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">City:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_city}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_postal_code) && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Postcode:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_postal_code}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_building_name_no || selectedSubcontractorForPayments.subcontractor_street) && (
                    <div className="payment-detail-item" style={{ gridColumn: '1 / -1' }}>
                      <span className="payment-detail-label">Address:</span>
                      <span className="payment-detail-value">
                        {[
                          selectedSubcontractorForPayments.subcontractor_building_name_no,
                          selectedSubcontractorForPayments.subcontractor_street
                        ].filter(Boolean).join(', ')}
                      </span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_ni_number) && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">NI Number:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_ni_number || 'N/A'}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_utr || selectedSubcontractorForPayments.subcontractor_utr_alt) && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">UTR:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_utr || selectedSubcontractorForPayments.subcontractor_utr_alt || 'N/A'}</span>
                    </div>
                  )}
                  {(selectedSubcontractorForPayments.subcontractor_vat_number) && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">VAT Number:</span>
                      <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_vat_number || 'N/A'}</span>
                    </div>
                  )}
                </div>
                {/* Banking Details Section */}
                {(selectedSubcontractorForPayments.subcontractor_account_holder_name || selectedSubcontractorForPayments.subcontractor_sort_code || selectedSubcontractorForPayments.subcontractor_account_number) && (
                  <div style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                    <h4 style={{ marginBottom: '15px' }}>Banking Details</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
                      {(selectedSubcontractorForPayments.subcontractor_account_holder_name) && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Account Holder Name:</span>
                          <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_account_holder_name}</span>
                        </div>
                      )}
                      {(selectedSubcontractorForPayments.subcontractor_sort_code) && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Sort Code:</span>
                          <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_sort_code}</span>
                        </div>
                      )}
                      {(selectedSubcontractorForPayments.subcontractor_account_number) && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Account Number:</span>
                          <span className="payment-detail-value">{selectedSubcontractorForPayments.subcontractor_account_number}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {selectedSubcontractorForPayments.subcontractor_cis_rate != null && (
                <div className="payment-detail-section" style={{ marginBottom: '20px', borderTop: '1px solid rgba(255, 255, 255, 0.1)', paddingTop: '20px' }}>
                  <div className="payment-detail-item">
                    <span className="payment-detail-label">Verified Tax Status (CIS Rate):</span>
                    <span className="payment-detail-value">
                      {selectedSubcontractorForPayments.subcontractor_cis_rate}%
                      {selectedSubcontractorForPayments.subcontractor_cis_rate === 20 ? ' (Standard Rate)' :
                       selectedSubcontractorForPayments.subcontractor_cis_rate === 30 ? ' (Higher Rate)' :
                       ' (Custom Rate)'}
                    </span>
                  </div>
                </div>
              )}

              {subcontractorPaymentsLoading ? (
                <div className="loading-state">Loading payments...</div>
              ) : subcontractorPayments.length > 0 ? (
                <div className="table-container">
                  <table className="relationships-table">
                    <thead>
                      <tr>
                        <th>Payment Type</th>
                        <th>Net Payable</th>
                        <th>Submitted Date</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subcontractorPayments.map((payment) => (
                        <tr key={payment.payment_request_id} className={`table-row ${payment.status}`}>
                          <td>{payment.payment_type === 'job_rate' ? 'Job Rate' : 'Pay By Day'}</td>
                          <td>{formatPaymentNetPayable(payment)}</td>
                          <td>
                            {payment.submitted_at 
                              ? new Date(payment.submitted_at).toLocaleDateString()
                              : 'N/A'}
                          </td>
                          <td>
                            <span className={`status-badge status-${payment.status}`}>
                              {payment.status === 'approved' ? 'To be paid' : payment.status.charAt(0).toUpperCase() + payment.status.slice(1)}
                            </span>
                          </td>
                          <td>
                            <button
                              className="view-button"
                              onClick={() => {
                                handleViewPaymentDetails(payment);
                                setIsSubcontractorPaymentsModalOpen(false);
                              }}
                              title="View Details"
                              style={{
                                background: 'transparent',
                                border: '1px solid rgba(91, 192, 222, 0.5)',
                                color: '#57bedd',
                                padding: '6px 10px',
                                cursor: 'pointer',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minWidth: '32px',
                                height: '32px'
                              }}
                            >
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
                                <circle cx="12" cy="12" r="3"></circle>
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="empty-state">
                  <p>No payments found for this subcontractor.</p>
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Contact Sheet Modal */}
      {isContactSheetOpen && selectedSubcontractorContact && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setIsContactSheetOpen(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Contact Information</h2>
              <button className="modal-close" onClick={() => setIsContactSheetOpen(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="payment-detail-section">
                <h4 style={{ marginTop: 0, marginBottom: '12px' }}>{selectedSubcontractorContact.name}</h4>
                {selectedSubcontractorContact.first_name && (
                  <div className="payment-detail-item" style={{ marginBottom: '8px' }}>
                    <span className="payment-detail-label">First name:</span>
                    <span className="payment-detail-value">{selectedSubcontractorContact.first_name}</span>
                  </div>
                )}
                {selectedSubcontractorContact.surname && (
                  <div className="payment-detail-item" style={{ marginBottom: '16px' }}>
                    <span className="payment-detail-label">Surname:</span>
                    <span className="payment-detail-value">{selectedSubcontractorContact.surname}</span>
                  </div>
                )}
                {selectedSubcontractorContact.registered_name &&
                  selectedSubcontractorContact.registered_name !== selectedSubcontractorContact.name && (
                  <div className="payment-detail-item" style={{ marginBottom: '16px' }}>
                    <span className="payment-detail-label">Registered name:</span>
                    <span className="payment-detail-value">{selectedSubcontractorContact.registered_name}</span>
                  </div>
                )}
                
                {/* Contact Information */}
                <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#57bedd', fontSize: '14px', fontWeight: '600' }}>Contact Information</h5>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
                  {selectedSubcontractorContact.email && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Email:</span>
                      <span className="payment-detail-value">
                        <a href={`mailto:${selectedSubcontractorContact.email}`} style={{ color: '#57bedd', textDecoration: 'none' }}>
                          {selectedSubcontractorContact.email}
                        </a>
                      </span>
                    </div>
                  )}
                  {selectedSubcontractorContact.phone && (
                    <div className="payment-detail-item">
                      <span className="payment-detail-label">Phone:</span>
                      <span className="payment-detail-value">
                        <a href={`tel:${selectedSubcontractorContact.phone}`} style={{ color: '#57bedd', textDecoration: 'none' }}>
                          {selectedSubcontractorContact.phone}
                        </a>
                      </span>
                    </div>
                  )}
                </div>

                {/* Address Information */}
                {(selectedSubcontractorContact.address || selectedSubcontractorContact.building_name_no || selectedSubcontractorContact.street || selectedSubcontractorContact.city || selectedSubcontractorContact.postal_code) && (
                  <>
                    <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#57bedd', fontSize: '14px', fontWeight: '600' }}>Address</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
                      {selectedSubcontractorContact.building_name_no && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Building Name/No:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.building_name_no}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.street && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Street:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.street}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.city && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">City:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.city}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.postal_code && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Postal Code:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.postal_code}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.address && !selectedSubcontractorContact.building_name_no && !selectedSubcontractorContact.street && (
                        <div className="payment-detail-item" style={{ gridColumn: '1 / -1' }}>
                          <span className="payment-detail-label">Address:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.address}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Tax Information */}
                {(selectedSubcontractorContact.vat_number || selectedSubcontractorContact.utr || selectedSubcontractorContact.ni_number || selectedSubcontractorContact.cis_rate) && (
                  <>
                    <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#57bedd', fontSize: '14px', fontWeight: '600' }}>Tax Information</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
                      {selectedSubcontractorContact.vat_number && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">VAT Number:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.vat_number}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.utr && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">UTR:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.utr}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.ni_number && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">NI Number:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.ni_number}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.cis_rate !== null && selectedSubcontractorContact.cis_rate !== undefined && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">CIS Rate:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.cis_rate}%</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {/* Professional Information */}
                {(selectedSubcontractorContact.trade || (selectedSubcontractorContact.company_name && selectedSubcontractorContact.company_name !== selectedSubcontractorContact.name) || selectedSubcontractorContact.day_rate) && (
                  <>
                    <h5 style={{ marginTop: '20px', marginBottom: '10px', color: '#57bedd', fontSize: '14px', fontWeight: '600' }}>Professional Information</h5>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px', marginBottom: '20px' }}>
                      {selectedSubcontractorContact.company_name && selectedSubcontractorContact.company_name !== selectedSubcontractorContact.name && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Company name:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.company_name}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.trade && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Trade:</span>
                          <span className="payment-detail-value">{selectedSubcontractorContact.trade}</span>
                        </div>
                      )}
                      {selectedSubcontractorContact.day_rate && (
                        <div className="payment-detail-item">
                          <span className="payment-detail-label">Day Rate:</span>
                          <span className="payment-detail-value">£{parseFloat(selectedSubcontractorContact.day_rate).toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                  </>
                )}

                {!selectedSubcontractorContact.email && !selectedSubcontractorContact.phone && !selectedSubcontractorContact.address && !selectedSubcontractorContact.vat_number && !selectedSubcontractorContact.utr && (
                  <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.6)' }}>
                    <p>No information available for this subcontractor.</p>
                  </div>
                )}
              </div>
              <div className="modal-actions" style={{ marginTop: '20px', justifyContent: 'flex-end' }}>
                <button
                  onClick={() => setIsContactSheetOpen(false)}
                  className="cancel-button"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Payment List Modal */}
      {showPaymentListModal && ReactDOM.createPortal(
        <div className="modal-overlay" onClick={() => setShowPaymentListModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '90vw', maxHeight: '90vh', overflow: 'auto' }}>
            <div className="modal-header">
              <h2 style={{ color: '#000' }}>Bulk Payment List</h2>
              <button className="modal-close" onClick={() => setShowPaymentListModal(false)}>×</button>
            </div>
            <div className="modal-body payment-list-modal-body" style={{ color: '#000' }}>
              <div style={{ marginBottom: '20px', display: 'flex', gap: '10px' }}>
                <button
                  onClick={() => downloadPaymentList('json')}
                  style={{
                    padding: '8px 16px',
                    background: '#007bff',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Download JSON
                </button>
                <button
                  onClick={() => downloadPaymentList('csv')}
                  style={{
                    padding: '8px 16px',
                    background: '#28a745',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Download CSV
                </button>
                <button
                  onClick={() => downloadPaymentList('starling_csv')}
                  style={{
                    padding: '8px 16px',
                    background: '#17a2b8',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  Download Starling CSV
                </button>
              </div>
              
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', color: '#000' }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Payment ID</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Payee First Name</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Payee Surname</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Payee Organisation</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Account Number</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Sort Code</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>Net Payable</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Currency</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Reference Number</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Payment Method</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', color: '#000' }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentListData.map((payment, index) => (
                      <tr key={index} style={{ background: index % 2 === 0 ? '#fff' : '#f9f9f9' }}>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{String(payment.payment_id).substring(0, 8)}...</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.payee_first_name || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.payee_surname || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.payee_organisation_name || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.account_number || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.sort_code || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'right', color: '#000' }}>£{payment.amount}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.currency}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.reference_number || 'N/A'}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.payment_method}</td>
                        <td style={{ padding: '8px', border: '1px solid #ddd', color: '#000' }}>{payment.status}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div style={{ marginTop: '20px', padding: '15px', background: 'rgb(170 170 170)', borderRadius: '4px', color: '#000' }}>
                <strong>Payment Run ID:</strong> {paymentListData[0]?.payment_run_id || 'N/A'}<br />
                <strong>Total Payments:</strong> {paymentListData.length}<br />
                <strong>Total Net Payable:</strong> £{paymentListData.reduce((sum, p) => sum + parseFloat(p.amount), 0).toFixed(2)}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CompanyDashboard;
